import { useState, useEffect, createContext, useContext } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { Settings, Save, Clock, MapPin, Calendar, Users, LogOut, AlertTriangle, Wand2, Send, Bell, Droplets, RefreshCw, BarChart2, ChevronDown, ChevronUp, Download, Copy, FileSpreadsheet, Trash2, HelpCircle } from 'lucide-react';
import api from '../services/api.js';
// 2026-09-19 (Phase 7A) - stat picker options. Server validates on save;
// backend/src/utils/gameStats.js is the source of truth.
import { STAT_GROUPS, DEFAULT_TRACKED } from '../constants/statCatalogue.js';
import { useTeamRole } from '../hooks/useTeamRole.js';
import { useAuth } from '../context/AuthContext.jsx';
import toast from 'react-hot-toast';
import { PageSpinner } from '../components/Skeleton.jsx';
import ArenaSearch from '../components/shared/ArenaSearch.jsx';
// v2.5.135 — SMS-A team manager toggle.
import TeamSmsToggleCard from '../components/TeamSmsToggleCard.jsx';
import TeamWhatsAppCard from '../components/TeamWhatsAppCard.jsx';
import { TEAM_SETTINGS_SUBSECTIONS } from '../layouts/teamSettingsSubNav.js';

// ── Your Role Panel ────────────────────────────────────────────────────────────
// Apr 30 2026 (App_Change_List #1) — collapsed to a single role.
// The two-button "Coach vs Manager" picker that lived here before
// is gone; the user-facing app now exposes only Coach + Player.
// Legacy managers (currentRole === 'manager') still hit this panel
// and see a one-click "Migrate to Coach" button so they can promote
// themselves into the new model. Backend continues to recognise
// the manager role for back-compat with any unmigrated rows.
function YourRole({ teamId, memberId, currentRole }) {
  const [role,   setRole]   = useState(currentRole || 'coach');
  const [saving, setSaving] = useState(false);

  // Sync if parent reloads with updated data
  useEffect(() => {
    if (currentRole) setRole(currentRole);
  }, [currentRole]);

  const migrateToCoach = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.put(`/teams/${teamId}/members/${memberId}`, { memberRole: 'coach' });
      setRole('coach');
      toast.success('You are now listed as Coach');
    } catch { toast.error('Failed to update your role'); }
    setSaving(false);
  };

  const isLegacyManager = role === 'manager';

  return (
    <div className="card mt-6" style={{ borderColor: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.15)' }}>
      <div className="flex items-center gap-3 mb-4">
        <Users size={17} style={{ color: 'var(--ice)' }} />
        <h3 className="section-title text-lg">Your Role</h3>
        {saving && <div className="spinner ml-auto" style={{ width: 14, height: 14 }} />}
      </div>
      {isLegacyManager ? (
        <>
          <p className="text-xs mb-3" style={{ color: 'var(--text-2)' }}>
            You're currently listed as <strong>Manager</strong> — a legacy role
            from an earlier version of the app. The two-role model uses just
            <strong> Coach</strong> and <strong> Player</strong> now. Your
            permissions stay the same; this just updates how you appear on
            the player list.
          </p>
          <button type="button" disabled={saving} onClick={migrateToCoach}
            className="px-4 py-2 rounded-lg text-sm font-bold"
            style={{
              background: 'var(--ice-a12)',
              border: '2px solid var(--ice-a30)',
              color: 'var(--ice)',
              opacity: saving ? 0.6 : 1,
              cursor: saving ? 'wait' : 'pointer',
            }}>
            Migrate to Coach
          </button>
        </>
      ) : (
        <>
          <p className="text-xs mb-3" style={{ color: 'var(--text-2)' }}>
            You're listed as a <strong>Coach</strong> on this team.
          </p>
          <div className="p-4 rounded-xl"
            style={{
              background: 'var(--ice-a12)',
              border: '2px solid var(--ice-a30)',
            }}>
            <p className="font-bold text-sm mb-0.5" style={{ color: 'var(--ice)' }}>
              Coach
            </p>
            <p className="text-xs leading-snug" style={{ color: 'var(--text-2)' }}>
              Full team-management access — schedule, players, settings.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// ── ToggleSetting (Apr 30 2026, App_Change_List #41) ──────────────────────────
//
// Unified Team Settings toggle. Replaces the mix of checkboxes + tap-the-card
// patterns scattered across the page. One canonical visual: pill-style header
// with a clear ON/OFF indicator, an optional ? tooltip for additional context,
// and an expandable body that shows the explanation + nested config UI ONLY
// when the toggle is ON. Off-state shows a single-line description so the
// page stays scannable.
//
// Why this design:
//   • The auto-jab card (since removed in Batch D) had a "Jab ON / Jab OFF"
//     pill that coaches understood immediately. Generalising that shape
//     across every toggle keeps Team Settings consistent.
//   • Checkboxes are easy to mis-tap on mobile (the hit target is small).
//     A full-width tap region with a wide pill is friendlier on a phone.
//   • Hiding nested config when OFF cuts visual noise — settings that
//     don't apply don't dominate the page.
//
// Props:
//   • emoji        — leading icon (e.g. '🔔', '⚖️'). Optional.
//   • title        — main label.
//   • shortDesc    — one-line description shown when OFF.
//   • longDesc     — full explanation shown when ON. Defaults to shortDesc.
//   • tooltip      — extra context for the ? icon. Optional.
//   • value        — current boolean.
//   • onChange     — (next: boolean) => void.
//   • disabled     — disables the toggle.
//   • children     — nested config UI rendered below longDesc when ON.
//   • accentColor  — CSS color for the ON-state border + pill (default ice).
// May 4 2026 (v2.5.62) — body scroll lock helper. Mount this as a child
// of any conditional modal render to lock body scroll while the modal
// is open. Coach feedback: modals on mobile felt non-native because
// the underlying page would horizontally drift during scroll. Locking
// `body.overflow + body.touchAction` keeps the page underneath
// stationary, matching the iOS/Android native modal feel.
//
// Usage: drop `<BodyScrollLock />` anywhere inside a conditionally-
// rendered modal block. Cleanup runs on unmount, restoring previous
// values so nested modals don't break the unlock.
function BodyScrollLock() {
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, []);
  return null;
}

// May 4 2026 (v2.5.68) — context for ToggleSetting presentation mode.
// Carries the system-wide compact_toggle_descriptions flag (read from
// system_settings via GET /teams/:teamId by TeamPage). Provided by the
// top-level TeamPage component once the team data is loaded; consumed
// by every ToggleSetting so individual call sites don't need to pass
// the prop through. Default false matches the legacy verbose layout.
const TogglePresentationContext = createContext({ compact: false });

function ToggleSetting({
  emoji, title, shortDesc, longDesc, tooltip,
  value, onChange, disabled = false,
  children, accentColor,
  // Optional explicit override — usually you want the context value.
  // Pass `compact={true}` to force compact mode regardless of the
  // system setting (used by AdminPage's preview, for example).
  compact: compactProp,
}) {
  const ctx = useContext(TogglePresentationContext);
  // Explicit prop wins; otherwise fall back to context; otherwise false.
  const compact = compactProp !== undefined ? compactProp : ctx.compact;
  const isOn = value === true;
  // May 4 2026 — coach asked for ON=green, OFF=red consistently across
  // every toggle in the app. Previously the ON pill used `accentColor`
  // (defaulting to ice blue) and the OFF pill was neutral grey, which
  // varied a lot from card to card. Now the pill is always green when
  // ON and red when OFF — visual semantics match the toggle action
  // (green = go / enabled, red = stop / disabled).
  //
  // The card background still tints with `accentColor` when ON (kept
  // for visual continuity with prior versions; ice-blue tint is the
  // default and matches the team palette), but the pill itself is the
  // unambiguous status indicator the coach asked for.
  const accent = accentColor || 'var(--ice)';
  // Build rgba versions of the accent for backgrounds. We accept either
  // a CSS variable (var(--ice)) or a literal color. For variables we
  // fall back to a neutral semi-transparent ice-blue tint since we
  // can't compute rgba from a var at runtime.
  const isVar = typeof accent === 'string' && accent.startsWith('var(');
  const onBg     = isVar ? 'rgba(0,212,255,0.06)' : `${accent}14`;
  const onBorder = isVar ? 'rgba(0,212,255,0.30)' : `${accent}55`;
  const offBg     = 'var(--surface-2)';
  const offBorder = 'var(--border)';
  // Pill colors — fixed green/red per coach request.
  const pillOnColor    = 'var(--goal)';     // green (already a project token)
  const pillOnBg       = 'rgba(0,232,122,0.15)';
  const pillOnDot      = 'var(--goal)';
  const pillOffColor   = 'var(--penalty)';  // red (already a project token)
  const pillOffBg      = 'rgba(255,59,59,0.10)';
  const pillOffBorder  = 'rgba(255,59,59,0.35)';
  const pillOffDot     = 'var(--penalty)';

  const toggle = () => { if (!disabled) onChange(!isOn); };

  return (
    <div
      className="rounded-xl p-3"
      style={{
        background: isOn ? onBg : offBg,
        border: `1.5px solid ${isOn ? onBorder : offBorder}`,
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.15s, border-color 0.15s',
      }}
      onClick={toggle}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-pressed={isOn}
      aria-disabled={disabled}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      }}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
              {emoji && <span style={{ marginRight: 6 }}>{emoji}</span>}
              {title}
            </span>
            {/* May 4 2026 (v2.5.68) — in compact mode the help icon's
                title attribute carries the longDesc (or shortDesc as
                fallback) so the description is still discoverable on
                hover. Outside compact mode, the explicit `tooltip`
                prop wins (legacy behavior unchanged). */}
            {(compact ? (longDesc || shortDesc || tooltip) : tooltip) && (
              <span
                onClick={(e) => e.stopPropagation()}
                title={compact ? (longDesc || shortDesc || tooltip) : tooltip}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 20, height: 20, borderRadius: '50%',
                  background: 'var(--surface-3)', color: 'var(--text-3)',
                  cursor: 'help', flexShrink: 0,
                }}
                aria-label={`More info: ${title}`}>
                <HelpCircle size={14} />
              </span>
            )}
          </div>
          {/* Description — short when OFF, full when ON. Suppressed
              entirely if no description was provided.
              May 4 2026 (v2.5.68) — also suppressed when compact=true
              (description moves into the help icon tooltip instead). */}
          {!compact && (isOn ? (longDesc || shortDesc) : shortDesc) && (
            <p className="text-xs mt-1.5" style={{
              color: 'var(--text-2)',
              lineHeight: 1.5,
            }}>
              {isOn ? (longDesc || shortDesc) : shortDesc}
            </p>
          )}
        </div>
        {/* ON/OFF pill on the right — green when ON, red when OFF,
            same green/red across every toggle in the app for clear
            status semantics. The outer card is tappable for forgiving
            fat-thumb hits; the pill itself doubles as a clear affordance. */}
        <div
          aria-hidden="true"
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg flex-shrink-0"
          style={{
            background: isOn ? pillOnBg : pillOffBg,
            border: `1.5px solid ${isOn ? pillOnColor : pillOffBorder}`,
            color: isOn ? pillOnColor : pillOffColor,
            fontSize: 12, fontWeight: 800, letterSpacing: '0.04em',
          }}>
          <span style={{
            display: 'inline-block', width: 8, height: 8, borderRadius: 4,
            background: isOn ? pillOnDot : pillOffDot,
            boxShadow: isOn ? `0 0 6px ${pillOnDot}` : 'none',
          }} />
          {isOn ? 'ON' : 'OFF'}
        </div>
      </div>
      {/* Nested config UI — only when ON. We stop click propagation on
          this region so interacting with inputs inside doesn't
          accidentally toggle the parent. */}
      {isOn && children && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ marginTop: 12, paddingLeft: 4 }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── FieldHelp (v2.5.97) ──────────────────────────────────────────────────────
//
// Companion to ToggleSetting for non-toggle fields. Coach feedback (item 12):
// "compress more of the text descriptions in the team settings into more
// tooltips. See, for example, the team information they're still a couple
// sentences below in a player cap." Many input fields have a long
// `<p className="text-xs text-secondary mt-1">…</p>` paragraph below them
// that's helpful first-time but noisy once the coach knows what the field
// does. This component renders that paragraph inline by default; in compact
// mode (driven by the same TogglePresentationContext as ToggleSetting), it
// collapses to a small ⓘ help-icon next to its anchor that exposes the text
// via `title=` tooltip on hover.
//
// Two render modes:
//   <FieldHelp>{paragraph text}</FieldHelp>         (paragraph, defaults to inline)
//   <FieldHelp inline>{title}</FieldHelp>           — used inline next to a label so
//                                                     the help icon sits beside the label
//                                                     when in compact mode
//
// In compact mode, the paragraph becomes a tooltip on a small ⓘ icon. The
// caller is responsible for either (a) putting the icon next to the label
// they want it associated with, or (b) accepting that the icon renders
// inline where the paragraph used to.
//
// `anchor` mode (v2.5.104): renders as a span-based help-icon in BOTH
// compact and non-compact mode. Used inside line-level titles (e.g.
// radio-card titles in Create Teams) where the long description would
// otherwise force `<p>` inside `<p>` (invalid HTML) when the parent
// is already a `<p className="text-sm font-semibold">`. In anchor mode
// the inline-paragraph long-form rendering is the caller's
// responsibility (via TogglePresentationContext.Consumer or similar)
// — FieldHelp just provides the icon + tooltip.
//
// Why not just hide the paragraph in compact mode? Because the help is
// often important — the player-cap description ("Hard cap on the roster.
// When reached, additional RSVPs go to the waitlist.") is not redundant.
// Moving it to a hover tooltip preserves discoverability without the
// vertical clutter.
function FieldHelp({ children, anchor }) {
  const ctx = useContext(TogglePresentationContext);
  const compact = ctx?.compact === true;
  // Strip JSX to a plain string for the tooltip. If the help text is
  // simple text, this is straightforward. If it has embedded <strong>,
  // we render the markup inline (full mode) but feed text-only into
  // title= (compact mode) since browser tooltips don't render HTML.
  const tooltipText = (() => {
    const flatten = (node) => {
      if (typeof node === 'string' || typeof node === 'number') return String(node);
      if (Array.isArray(node)) return node.map(flatten).join('');
      if (node?.props?.children) return flatten(node.props.children);
      return '';
    };
    return flatten(children).replace(/\s+/g, ' ').trim();
  })();

  // Anchor mode: ALWAYS render as a span-icon (both compact + non-compact).
  // The caller manages the inline-paragraph rendering separately so we
  // don't produce `<p>` inside `<p>`.
  if (anchor) {
    return (
      <span
        title={tooltipText}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 20, height: 20, borderRadius: '50%',
          background: 'var(--surface-3)', color: 'var(--text-3)',
          cursor: 'help', flexShrink: 0,
          marginLeft: 6,
          verticalAlign: 'middle',
        }}
        aria-label={tooltipText}>
        <HelpCircle size={14} />
      </span>
    );
  }

  if (compact) {
    return (
      <span
        title={tooltipText}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 20, height: 20, borderRadius: '50%',
          background: 'var(--surface-3)', color: 'var(--text-3)',
          cursor: 'help', flexShrink: 0,
          marginLeft: 0,
          verticalAlign: 'top',
        }}
        aria-label={tooltipText}>
        <HelpCircle size={14} />
      </span>
    );
  }
  // Full mode — render as the standard descriptive paragraph below the field.
  return (
    <p className="text-xs text-secondary mt-1">
      {children}
    </p>
  );
}

// ── CheckoutWindowPicker (v2.5.165) ───────────────────────────────────────────
// Per-tier "within how many hours before the game" picker for each of the
// four coach check-out alerts. Replaces the single shared window. Renders a
// preset dropdown + a Custom option that reveals a 1–720h number box. `value`
// is the current hours (number), `onChange(hours)` receives the new value.
function CheckoutWindowPicker({ value, onChange }) {
  const PRESETS = [3, 6, 12, 24, 48, 72, 168];
  const cur = parseInt(value, 10) || 24;
  const isCustom = !PRESETS.includes(cur);
  return (
    <div className="mt-2">
      <label className="label" style={{ fontSize: 12 }}>
        Notify me if the check-out happens within this many hours before the game
      </label>
      <select
        className="input-field"
        style={{ maxWidth: 240 }}
        value={isCustom ? 'custom' : String(cur)}
        onChange={(e) => {
          if (e.target.value === 'custom') {
            onChange(cur); // seed custom box with current value
          } else {
            onChange(parseInt(e.target.value, 10));
          }
        }}>
        <option value="3">3 hours before</option>
        <option value="6">6 hours before</option>
        <option value="12">12 hours before</option>
        <option value="24">24 hours before (game day)</option>
        <option value="48">48 hours before (today or tomorrow)</option>
        <option value="72">72 hours before (3 days)</option>
        <option value="168">168 hours before (1 week)</option>
        <option value="custom">Custom…</option>
      </select>
      {isCustom && (
        <div className="mt-2">
          <input
            type="number"
            min="1"
            max="720"
            className="input-field"
            style={{ maxWidth: 140 }}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
          <span className="ml-2 text-secondary" style={{ fontSize: 13 }}>hours before the game</span>
        </div>
      )}
      <p className="text-xs text-secondary mt-1">
        If the check-out happens earlier than this, the alert is held back. Range 1–720 hours.
      </p>
    </div>
  );
}

// ── CascadeListEditor (Apr 30 2026, App_Change_List #43-#45) ──────────────────
//
// Coach builds up to 3 named "waves" for the cascade. Each wave is either:
//   • By rating tier  — ranks: number[] (1..5; tier 5 also pulls unrated)
//   • Specific spares — userIds: string[]
// And each has a delay (hours) since the previous wave fired. The first
// wave's delay is implicitly zero — it fires as soon as the auto-invite
// trigger conditions are met.
//
// Used twice on the page: once for spares (pool='spare') and once for
// goalies (pool='goalie'). The two pools have different candidate sets
// but the editor logic is identical.
//
// Why a single component for both pools: the change list (#44 + #45)
// explicitly says "apply the same list-based cascading logic to
// goalies." Building twice would mean two places to keep in sync.
//
// Props:
//   • pool             — 'spare' | 'goalie' (used for labels + accent)
//   • lists            — current array (0..3 entries)
//   • onChange         — (next: list[]) => void
//   • candidates       — Array of { userId, name, rating } — already
//                        filtered to the right pool
//   • candidatesLoading — boolean for the picker fetch state
//   • onLoadCandidates — () => void; called lazily when the picker opens
//   • disabled         — disable all interactions
function CascadeListEditor({
  pool, lists, onChange, candidates, candidatesLoading, onLoadCandidates, disabled,
}) {
  const isSpare = pool === 'spare';
  const accent  = isSpare ? 'var(--puck)' : 'var(--ice)';
  const poolLabel = isSpare ? 'spare' : 'goalie';

  const RANK_LABELS = {
    1: 'Elite (1)',
    2: 'Strong (2)',
    3: 'Average (3)',
    4: 'Developing (4)',
    5: 'Beginner / unrated (5)',
  };

  // Stable ID generator — short random suffix so coaches can have
  // multiple lists without ID collisions on the same save.
  const newId = () => `list_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

  const addList = () => {
    if (disabled) return;
    if (lists.length >= 3) return;
    const next = [...lists, {
      id: newId(),
      name: lists.length === 0
        ? 'A-list'
        : lists.length === 1 ? 'B-list' : 'C-list',
      memberSelection: 'ranks',
      ranks: [],
      userIds: [],
      delayHoursAfterPrev: lists.length === 0 ? 0 : (isSpare ? 4 : 8),
    }];
    onChange(next);
  };

  const removeList = (id) => {
    if (disabled) return;
    onChange(lists.filter(l => l.id !== id));
  };

  const updateList = (id, updates) => {
    if (disabled) return;
    onChange(lists.map(l => l.id === id ? { ...l, ...updates } : l));
  };

  const moveList = (id, direction) => {
    if (disabled) return;
    const idx = lists.findIndex(l => l.id === id);
    if (idx === -1) return;
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= lists.length) return;
    const next = [...lists];
    [next[idx], next[target]] = [next[target], next[idx]];
    // Apr 30 2026 — when the first list moves, its
    // delayHoursAfterPrev becomes meaningless (first list always fires
    // immediately). We DON'T reset it on swap because the coach may
    // re-order again; on save, the scheduler treats lists[0]'s delay
    // as zero regardless. Keeping the value lets a swap be lossless.
    onChange(next);
  };

  const togglePickerOpen = (id) => {
    if (disabled) return;
    if (typeof onLoadCandidates === 'function') onLoadCandidates();
    updateList(id, { _pickerOpen: !lists.find(l => l.id === id)?._pickerOpen });
  };

  return (
    <div>
      {/* Empty-state — no lists yet. */}
      {lists.length === 0 && (
        <div className="p-3 rounded-xl text-xs"
          style={{
            background: 'var(--surface-2)',
            border: '1px dashed var(--border)',
            color: 'var(--text-2)',
            lineHeight: 1.5,
          }}>
          No cascade lists configured yet. When the auto-invite trigger
          fires, every {poolLabel} will be invited at once. Add a list
          below to break the invite into named waves with delays between
          them.
        </div>
      )}

      {/* The lists themselves. Order = send order. */}
      <div className="space-y-2">
        {lists.map((list, idx) => {
          const isFirst = idx === 0;
          const isLast  = idx === lists.length - 1;
          const memberSelection = list.memberSelection === 'specific' ? 'specific' : 'ranks';
          const pickerOpen = list._pickerOpen === true;
          return (
            <div
              key={list.id}
              className="rounded-xl p-3"
              style={{
                background: 'var(--surface-2)',
                border: `1.5px solid ${accent}40`,
              }}>
              {/* Header row: list number + name + delete + reorder */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold flex-shrink-0"
                  style={{
                    background: `${accent}26`, color: accent,
                    width: 22, height: 22, borderRadius: '50%',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  {idx + 1}
                </span>
                <input
                  type="text"
                  className="input-field py-1 text-sm flex-1"
                  style={{ minWidth: 0 }}
                  placeholder={`Wave ${idx + 1} label`}
                  maxLength={40}
                  value={list.name || ''}
                  onChange={e => updateList(list.id, { name: e.target.value })}
                  disabled={disabled}
                />
                <button type="button"
                  onClick={() => moveList(list.id, 'up')}
                  disabled={disabled || isFirst}
                  className="text-xs px-2 py-1"
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    color: isFirst ? 'var(--text-3)' : 'var(--text-2)',
                    cursor: isFirst || disabled ? 'not-allowed' : 'pointer',
                  }}
                  aria-label="Move list up"
                  title="Move list up">▲</button>
                <button type="button"
                  onClick={() => moveList(list.id, 'down')}
                  disabled={disabled || isLast}
                  className="text-xs px-2 py-1"
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    color: isLast ? 'var(--text-3)' : 'var(--text-2)',
                    cursor: isLast || disabled ? 'not-allowed' : 'pointer',
                  }}
                  aria-label="Move list down"
                  title="Move list down">▼</button>
                <button type="button"
                  onClick={() => removeList(list.id)}
                  disabled={disabled}
                  className="text-xs px-2 py-1"
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(255,68,68,0.3)',
                    borderRadius: 6,
                    color: 'var(--penalty)',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                  }}
                  aria-label="Remove list"
                  title="Remove this list">✕</button>
              </div>

              {/* Selection-mode toggle */}
              <div className="flex gap-2 mb-2">
                {[
                  { key: 'ranks',    label: 'By rank' },
                  { key: 'specific', label: 'Specific spares' },
                ].map(opt => {
                  const active = memberSelection === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => updateList(list.id, { memberSelection: opt.key })}
                      disabled={disabled}
                      className="text-xs px-3 py-1.5 font-semibold"
                      style={{
                        background: active ? `${accent}26` : 'var(--surface-3)',
                        border: `1.5px solid ${active ? accent : 'var(--border)'}`,
                        color: active ? accent : 'var(--text-2)',
                        borderRadius: 8,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                      }}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              {/* Selection body — by rank */}
              {memberSelection === 'ranks' && (
                <div className="mb-2">
                  <p className="text-xs mb-1.5" style={{ color: 'var(--text-2)' }}>
                    Pick which rating tiers go in this wave:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {[1, 2, 3, 4, 5].map(r => {
                      const ranks = Array.isArray(list.ranks) ? list.ranks : [];
                      const active = ranks.includes(r);
                      return (
                        <button
                          key={r}
                          type="button"
                          onClick={() => {
                            const next = active
                              ? ranks.filter(x => x !== r)
                              : [...ranks, r];
                            next.sort((a, b) => a - b);
                            updateList(list.id, { ranks: next });
                          }}
                          disabled={disabled}
                          className="text-xs px-2 py-1 font-semibold"
                          style={{
                            background: active ? 'var(--ice-a12)' : 'var(--surface-3)',
                            border: `1.5px solid ${active ? 'var(--ice-a30)' : 'var(--border)'}`,
                            color: active ? 'var(--ice)' : 'var(--text-2)',
                            borderRadius: 6,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                          }}>
                          {RANK_LABELS[r]}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs mt-1.5" style={{ color: 'var(--text-3)' }}>
                    Tier 5 also includes unrated {poolLabel}s so they aren't silently skipped.
                  </p>
                </div>
              )}

              {/* Selection body — specific spares */}
              {memberSelection === 'specific' && (
                <div className="mb-2">
                  <button
                    type="button"
                    onClick={() => togglePickerOpen(list.id)}
                    disabled={disabled}
                    className="text-xs px-3 py-1.5"
                    style={{
                      background: 'var(--surface-3)',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      color: 'var(--text)',
                    }}>
                    {pickerOpen ? '▾' : '▸'} {(list.userIds || []).length} {poolLabel}
                    {(list.userIds || []).length === 1 ? '' : 's'} selected
                  </button>
                  {pickerOpen && (
                    <div className="mt-2 p-2 rounded-lg"
                      style={{
                        background: 'var(--surface-3)',
                        border: '1px solid var(--border)',
                        maxHeight: 240,
                        overflowY: 'auto',
                      }}>
                      {candidatesLoading ? (
                        <p className="text-xs" style={{ color: 'var(--text-3)' }}>
                          Loading {poolLabel}s…
                        </p>
                      ) : candidates.length === 0 ? (
                        <p className="text-xs" style={{ color: 'var(--text-3)' }}>
                          No {poolLabel}s on this team yet.
                        </p>
                      ) : (
                        candidates.map(c => {
                          const ids = list.userIds || [];
                          const checked = ids.includes(c.userId);
                          return (
                            <label key={c.userId}
                              className="flex items-center gap-2 py-1 px-1 cursor-pointer"
                              style={{ borderRadius: 4 }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  const next = checked
                                    ? ids.filter(u => u !== c.userId)
                                    : [...ids, c.userId];
                                  updateList(list.id, { userIds: next });
                                }}
                                disabled={disabled}
                                style={{ flexShrink: 0 }}
                              />
                              <span className="text-xs flex-1 truncate">
                                {c.name || 'Unnamed'}
                              </span>
                              {c.rating != null && (
                                <span className="text-xs" style={{ color: 'var(--text-3)' }}>
                                  Tier {c.rating}
                                </span>
                              )}
                            </label>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Delay row — hidden for the first list since it's
                  immediate by definition. */}
              {!isFirst && (
                <div className="flex items-center gap-2 mt-2 pt-2"
                  style={{ borderTop: '1px solid var(--border)' }}>
                  <label className="text-xs flex-shrink-0" style={{ color: 'var(--text-2)' }}>
                    Send this wave
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="168"
                    className="input-field py-1 text-sm"
                    style={{ width: 80 }}
                    value={list.delayHoursAfterPrev ?? 4}
                    onChange={e => {
                      let n = parseInt(e.target.value);
                      if (!Number.isFinite(n) || n < 0) n = 0;
                      if (n > 168) n = 168;
                      updateList(list.id, { delayHoursAfterPrev: n });
                    }}
                    disabled={disabled}
                  />
                  <span className="text-xs" style={{ color: 'var(--text-2)' }}>
                    hours after the previous wave fires
                  </span>
                </div>
              )}
              {isFirst && (
                <p className="text-xs mt-2 pt-2"
                  style={{ borderTop: '1px solid var(--border)', color: 'var(--text-3)' }}>
                  First wave fires immediately when the cascade trigger conditions are met.
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Add list button */}
      {lists.length < 3 && (
        <button
          type="button"
          onClick={addList}
          disabled={disabled}
          className="text-xs px-3 py-2 mt-2 font-semibold w-full"
          style={{
            background: `${accent}14`,
            border: `1.5px dashed ${accent}55`,
            color: accent,
            borderRadius: 8,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}>
          + Add a {poolLabel} list ({lists.length}/3)
        </button>
      )}
    </div>
  );
}

// ── DrinksParticipantPicker (May 4 2026) ─────────────────────────────────────
// Stage selection locally so the coach can flip checkboxes freely, then
// commit with Save (one round-trip + one toast). Cancel discards.
// `initialSelected` empty = pre-select everyone (matches the legacy
// "all eligible" semantics — coach gets a familiar starting point).
function DrinksParticipantPicker({ members, initialSelected, onCancel, onSave, saving }) {
  // If initialSelected is empty, default to "everyone selected" so a
  // first-time picker open shows the legacy state visually. The coach
  // then deselects to opt people out. Saves an empty array as
  // "everyone" if the coach doesn't change anything (idempotent).
  const initial = (Array.isArray(initialSelected) && initialSelected.length > 0)
    ? new Set(initialSelected)
    : new Set(members.map(m => m.id));
  const [staged, setStaged] = useState(initial);

  const toggle = (id) => {
    setStaged(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setStaged(new Set(members.map(m => m.id)));
  const clearAll  = () => setStaged(new Set());

  // Sort by name for predictable display.
  const sorted = [...members].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-2">
        <p className="text-xs font-semibold" style={{ color: 'var(--text)' }}>
          {staged.size} of {members.length} selected
        </p>
        <div className="flex gap-2">
          <button type="button" onClick={selectAll}
            className="text-xs font-bold"
            style={{ color: 'var(--ice)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            Select all
          </button>
          <span style={{ color: 'var(--text-3)' }}>·</span>
          <button type="button" onClick={clearAll}
            className="text-xs font-bold"
            style={{ color: 'var(--text-2)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            Clear
          </button>
        </div>
      </div>
      <div style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 12 }}
        className="space-y-1">
        {sorted.map(m => {
          const picked = staged.has(m.id);
          return (
            <label key={m.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer"
              style={{
                background: picked ? 'rgba(0,212,255,0.10)' : 'transparent',
                border: `1px solid ${picked ? 'var(--ice-a30)' : 'transparent'}`,
              }}>
              <input
                type="checkbox"
                checked={picked}
                onChange={() => toggle(m.id)}
                style={{ flexShrink: 0 }}
              />
              <span className="text-sm flex-1 truncate" style={{ color: 'var(--text)' }}>
                {m.name || 'Unnamed'}
              </span>
              {m.position === 'goalie' && (
                <span className="text-xs" style={{ color: 'var(--ice)' }}>🥅 G</span>
              )}
            </label>
          );
        })}
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={onCancel}
          disabled={saving}
          className="btn-ghost flex-1 justify-center text-xs">
          Cancel
        </button>
        <button type="button"
          onClick={() => {
            // Convert "everyone selected" to an empty array so the
            // backend treats it as the legacy "all eligible" mode.
            // This keeps storage tidy and avoids drift over time as
            // the roster grows/shrinks.
            const arr = Array.from(staged);
            if (arr.length === members.length) onSave([]);
            else onSave(arr);
          }}
          disabled={saving}
          className="flex-1 justify-center text-xs font-bold py-2 rounded"
          style={{
            background: 'var(--ice)', color: 'var(--btn-accent-text)',
            border: 'none',
            cursor: saving ? 'wait' : 'pointer',
            opacity: saving ? 0.5 : 1,
          }}>
          {saving ? 'Saving…' : 'Save participants'}
        </button>
      </div>
      <p className="text-xs mt-2" style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>
        When everyone is selected, the rotation includes all current and
        future full-time players (default behaviour).
      </p>
    </div>
  );
}

// ── Drinks Settings Panel (self-contained, mounts below the main form) ────────
// v2.5.274 (2026-09-16) — every <button> in here is type="button". The
// panel is rendered INSIDE the main Team Settings <form>, and a <button>
// without a type defaults to type="submit": tapping "Drinks OFF" submitted
// the parent form ("Team settings saved!" toast), which PUT the old
// drinks_enabled=false, reloaded the team, and the sync effect below
// flipped the toggle straight back to OFF.
function DrinksSettings({ teamId, settings }) {
  const [enabled,    setEnabled]    = useState(settings?.drinks_enabled ?? false);
  const [mode,       setMode]       = useState(settings?.drinks_mode || 'volunteer');
  // v2.5.104 — compact mode detection for the super-admin
  // compact_toggle_descriptions flag. When ON, the longer descriptive
  // paragraphs (Bulk-clear, "How reminders work" callout, etc.) are
  // collapsed under tooltip icons; the shorter sentences inline are
  // tightened further. Coach feedback: "for Drinks, try to put
  // whatever you can under a tooltip, also make the explanations
  // more concise, reduce the number of words in each sentence."
  const presentationCtx = useContext(TogglePresentationContext);
  const drinksCompact = presentationCtx?.compact === true;
  // May 4 2026 — coach picks what's being brought (drinks/beer/etc).
  // Default 'drinks' preserves prior wording for existing teams.
  // Stored as a free-text string so the coach can use anything,
  // but we expose a quick-pick chip row for the common options
  // and an "Other…" input for custom values.
  const [label,      setLabel]      = useState(settings?.drinks_label || 'drinks');
  const [labelInput, setLabelInput] = useState(settings?.drinks_label || 'drinks');
  // v2.5.182 — lead time for the "nobody assigned" coach alert. Default
  // 24h. Replaces the old hardcoded 9 PM-night-before trigger.
  const [coachAlertHours, setCoachAlertHours] = useState(settings?.drinks_coach_alert_hours_before ?? 24);
  // May 4 2026 — eligible-participants for scheduled mode. null/empty
  // = all primaries participate (back-compat). Coach selects subset.
  const [eligibleIds,    setEligibleIds]    = useState(
    Array.isArray(settings?.drinks_eligible_user_ids) ? settings.drinks_eligible_user_ids : []
  );
  const [pickerOpen,     setPickerOpen]     = useState(false);
  const [pickerMembers,  setPickerMembers]  = useState(null); // null = not loaded
  const [pickerLoading,  setPickerLoading]  = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [stats,      setStats]      = useState(null);
  const [showStats,  setShowStats]  = useState(false);
  // v2.5.149 — sort mode for the full drinks list. Coach asked for
  // two options: alphabetical (the old behaviour) and "by highest
  // contributor" (most past drinks-duty first), with highest-
  // contributor as the new DEFAULT — it answers "who's pulling
  // their weight?" at a glance.
  const [statsSort,  setStatsSort]  = useState('contributor'); // 'contributor' | 'alpha'

  // Sync local state whenever the parent reloads team data (e.g. after
  // saving main form). v2.5.105 — also clears the dirty flag because
  // the new prop values become the "clean" baseline.
  useEffect(() => {
    setEnabled(settings?.drinks_enabled ?? false);
    setMode(settings?.drinks_mode || 'volunteer');
    setLabel(settings?.drinks_label || 'drinks');
    setLabelInput(settings?.drinks_label || 'drinks');
    setCoachAlertHours(settings?.drinks_coach_alert_hours_before ?? 24);
    setEligibleIds(Array.isArray(settings?.drinks_eligible_user_ids) ? settings.drinks_eligible_user_ids : []);
    setDirty(false);
  }, [
    settings?.drinks_enabled, settings?.drinks_mode, settings?.drinks_label,
    settings?.drinks_eligible_user_ids,
    settings?.drinks_coach_alert_hours_before,
  ]);

  // Lazy-load team members when picker opens for the first time.
  useEffect(() => {
    if (!pickerOpen || pickerMembers !== null) return;
    setPickerLoading(true);
    api.get(`/teams/${teamId}/members`)
      .then(r => setPickerMembers((r.data || []).filter(m => m.isPrimary && !m.isPending)))
      .catch(() => { toast.error('Failed to load roster'); setPickerMembers([]); })
      .finally(() => setPickerLoading(false));
  }, [pickerOpen, pickerMembers, teamId]);

  // v2.5.105 — Drinks settings UX refactor. Coach feedback: "in the
  // drinks duty, instead of auto saving each change, have a 'Save
  // Settings' to save whatever the coach decides on, the 'Save
  // Settings' button is consistent with all the other pages."
  //
  // Previously: every toggle/mode change/label edit/picker apply
  // immediately PUT /teams. Five distinct setter handlers each fired
  // their own request + toast — chatty network usage and feel.
  // Now: each handler just updates local state and marks `dirty=true`.
  // A single "Save Settings" button at the bottom commits all four
  // fields in one request, matching the pattern used by the rest of
  // the Team Settings page. Action endpoints (auto-schedule,
  // unassign-all, picker open/close) are unchanged — those aren't
  // settings writes.
  //
  // Dirty-flag semantics: any state change after the initial load
  // sets dirty=true. The save action sets dirty=false on success.
  // The useEffect that resyncs from `settings` prop (when the parent
  // reloads team data after the main save) also resets dirty=false
  // because the new prop values are now the "clean" baseline.
  const [dirty, setDirty] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        settings: {
          drinks_enabled: enabled,
          drinks_mode:    mode,
          drinks_label:   (label || 'drinks').trim().slice(0, 30) || 'drinks',
          // v2.5.182 — coach-alert lead time (hours). Clamped 1..168
          // client-side; backend re-clamps.
          drinks_coach_alert_hours_before: Math.max(1, Math.min(168, parseInt(coachAlertHours, 10) || 24)),
          // Always include the eligible-ids array now. Empty array =
          // "everyone eligible" semantically (cleared list), null
          // also valid. Sending it ensures the picker selection is
          // persisted even when the coach didn't open the picker
          // this session.
          drinks_eligible_user_ids: Array.isArray(eligibleIds)
            ? eligibleIds.filter(s => typeof s === 'string')
            : [],
        },
      };
      await api.put(`/teams/${teamId}`, payload);
      toast.success('Drinks settings saved!');
      setDirty(false);
    } catch { toast.error('Failed to save drinks settings'); }
    setSaving(false);
  };

  // Setters now just mutate state + mark dirty. No network call.
  const toggle = () => {
    setEnabled(prev => !prev);
    setDirty(true);
  };
  const changeMode = (m) => {
    setMode(m);
    setDirty(true);
  };
  const changeLabel = (newLabel) => {
    const cleaned = (newLabel || '').trim().slice(0, 30) || 'drinks';
    setLabel(cleaned);
    setLabelInput(cleaned);
    setDirty(true);
  };

  // May 4 2026 — apply the picker selection. v2.5.105 — no longer
  // saves automatically; closes the picker and marks dirty so the
  // coach reviews then hits Save Settings.
  const applyEligible = (newIds) => {
    setEligibleIds(newIds);
    setPickerOpen(false);
    setDirty(true);
  };

  const autoSchedule = async () => {
    setScheduling(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/drinks/schedule`);
      toast.success(data.message);
    } catch (err) { toast.error(err.response?.data?.error || 'Auto-schedule failed'); }
    setScheduling(false);
  };

  // May 4 2026 — bulk unassign drinks roster. Two-step confirm:
  // first click arms the confirm-state for that scope, second click
  // (within 5s) actually fires the API call. Avoids accidental wipe
  // of the entire drinks history.
  const [unassigning, setUnassigning] = useState(false);
  const [unassignConfirm, setUnassignConfirm] = useState(null); // 'future' | 'all' | null
  const unassignAll = async (scope) => {
    if (unassigning) return;
    if (unassignConfirm !== scope) {
      setUnassignConfirm(scope);
      // Auto-clear after 5s if no second tap
      setTimeout(() => setUnassignConfirm(prev => prev === scope ? null : prev), 5000);
      return;
    }
    setUnassignConfirm(null);
    setUnassigning(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/drinks/unassign-all`, { scope });
      if (data.cleared > 0) {
        toast.success(data.message);
      } else {
        toast(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to clear drinks roster');
    }
    setUnassigning(false);
  };

  const loadStats = async () => {
    if (stats) { setShowStats(v => !v); return; }
    try {
      const { data } = await api.get(`/teams/${teamId}/drinks/stats`);
      setStats(data);
      setShowStats(true);
    } catch { toast.error('Failed to load stats'); }
  };

  return (
    <div className="card mt-6" style={{ borderColor: 'rgba(0,212,255,0.2)', border: '1px solid rgba(0,232,122,0.15)' }}>
      <div className="flex items-center gap-3 mb-4">
        <Droplets size={18} style={{ color: '#00d4ff' }} />
        <h3 className="section-title text-lg">Drinks Duty</h3>
      </div>

      {/* Master toggle */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="font-semibold text-sm">Enable drinks tracking</p>
          <p className="text-secondary text-xs mt-0.5">Show who's bringing drinks on the schedule and send reminders.</p>
        </div>
        <button type="button"
          onClick={toggle} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all"
          style={{
            background: enabled ? 'rgba(0,232,122,0.12)' : 'var(--surface-3)',
            border: `1.5px solid ${enabled ? 'var(--goal)' : 'var(--border)'}`,
            color: enabled ? 'var(--goal)' : 'var(--text-2)',
          }}
        >
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: enabled ? 'var(--goal)' : 'var(--text-3)',
            boxShadow: enabled ? '0 0 6px var(--goal)' : 'none',
            display: 'inline-block',
          }} />
          {enabled ? 'Drinks ON' : 'Drinks OFF'}
        </button>
      </div>

      {enabled && (
        <>
          {/* Mode picker */}
          <div className="mb-4">
            <label className="label">Assignment Mode</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'volunteer', label: '🙋 Volunteer', desc: 'Players sign up themselves' },
                { value: 'scheduled', label: '📅 Scheduled', desc: 'Fairly auto-assigned by rotation' },
              ].map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => changeMode(opt.value)}
                  className="p-3 rounded-xl text-left transition-all"
                  style={{
                    background: mode === opt.value ? 'var(--ice-a12)' : 'var(--surface-2)',
                    border: `1.5px solid ${mode === opt.value ? 'var(--ice-a30)' : 'var(--border)'}`,
                  }}>
                  <p className="text-sm font-bold" style={{ color: mode === opt.value ? 'var(--ice)' : 'var(--text)' }}>{opt.label}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-2)' }}>{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* May 4 2026 — what is being brought? Coaches whose teams
              bring beer instead of drinks (or snacks, or coffee) flip
              this label and the reminder emails + push notifications
              re-word accordingly. Stored as a free-text string capped
              at 30 chars; a quick-pick row covers the common values
              and "Other…" reveals an inline input for custom text. */}
          <div className="mb-4">
            <label className="label">What's being brought?</label>
            <p className="text-secondary text-xs mb-2" style={{ marginTop: -4 }}>
              Affects the wording in reminder emails and notifications.
            </p>
            <div className="flex flex-wrap gap-2 mb-2">
              {[
                { v: 'drinks', l: '🥤 Drinks' },
                { v: 'beer',   l: '🍺 Beer' },
                { v: 'snacks', l: '🍿 Snacks' },
                { v: 'water',  l: '💧 Water' },
                { v: 'coffee', l: '☕ Coffee' },
                { v: 'pizza',  l: '🍕 Pizza' },
              ].map(opt => {
                const active = label.toLowerCase() === opt.v;
                return (
                  <button key={opt.v} type="button"
                    onClick={() => changeLabel(opt.v)}
                    disabled={saving}
                    style={{
                      padding: '6px 12px', borderRadius: 999,
                      background: active ? 'var(--ice-a15)' : 'var(--surface-2)',
                      border: `1px solid ${active ? 'var(--ice-a30)' : 'var(--border)'}`,
                      color: active ? 'var(--ice)' : 'var(--text-2)',
                      fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
                      opacity: saving ? 0.5 : 1,
                    }}>
                    {opt.l}
                  </button>
                );
              })}
            </div>
            {/* Custom text input — always visible so coaches can type
                a custom value (e.g. "BBQ", "Wine"). Saves on blur or
                Enter. The current `label` shows as the placeholder
                when matching one of the chips above; otherwise it's
                the source of truth. */}
            <div className="flex items-center gap-2 mt-2">
              <input
                type="text"
                value={labelInput}
                onChange={(e) => setLabelInput(e.target.value)}
                onBlur={() => { if (labelInput !== label) changeLabel(labelInput); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); changeLabel(labelInput); } }}
                maxLength={30}
                placeholder="Custom (e.g. BBQ, Wine)…"
                className="input-field"
                style={{ flex: 1, fontSize: 13 }}
                disabled={saving}
              />
              <span className="text-xs text-secondary" style={{ minWidth: 'auto' }}>
                Currently: <strong style={{ color: 'var(--ice)' }}>{label}</strong>
              </span>
            </div>
          </div>

          {/* May 4 2026 — eligible-participants picker (scheduled mode only).
              Coach feedback: "under the scheduled mode for drinks, there
              needs to be a list to pick the people that are part of the
              drink program, as not everyone does it." Empty list = all
              primaries participate (back-compat). Auto-schedule honors
              this list when assigning the rotation. ── */}
          {mode === 'scheduled' && (
            <div style={{
              padding: 12, marginBottom: 16, borderRadius: 10,
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
            }}>
              <div className="flex items-center justify-between mb-2 gap-2">
                <div>
                  <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>
                    👥 Drinks rotation participants
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-2)' }}>
                    {eligibleIds.length === 0
                      ? 'All full-time players participate (default).'
                      : `${eligibleIds.length} player${eligibleIds.length === 1 ? '' : 's'} selected for the rotation.`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setPickerOpen(o => !o)}
                  disabled={saving}
                  style={{
                    padding: '6px 12px', borderRadius: 8,
                    background: pickerOpen ? 'var(--ice-a15)' : 'var(--surface-3)',
                    border: `1px solid ${pickerOpen ? 'var(--ice-a30)' : 'var(--border)'}`,
                    color: pickerOpen ? 'var(--ice)' : 'var(--text)',
                    fontSize: 12, fontWeight: 700,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.5 : 1,
                    flexShrink: 0,
                  }}>
                  {pickerOpen ? 'Close' : 'Edit list'}
                </button>
              </div>

              {pickerOpen && (
                <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                  {pickerLoading && (
                    <p className="text-xs text-secondary text-center py-3">
                      Loading roster…
                    </p>
                  )}
                  {!pickerLoading && pickerMembers !== null && pickerMembers.length === 0 && (
                    <p className="text-xs text-secondary text-center py-3">
                      No active full-time players on this team yet.
                    </p>
                  )}
                  {!pickerLoading && pickerMembers !== null && pickerMembers.length > 0 && (() => {
                    // Local picker state — stage selection inside the
                    // picker, only commit on Save. This way the coach
                    // can experiment without auto-saving on every
                    // checkbox tap (each save is a backend round-trip
                    // + a save-toast that gets noisy).
                    const stagedKey = pickerOpen ? 'open' : 'closed'; // re-mount on open
                    return (
                      <DrinksParticipantPicker
                        key={stagedKey}
                        members={pickerMembers}
                        initialSelected={eligibleIds}
                        onCancel={() => setPickerOpen(false)}
                        onSave={applyEligible}
                        saving={saving}
                      />
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Auto-schedule button (scheduled mode only) */}
          {mode === 'scheduled' && (
            <button type="button"
              onClick={autoSchedule} disabled={scheduling}
              className="btn-ghost w-full justify-center mb-4 text-sm"
            >
              {scheduling
                ? <><div className="spinner" style={{ width:14, height:14 }} /> Scheduling…</>
                : <><RefreshCw size={14} /> Auto-Assign Future Games (Fair Rotation)</>}
            </button>
          )}

          {/* May 4 2026 — bulk-unassign actions. Two-step confirm: first
              click arms the confirm state ("Tap again to confirm"), second
              click within 5s fires the API. ── */}
          <div style={{
            padding: 12, marginBottom: 16, borderRadius: 10,
            background: 'rgba(255,149,0,0.04)',
            border: '1px solid rgba(255,149,0,0.18)',
          }}>
            <p className="text-xs font-semibold mb-2 flex items-center" style={{ color: 'var(--puck)', gap: 6 }}>
              ⚠ Bulk-clear assignments
              {drinksCompact && (
                <span
                  title='Wipe assignments in bulk. "Future games" keeps past stats; "All games" wipes the whole history.'
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 20, height: 20, borderRadius: '50%',
                    background: 'var(--surface-3)', color: 'var(--text-3)',
                    cursor: 'help', flexShrink: 0,
                  }}
                  aria-label="Bulk-clear help">
                  <HelpCircle size={14} />
                </span>
              )}
            </p>
            {/* v2.5.104 — long description hidden in compact mode; the
                tooltip on the icon above carries the same info. */}
            {!drinksCompact && (
              <p className="text-xs mb-3" style={{ color: 'var(--text-2)', lineHeight: 1.5 }}>
                Wipe drinks assignments in bulk — useful when seasons change
                or when restructuring the rotation. Past assignments stay in
                the stats history when you choose "future games only."
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => unassignAll('future')}
                disabled={unassigning}
                style={{
                  padding: '8px 12px', borderRadius: 8,
                  background: unassignConfirm === 'future' ? 'rgba(255,149,0,0.18)' : 'var(--surface-2)',
                  border: `1px solid ${unassignConfirm === 'future' ? 'var(--puck)' : 'var(--border)'}`,
                  color: unassignConfirm === 'future' ? 'var(--puck)' : 'var(--text)',
                  fontSize: 12, fontWeight: 700, letterSpacing: '0.02em',
                  cursor: unassigning ? 'wait' : 'pointer',
                  opacity: unassigning ? 0.5 : 1,
                  textAlign: 'left',
                }}>
                {unassignConfirm === 'future'
                  ? '⚠ Tap again to confirm'
                  : '🔄 Unassign FUTURE games'}
              </button>
              <button
                type="button"
                onClick={() => unassignAll('all')}
                disabled={unassigning}
                style={{
                  padding: '8px 12px', borderRadius: 8,
                  background: unassignConfirm === 'all' ? 'rgba(255,68,68,0.15)' : 'var(--surface-2)',
                  border: `1px solid ${unassignConfirm === 'all' ? 'var(--penalty)' : 'var(--border)'}`,
                  color: unassignConfirm === 'all' ? 'var(--penalty)' : 'var(--text)',
                  fontSize: 12, fontWeight: 700, letterSpacing: '0.02em',
                  cursor: unassigning ? 'wait' : 'pointer',
                  opacity: unassigning ? 0.5 : 1,
                  textAlign: 'left',
                }}>
                {unassignConfirm === 'all'
                  ? '⚠ Tap again to confirm — wipes history!'
                  : '🗑 Unassign ALL games (incl. past)'}
              </button>
            </div>
            {!drinksCompact && (
              <p className="text-xs mt-2" style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>
                "Future games" only clears assignments on games today or later.
                "All games" wipes the entire drinks history — use with caution.
              </p>
            )}
          </div>

          {/* Stats toggle */}
          <button type="button" onClick={loadStats} className="flex items-center gap-2 text-xs text-secondary w-full justify-between py-1">
            <span className="flex items-center gap-1.5"><BarChart2 size={13} /> Drinks count history + top picks</span>
            {showStats ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>

          {showStats && stats && (
            <div className="mt-3 space-y-1.5">
              {/* Apr 2026 (Tom_Notes G3/G4) — fairness summary line.
                  Tells the coach at a glance how balanced the duty
                  is across the team. neverBroughtCount > 0 flips
                  the colour to amber as a soft alert. */}
              {stats.summary && stats.summary.totalGames > 0 && (
                <div className="mb-3 p-3 rounded-xl text-xs"
                  style={{
                    background: stats.summary.neverBroughtCount > 0
                      ? 'rgba(255,149,0,0.06)'
                      : 'rgba(0,232,122,0.06)',
                    border: `1px solid ${stats.summary.neverBroughtCount > 0
                      ? 'rgba(255,149,0,0.2)'
                      : 'rgba(0,232,122,0.2)'}`,
                  }}>
                  <div className="font-semibold mb-1">
                    📊 Fair share: {stats.summary.fairShare.toFixed(1)} per player
                  </div>
                  <div style={{ color: 'var(--text-2)', lineHeight: 1.4 }}>
                    {stats.summary.totalGames} drinks slot{stats.summary.totalGames !== 1 ? 's' : ''} across{' '}
                    {stats.summary.memberCount} active member{stats.summary.memberCount !== 1 ? 's' : ''}.
                    {stats.summary.neverBroughtCount > 0 && (
                      <>
                        {' '}<strong style={{ color: 'var(--puck)' }}>
                          {stats.summary.neverBroughtCount} {stats.summary.neverBroughtCount === 1 ? 'has' : 'have'} never brought
                        </strong>.
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Top picks — uses deviation-based ranking when
                  available, falls back to the legacy least-total
                  list otherwise. */}
              {(stats.recommendationsByDeviation || stats.recommendations)?.length > 0 && (
                <div className="p-3 rounded-xl mb-3"
                  style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)' }}>
                  <p className="text-xs font-bold mb-2" style={{ color: 'var(--ice)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    🏅 Top Picks (most behind on fair share)
                  </p>
                  {(stats.recommendationsByDeviation || stats.recommendations).map((r, i) => {
                    const lastLabel = r.daysSinceLast == null
                      ? 'never'
                      : r.daysSinceLast === 0
                        ? 'today'
                        : `${r.daysSinceLast}d ago`;
                    return (
                      <div key={r.userId} className="flex items-center justify-between py-1">
                        <span className="text-sm font-semibold">{['🥇','🥈','🥉'][i]} {r.name}</span>
                        <span className="text-xs text-secondary">
                          {r.total} total · last {lastLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Full list — added "Last" column (Apr 2026, G3/G4).
                  Apr 30 2026 — clarified header copy because coaches
                  reported confusion: "Upcoming" counts everyone with
                  any upcoming game_drinks row (volunteered OR auto-
                  assigned via the scheduler), so users who haven't
                  explicitly volunteered can still show non-zero
                  values. The "Scheduled" header makes that
                  unambiguous; the title attribute spells it out
                  on hover.
                  v2.5.149 — sort selector (alphabetical / highest
                  contributor) + "Last" now shows the actual date,
                  not just "Nd ago". */}
              <div className="flex items-center justify-end mb-2 gap-2">
                <label className="text-xs text-secondary" htmlFor="drinks-sort">Sort:</label>
                <select
                  id="drinks-sort"
                  className="input-field"
                  style={{ maxWidth: 200, padding: '4px 8px', fontSize: 12 }}
                  value={statsSort}
                  onChange={e => setStatsSort(e.target.value)}>
                  <option value="contributor">Highest contributor first</option>
                  <option value="alpha">Alphabetical (A–Z)</option>
                </select>
              </div>
              {/* Mobile fix — wrap the 4-col stats table in a horizontal-scroll
                  container with a minWidth floor. At ~390px, 4 equal grid-cols-4
                  columns squeeze to ~70-80px each and the "Scheduled" header can
                  crowd its neighbors; scrolling beats silently overlapping text. */}
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)', minWidth: 380 }}>
                <div className="grid grid-cols-4 px-3 py-2 text-xs font-bold text-secondary uppercase tracking-wider"
                  style={{ background: 'var(--surface-2)', letterSpacing: '0.06em' }}>
                  <span>Player</span>
                  <span className="text-center" title="Past games where this player brought drinks (any non-ignored status).">Past</span>
                  <span className="text-center" title="Upcoming games where this player is scheduled to bring drinks — includes both self-volunteered and coach- or auto-scheduled assignments.">Scheduled</span>
                  <span className="text-right" title="Most recent past game where this player was on drinks duty.">Last</span>
                </div>
                {(() => {
                  // v2.5.149 — apply the chosen sort. 'contributor'
                  // = most past drinks first (ties → more recent
                  // last-brought first, then name). 'alpha' = name
                  // A–Z. We copy before sorting so we don't mutate
                  // the stats array in state.
                  const sorted = [...(stats.stats || [])].sort((a, b) => {
                    if (statsSort === 'alpha') {
                      return (a.name || '').localeCompare(b.name || '');
                    }
                    // contributor: highest 'historical' (past duty) first
                    if ((b.historical || 0) !== (a.historical || 0)) {
                      return (b.historical || 0) - (a.historical || 0);
                    }
                    // tie-break: more recent last-brought first (smaller
                    // daysSinceLast). never-brought (null) sorts last.
                    const aD = a.daysSinceLast == null ? Infinity : a.daysSinceLast;
                    const bD = b.daysSinceLast == null ? Infinity : b.daysSinceLast;
                    if (aD !== bD) return aD - bD;
                    return (a.name || '').localeCompare(b.name || '');
                  });
                  return sorted.map(s => {
                    // v2.5.149 — show the actual last-brought date
                    // (e.g. "May 18") instead of only a relative "Nd".
                    // Keep the relative hint in the tooltip. Falls back
                    // to "never" when the player has never brought.
                    let lastLabel = 'never';
                    if (s.lastBroughtDate) {
                      // Normalize to a date-only value parsed at LOCAL
                      // noon so a UTC-midnight timestamp doesn't render
                      // as the previous day for users west of UTC.
                      const ymd = String(s.lastBroughtDate).slice(0, 10);
                      const [y, m, dd] = ymd.split('-').map(Number);
                      const d = (y && m && dd) ? new Date(y, m - 1, dd, 12) : new Date(s.lastBroughtDate);
                      lastLabel = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                    }
                    const relHint = s.daysSinceLast == null
                      ? 'Never brought drinks'
                      : s.daysSinceLast === 0
                        ? 'Today'
                        : `${s.daysSinceLast} day${s.daysSinceLast === 1 ? '' : 's'} ago`;
                    const lastColor = s.daysSinceLast == null
                      ? 'var(--puck)'
                      : 'var(--text-2)';
                    return (
                      <div key={s.userId} className="grid grid-cols-4 px-3 py-2 text-sm border-t items-center"
                        style={{ borderColor: 'var(--border)' }}>
                        <span className="font-medium truncate">{s.name}</span>
                        <span className="text-center text-secondary">{s.historical}</span>
                        <span className="text-center text-secondary">{s.upcoming}</span>
                        <span className="text-right text-xs" title={relHint}
                          style={{ color: lastColor, fontWeight: s.daysSinceLast == null ? 600 : 400 }}>
                          {lastLabel}
                        </span>
                      </div>
                    );
                  });
                })()}
              </div>
              </div>
            </div>
          )}

          {/* v2.5.182 — coach-alert lead time. Replaces the old fixed
              "9 PM the night before" trigger with a configurable
              hours-before. */}
          <div className="mt-4">
            <label className="label">Alert me if nobody's assigned</label>
            <div className="flex items-center gap-2">
              <input type="number" min="1" max="168" className="input-field" style={{ maxWidth: 110 }}
                value={coachAlertHours}
                onChange={(e) => {
                  const v = e.target.value === '' ? '' : Math.max(1, Math.min(168, parseInt(e.target.value, 10) || 1));
                  setCoachAlertHours(v);
                  setDirty(true);
                }} />
              <span className="text-sm" style={{ color: 'var(--text-2)' }}>hours before each game</span>
            </div>
            <FieldHelp>
              If no one has signed up to bring {label} this many hours before game time, you (and any
              other coaches) get an alert email + push to assign someone. Default 24 hours.
            </FieldHelp>
          </div>

          {/* Info — v2.5.104 compact mode: collapse to single short
              sentence + tooltip when compact_toggle_descriptions is
              on. Full mode keeps the original 2-line callout. */}
          {drinksCompact ? (
            <div className="p-2 rounded-xl text-xs mt-4 flex items-center justify-between gap-2"
              style={{ background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.12)' }}>
              <span style={{ color: 'var(--ice)' }}>
                <strong>How reminders work:</strong>
                <span className="text-secondary ml-1">Email + push to the assignee night-before & ~3h before.</span>
              </span>
              <span
                title={`The assigned player gets an email + push notification the evening before and again ~3 hours before game time. If nobody is assigned ${coachAlertHours}h before game time, you'll get a coach alert email to assign someone.`}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 20, height: 20, borderRadius: '50%',
                  background: 'var(--surface-3)', color: 'var(--text-3)',
                  cursor: 'help', flexShrink: 0,
                }}
                aria-label="How reminders work help">
                <HelpCircle size={14} />
              </span>
            </div>
          ) : (
            <div className="p-3 rounded-xl text-sm mt-4"
              style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)' }}>
              <p className="font-semibold mb-1" style={{ color: 'var(--ice)' }}>How reminders work</p>
              <p className="text-secondary text-xs leading-relaxed">
                The assigned player gets an <strong>email + push notification</strong> the evening before and again ~3 hours before game time.
                If nobody is assigned <strong>{coachAlertHours} hours</strong> before game time, you'll get a coach alert email to assign someone.
              </p>
            </div>
          )}
        </>
      )}

      {/* v2.5.105 — Save Settings button. Coach feedback: "in the
          drinks duty, instead of auto saving each change, have a
          'Save Settings' to save whatever the coach decides on, the
          'Save Settings' button is consistent with all the other
          pages." Mirrors the styling + behavior of the main page's
          save button (#team-settings-save-button). Disabled when
          either nothing has changed (no point saving) OR a save is
          already in flight. Shown even when drinks_enabled is OFF
          because the coach may want to save the OFF state itself. */}
      <button
        type="button"
        onClick={save}
        disabled={!dirty || saving}
        className="btn-ice w-full justify-center py-3 mt-6"
        style={{
          opacity: (!dirty || saving) ? 0.55 : 1,
          cursor:  (!dirty || saving) ? 'not-allowed' : 'pointer',
        }}>
        {saving
          ? <><div className="spinner" style={{ width:16, height:16 }} /> Saving…</>
          : <><Save size={16} /> Save Settings{dirty ? '' : ''}</>}
      </button>
    </div>
  );
}

// ── Stripe Connect Panel (Apr 2026) ───────────────────────────────────────
// Shown inside the Payment Collection card. Handles:
//   * Loading the team's current Connect status on mount
//   * "Connect Stripe Account" button → POST /onboard → redirect
//   * Status display (Connected / Onboarding / Restricted / Disconnected)
//   * "Open Stripe Dashboard" for connected coaches
//   * A reload after the coach returns from Stripe onboarding
//     (detects ?stripe=return in URL)
function StripeConnectPanel({ teamId, teamName, canEdit }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Country is only asked at FIRST onboarding; Stripe locks it
  // once an Express account is created. Default CA; most Game Day
  // users are Canadian recreational leagues.
  const [country, setCountry] = useState('CA');

  // v2.5.274 — reuse an already-onboarded Stripe account from one of
  // the coach's other teams. GET /status returns `reusable` (the
  // coach's finished accounts on other teams) whenever this team has
  // no account or an unfinished one; POST /link copies the chosen one
  // onto this team. When a reusable account exists, it is offered as
  // the primary action and the "new account" flow is tucked behind a
  // "connect a different account" link — a second full onboarding is
  // exactly what went wrong in the field (left half-done → dead
  // payment links), so it should be the exception, not the default.
  const [linking, setLinking] = useState(false);
  const [showNewAccountFlow, setShowNewAccountFlow] = useState(false);
  const reusable = Array.isArray(status?.reusable) ? status.reusable : [];
  const linkAccount = async (src) => {
    if (!src?.teamId || linking) return;
    setLinking(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/stripe-connect/link`, { sourceTeamId: src.teamId });
      if (data?.alreadyLinked) {
        toast('This team already uses that Stripe account.', { icon: 'ℹ️' });
        await load();
      } else {
        setStatus(data);
        toast.success(`Done — this team now uses the Stripe account from ${src.teamName}. Payments go to the same bank.`, { duration: 7000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not link that Stripe account.', { duration: 8000 });
    } finally {
      setLinking(false);
    }
  };
  // Plain render helper (not a nested component) so React doesn't
  // remount the box on every parent render.
  const renderReusableBox = (replacing) => {
    if (!reusable.length) return null;
    return (
      <div className="mb-3" style={{
        background: 'rgba(0,232,122,0.08)', border: '1px solid rgba(0,232,122,0.35)',
        borderRadius: 8, padding: '10px 12px', fontSize: 12, color: 'var(--text)',
      }}>
        <strong style={{ color: 'var(--goal)' }}>
          ✅ You already have a Stripe account set up{reusable.length === 1 ? ` on ${reusable[0].teamName}` : ''}.
        </strong>{' '}
        {replacing
          ? 'Skip this unfinished onboarding and use it for this team too — no new forms, same bank account.'
          : 'Use it for this team too — no new forms, no new onboarding, same bank account.'}
        <div className="mt-2 flex flex-col gap-2">
          {reusable.map(src => (
            <button key={src.accountId} type="button"
              onClick={() => linkAccount(src)}
              disabled={linking || busy}
              className="btn-ice text-sm font-bold w-full justify-center py-2">
              {linking ? 'Linking…' : `Use my Stripe account from ${src.teamName}`}
              {!linking && !(src.chargesEnabled && src.payoutsEnabled) && ' (still under Stripe review)'}
            </button>
          ))}
        </div>
        {!replacing && !showNewAccountFlow && (
          <button type="button" onClick={() => setShowNewAccountFlow(true)}
            className="mt-2 text-xs underline" style={{ color: 'var(--text-2)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            Payments for this team should go to a different bank? Connect a separate Stripe account instead.
          </button>
        )}
      </div>
    );
  };

  // v2.5.82 — admin-only Reset Connect feature. The modal mirrors
  // the Delete Team confirmation pattern: requires typing the
  // team name to enable the destructive button. The reset itself
  // hits DELETE /stripe-connect/account, which deletes the Stripe
  // account on Stripe's side AND clears the team row's stripe_*
  // columns. Used when a team's onboarding is in a bad state
  // (wrong email, wrong person started it, etc) and the cleanest
  // recovery is to tear down and start fresh.
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetConfirmName, setResetConfirmName] = useState('');
  const [resetting, setResetting] = useState(false);
  const closeResetModal = () => {
    if (resetting) return;
    setShowResetModal(false);
    setResetConfirmName('');
  };
  const performReset = async () => {
    // Defence: a missing teamName means we can't actually verify the
    // confirmation. Bail early — the button should already be disabled
    // by the same check, this is belt and suspenders.
    if (!teamName || (resetConfirmName || '').trim() !== teamName.trim()) return;
    setResetting(true);
    try {
      const { data: rr } = await api.delete(`/teams/${teamId}/stripe-connect/account`);
      // v2.5.274 — shared account (mig 111): unlinked here, kept on Stripe.
      toast.success(rr?.unlinkedOnly
        ? `Unlinked. The Stripe account stays — ${rr.sharedWith} other team${rr.sharedWith === 1 ? '' : 's'} still use it.`
        : 'Stripe Connect link reset. Team can re-onboard fresh.');
      setShowResetModal(false);
      setResetConfirmName('');
      await load();  // refresh panel — should now show "Not connected"
    } catch (err) {
      const errBody = err.response?.data;
      // 409 = charges-exist guard fired. Surface the specific
      // server message so admins know they can't reset without
      // manual Stripe intervention.
      if (err.response?.status === 409) {
        toast.error(errBody?.error || 'Reset blocked: account has charge history.', { duration: 8000 });
      } else {
        toast.error(errBody?.error || 'Failed to reset Stripe Connect.');
      }
    } finally {
      setResetting(false);
    }
  };

  const load = async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/teams/${teamId}/stripe-connect/status`);
      setStatus(data);
    } catch (err) {
      console.error('stripe status load failed:', err);
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  // v2.5.149 — actually re-query Stripe (not just re-read our DB).
  // Bug: the "Refresh" button was wired to load(), which calls GET
  // /status — and /status returns the LOCALLY CACHED flags
  // (stripe_charges_enabled etc.) that are only updated by the
  // account.updated webhook. When a coach completes onboarding in
  // a different browser/tab (so they never hit the ?stripe=return
  // redirect that triggers a refresh) AND the webhook is delayed or
  // dropped, the local flags stay stale → UI shows "Onboarding
  // incomplete" forever even though Stripe says the account is
  // fully enabled. (This is exactly the coach's report: friend
  // completed + got approved on Stripe's side, app still says
  // incomplete.)
  //
  // POST /refresh calls stripe.accounts.retrieve() server-side and
  // writes the fresh flags to our DB, then we re-load to repaint.
  const refreshFromStripe = async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/stripe-connect/refresh`);
      // v2.5.274 — a successful live check clears any earlier admin
      // "self-heal failed" diagnostic carried in state.
      setStatus(prev => ({ ...prev, selfHealFailed: false, adminDiagnostic: false, ...data }));
      // v2.5.151 — clearer verdict after a manual check. The button
      // asks Stripe directly (accounts.retrieve) and reports back
      // exactly what Stripe says, including WHAT is still outstanding
      // so the coach isn't left guessing why it's not "fully" done.
      const reqs = data?.requirements || {};
      const outstanding = [...(reqs.past_due || []), ...(reqs.currently_due || [])]
        .filter(x => typeof x === 'string' && x.length);
      if (data?.chargesEnabled && data?.payoutsEnabled) {
        toast.success('All set — Stripe says this account is fully connected. 🎉');
      } else if (data?.detailsSubmitted && outstanding.length === 0) {
        toast('All your info is in. Stripe is still reviewing the account — usually 1-2 business days.', { icon: '⏳', duration: 6000 });
      } else if (outstanding.length > 0) {
        const first = String(outstanding[0]).replace(/_/g, ' ').replace(/\./g, ' · ');
        toast(`Stripe still needs: ${first}${outstanding.length > 1 ? ` (+${outstanding.length - 1} more)` : ''}. Tap "${data?.detailsSubmitted ? 'Update Info' : 'Resume Onboarding'}".`, { icon: '⚠️', duration: 7000 });
      } else {
        toast('Stripe still reports onboarding as unfinished. Tap "Resume Onboarding" to continue.', { icon: 'ℹ️', duration: 6000 });
      }
    } catch (err) {
      console.error('stripe refresh failed:', err);
      const body = err.response?.data;
      toast.error(body?.error || 'Could not reach Stripe — try again in a moment.');
      // v2.5.274 — platform admins get the raw Stripe error too, so a
      // "temporarily unavailable" toast is diagnosable on the spot.
      if (body?.adminDiagnostic) {
        toast.error(`Admin: ${body.stripeErrorType}${body.stripeErrorCode ? ` / ${body.stripeErrorCode}` : ''} — ${body.stripeErrorMessage || ''}`, { duration: 15000 });
      }
      // Fall back to a local re-read so the panel isn't left stale.
      await load();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [teamId]);

  // When the coach returns from onboarding (?stripe=return), poll
  // Stripe right away via /refresh — webhook may not have arrived yet,
  // and we want the UI to reflect the new state immediately.
  //
  // ?stripe=refresh arrives when the coach abandoned the Stripe flow
  // mid-way (or the Account Link expired). Just clean the URL param;
  // no toast, no reload — the user can tap "Resume Onboarding" from
  // the panel.
  useEffect(() => {
    if (!teamId) return;
    const params = new URLSearchParams(window.location.search);
    const stripeParam = params.get('stripe');
    if (stripeParam === 'return') {
      (async () => {
        try {
          const { data } = await api.post(`/teams/${teamId}/stripe-connect/refresh`);
          await load();
          // Accurate toast based on actual state. "Complete" means
          // Stripe accepted all details; "Under review" / "needs info"
          // cover the middle states.
          if (data?.chargesEnabled && data?.payoutsEnabled) {
            toast.success('Stripe onboarding complete! 🎉');
          } else if (data?.detailsSubmitted) {
            toast('Thanks! Stripe is reviewing your details — this usually takes 1-2 business days.', { icon: '⏳' });
          } else {
            toast('Onboarding isn\'t finished yet — tap Resume to continue.', { icon: 'ℹ️' });
          }
        } catch { /* non-fatal */ }
      })();
    }
    // Always clean the query param so reloads don't refire the handler
    // or leak Stripe state in the URL.
    if (stripeParam) {
      const url = new URL(window.location.href);
      url.searchParams.delete('stripe');
      window.history.replaceState({}, '', url.toString());
    }
  }, [teamId]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/stripe-connect/onboard`, {
        country: status?.country || country,
      });
      window.location.href = data.url;
    } catch (err) {
      // Apr 2026 — the backend flags platform-side Connect setup
      // problems with platformSetupRequired:true. These aren't
      // something the coach can fix (it's a one-time setup at
      // dashboard.stripe.com/connect by the platform owner), so
      // show a longer-lived modal-style toast instead of the usual
      // 4-second error toast that they might miss.
      const errorBody = err.response?.data;
      if (errorBody?.platformSetupRequired) {
        toast.error(errorBody.error, { duration: 10000 });
      } else {
        toast.error(errorBody?.error || 'Could not start Stripe onboarding');
      }
      setBusy(false);
    }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/stripe-connect/dashboard`);
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not open Stripe dashboard');
    }
    setBusy(false);
  };

  if (!canEdit) return null;

  // Badge state logic:
  //   * No account              → "Not connected"       (grey)
  //   * Account, !details       → "Onboarding incomplete" (amber)
  //   * details, !charges       → "Under review"        (amber)
  //   * charges + payouts       → "Connected"           (goal)
  //   * charges, !payouts       → "Charges only"        (amber — can accept but not cash out)
  let badge = { text: 'Not connected', bg: 'var(--surface-3)', fg: 'var(--text-2)', border: 'var(--border)' };
  let helperCopy = null;
  if (status?.connected) {
    if (!status.detailsSubmitted) {
      badge = { text: 'Onboarding incomplete', bg: 'rgba(255,193,7,0.12)', fg: '#e8a200', border: 'rgba(255,193,7,0.35)' };
      helperCopy = 'Stripe onboarding was started but not finished. Click below to pick up where you left off.';
    } else if (!status.chargesEnabled) {
      badge = { text: 'Under Stripe review', bg: 'rgba(255,193,7,0.12)', fg: '#e8a200', border: 'rgba(255,193,7,0.35)' };
      helperCopy = 'Stripe is reviewing your account. This usually takes 1-2 business days. You\'ll be able to accept payments once approved.';
    } else if (!status.payoutsEnabled) {
      badge = { text: 'Charges only — payouts pending', bg: 'rgba(255,193,7,0.12)', fg: '#e8a200', border: 'rgba(255,193,7,0.35)' };
      helperCopy = 'You can accept payments but payouts to your bank aren\'t enabled yet. Add a bank account in your Stripe Dashboard.';
    } else {
      badge = { text: 'Connected', bg: 'rgba(0,232,122,0.12)', fg: 'var(--goal)', border: 'rgba(0,232,122,0.35)' };
    }
  }

  const reqs = status?.requirements || {};
  const hasPending = (reqs.currently_due?.length || 0) > 0 || (reqs.past_due?.length || 0) > 0;
  const disabledReason = reqs.disabled_reason;

  return (
    <div className="p-4 rounded-xl" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span style={{ fontSize: 18 }}>🏦</span>
          {/* 2026-07-11 (batch-B G2 follow-up) — was "Payouts (Stripe Connect)".
              The G2 rename only touched the outer section h2 (now "🏦 Stripe"
              at line ~5535); this inner panel heading was missed, so the page
              showed "Stripe" immediately above "Payouts (Stripe Connect)" —
              a stale duplicate label. Renamed to match. */}
          <h3 className="font-semibold text-sm">Stripe Connect Status</h3>
        </div>
        <span className="text-xs font-bold px-2 py-1 rounded" style={{ background: badge.bg, color: badge.fg, border: `1px solid ${badge.border}` }}>
          {badge.text}
        </span>
      </div>

      {loading ? (
        <p className="text-xs text-secondary">Loading…</p>
      ) : !status?.connected ? (
        <>
          <p className="text-xs text-secondary mb-3">
            Connect a Stripe account to have spare &amp; season payments deposited directly
            into your bank — minus the processing fee. Without this, payments land in the
            platform account and must be settled offline.
          </p>
          {renderReusableBox(false)}
          {(reusable.length === 0 || showNewAccountFlow) && (<>
          {/* Apr 2026 — Friendly, plain-English intro for coaches.
              Reassures that the setup is quick and that all sensitive
              data goes directly to Stripe, not to us. Avoids Stripe
              jargon ("Express", "API") — coaches don't need to know
              those terms to complete onboarding. */}
          <div className="mb-3" style={{
            background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.20)',
            borderRadius: 8, padding: '10px 12px', fontSize: 12, color: 'var(--text)',
          }}>
            <strong style={{ color: 'var(--ice)' }}>💡 Setup is quick and easy.</strong>{' '}
            There's nothing complicated to install or configure. Stripe takes care of
            the secure link between your account and Game Day — you just tap the button
            below and fill out a short form on Stripe's site. Your banking details and
            ID stay with Stripe and never pass through Game Day's servers. Go ahead and
            click the blue <strong>Connect Stripe Account</strong> button below and
            follow the steps.
          </div>
          {/* Apr 2026 — collapsible "How it works" block. Walks the
              coach through the actual Stripe onboarding sequence so
              there are no surprises ("why is it asking for my SIN?",
              "what's an MCC code?", etc). Closed by default to keep
              the settings panel compact. */}
          <details className="mb-3" style={{
            background: 'var(--surface-3)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '10px 12px',
          }}>
            <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>
              📋 What to expect — step by step
            </summary>
            <ol style={{ marginTop: 10, paddingLeft: 22, fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6 }}>
              <li><strong>Pick your country</strong> below (Canada or US). This locks once your account is created.</li>
              <li>Click <strong>Connect Stripe Account</strong>. You'll be redirected to Stripe's secure onboarding page.</li>
              <li><strong>Enter your business info:</strong> legal name, address, date of birth, and government ID number (SIN in Canada / SSN last 4 in US). Stripe needs this for tax and anti-fraud compliance — Game Day never sees it.</li>
              <li><strong>Verify your identity</strong> by uploading a photo of your driver's license or passport.</li>
              <li><strong>Add your bank account</strong> — routing &amp; account number. This is where your team's spare/season fees will be deposited.</li>
              <li><strong>Review and submit.</strong> Stripe approves most accounts instantly; some require 1–2 business days for review.</li>
              <li>You'll be sent back to Game Day automatically. Status here will flip to <span style={{ color: 'var(--goal)' }}>Connected</span> once Stripe confirms charges and payouts are enabled.</li>
            </ol>
            <p style={{ marginTop: 10, fontSize: 11, color: 'var(--text-2)', fontStyle: 'italic' }}>
              Total time: about 5–10 minutes. Have your ID and bank info ready before you start.
            </p>
          </details>
          <div className="mb-3">
            <label className="label text-xs mb-1">Country (locked once account is created)</label>
            <select
              value={country}
              onChange={e => setCountry(e.target.value)}
              disabled={busy}
              className="input-field text-sm w-full">
              <option value="CA">🇨🇦 Canada</option>
              <option value="US">🇺🇸 United States</option>
            </select>
          </div>
          <button type="button" onClick={connect} disabled={busy}
            className="btn-ice text-sm font-bold w-full justify-center py-2">
            {busy ? 'Starting…' : 'Connect Stripe Account'}
          </button>
          <p className="text-xs text-secondary mt-2 text-center">
            Takes about 3-5 minutes. You'll need a photo ID and a bank account number.
          </p>
          </>)}
        </>
      ) : (
        <>
          {helperCopy && (
            <p className="text-xs mb-3" style={{ color: '#e8a200' }}>
              {helperCopy}
            </p>
          )}
          {/* v2.5.274 — unfinished onboarding here + a finished account
              on another of the coach's teams: offer the shortcut. */}
          {!status.detailsSubmitted && renderReusableBox(true)}
          {status.selfHealFailed && status.adminDiagnostic && (
            <p className="text-xs mb-3 p-2 rounded" style={{ background: 'rgba(255,68,68,0.08)', border: '1px solid rgba(255,68,68,0.3)', color: 'var(--text-2)' }}>
              <strong>Admin:</strong> live status check failed — {status.stripeErrorType}
              {status.stripeErrorCode ? ` / ${status.stripeErrorCode}` : ''}: {status.stripeErrorMessage}
            </p>
          )}
          {/* v2.5.149 — when the account exists but we still think
              onboarding is incomplete, tell the coach the most common
              cause: their info IS in on Stripe's side but our copy is
              stale because the account.updated webhook hasn't reached
              us. "Refresh from Stripe" pulls the live status directly.
              Only show when details aren't submitted (the exact state
              the coach reported). */}
          {status.connected && !status.detailsSubmitted && (
            <p className="text-xs mb-3 p-2 rounded" style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)', color: 'var(--text-2)' }}>
              Already finished on Stripe's site? Tap <strong>Check account status</strong> below — it asks Stripe directly and updates this page with your real status.
            </p>
          )}
          {hasPending && (
            <div className="mb-3 p-2 rounded text-xs" style={{ background: 'rgba(255,193,7,0.08)', border: '1px solid rgba(255,193,7,0.25)' }}>
              <p className="font-semibold" style={{ color: '#e8a200' }}>Stripe needs more info:</p>
              <ul className="list-disc ml-4 mt-1" style={{ color: 'var(--text-2)' }}>
                {[...(reqs.past_due || []), ...(reqs.currently_due || [])]
                  .slice(0, 5)
                  .map(r => <li key={r}>{r.replace(/_/g, ' ').replace(/\./g, ' · ')}</li>)}
              </ul>
            </div>
          )}
          {disabledReason && (
            <p className="text-xs mb-3" style={{ color: '#d32f2f' }}>
              <strong>Restricted:</strong> {disabledReason.replace(/_/g, ' ')}
            </p>
          )}
          <div className="flex gap-2 flex-wrap">
            {(!status.detailsSubmitted || hasPending) && (
              <button type="button" onClick={connect} disabled={busy}
                className="btn-ice text-xs font-bold px-3 py-2">
                {status.detailsSubmitted ? 'Update Info' : 'Resume Onboarding'}
              </button>
            )}
            {status.detailsSubmitted && (
              <button type="button" onClick={openDashboard} disabled={busy}
                className="text-xs font-bold px-3 py-2 rounded-lg"
                style={{ background: 'var(--surface-3)', color: 'var(--text)', border: '1px solid var(--border)' }}>
                {busy ? '…' : 'Open Stripe Dashboard ↗'}
              </button>
            )}
            {/* v2.5.151 — promoted to a clear, prominent action. This
                is the "double-check with Stripe" button: it calls
                Stripe's accounts.retrieve API live and reports the real
                account status. Coaches reach for this when the panel
                looks wrong (e.g. shows incomplete after they finished
                on Stripe's site). Styled with a border + icon so it
                reads as the obvious thing to press, not a quiet
                secondary link. */}
            <button type="button" onClick={refreshFromStripe} disabled={busy || loading}
              className="text-xs font-bold px-3 py-2 rounded-lg"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'rgba(0,212,255,0.10)',
                color: 'var(--ice)',
                border: '1px solid rgba(0,212,255,0.40)',
                cursor: (busy || loading) ? 'not-allowed' : 'pointer',
                opacity: (busy || loading) ? 0.6 : 1,
              }}>
              <RefreshCw size={13} style={loading ? { animation: 'spin 0.8s linear infinite' } : undefined} />
              {loading ? 'Checking with Stripe…' : 'Check account status'}
            </button>
            {/* v2.5.82 — Admin-only Reset Connect button. Visible only
                when the team has a connected Stripe account AND the
                current user is a platform admin. Recovery tool for
                bad-state onboardings (wrong email, wrong person, etc).
                Backend re-validates admin role + blocks if charges
                exist on the account. Kept visually distinct (red)
                so it's not mistaken for a routine action. */}
            {isAdmin && (
              <button type="button"
                onClick={() => { setShowResetModal(true); setResetConfirmName(''); }}
                disabled={busy || resetting}
                className="text-xs font-bold px-3 py-2 rounded-lg"
                style={{
                  background: 'rgba(255,68,68,0.10)',
                  color: 'var(--penalty)',
                  border: '1px solid rgba(255,68,68,0.35)',
                }}>
                Admin: Reset Connect
              </button>
            )}
          </div>
          <p className="text-xs text-secondary mt-3">
            Account ID: <code style={{ fontSize: 11 }}>{status.accountId}</code>
            {status.country ? ` · ${status.country}` : ''}
            {status.linkedFrom ? ' · shared with your other team' : ''}
          </p>
        </>
      )}

      {/* v2.5.82 — Reset Stripe Connect confirmation modal (admin-only).
          Mirrors the Delete Team modal pattern: type team name to
          enable destructive action. Backend re-validates admin role
          and blocks if charges exist. */}
      {showResetModal && isAdmin && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ zIndex: 500 }}>
          <BodyScrollLock />
          <div className="glass w-full sm:max-w-md p-6 animate-slide-up modal-sheet">
            <div className="text-4xl text-center mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-center mb-2" style={{ color: 'var(--penalty)' }}>
              Reset Stripe Connect for {teamName}?
            </h2>
            <p className="text-sm text-center mb-3" style={{ color: 'var(--text-2)' }}>
              This will <strong>delete the connected Stripe account</strong> and clear
              the team's payout linkage. The team itself stays; only the Stripe
              connection is removed.
            </p>
            <ul className="text-xs space-y-1 mb-3 px-2" style={{ color: 'var(--text-2)' }}>
              <li>• The Stripe connected account will be deleted on Stripe's side{status?.linkedFrom ? ' — unless another team still uses it, in which case it is only unlinked here' : ''}.</li>
              <li>• The team's payout settings on Game Day will reset to "Not connected."</li>
              <li>• A coach (or admin) can immediately start fresh onboarding.</li>
              <li>• <strong>Blocked if the account has any charge history</strong> — refunds and disputes must be preserved.</li>
            </ul>
            <p className="text-xs text-center mb-4" style={{ color: 'var(--penalty)' }}>
              <strong>This cannot be undone.</strong>
            </p>

            <div className="mb-4">
              <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-2)' }}>
                Type <span style={{ color: 'var(--penalty)', fontFamily: 'monospace' }}>{teamName}</span> to confirm:
              </label>
              <input
                type="text"
                autoFocus
                className="input-field"
                value={resetConfirmName}
                onChange={e => setResetConfirmName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Escape') closeResetModal();
                  if (e.key === 'Enter' && teamName && (resetConfirmName || '').trim() === teamName.trim()) {
                    e.preventDefault();
                    performReset();
                  }
                }}
                placeholder="Team name" />
            </div>

            <div className="flex gap-3">
              <button onClick={closeResetModal} disabled={resetting} className="btn-ghost flex-1 justify-center">
                Cancel
              </button>
              <button
                onClick={performReset}
                disabled={resetting || !teamName || (resetConfirmName || '').trim() !== teamName.trim()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all"
                style={{
                  background: 'rgba(255,68,68,0.18)',
                  border: '1px solid rgba(255,68,68,0.4)',
                  color: 'var(--penalty)',
                  opacity: (teamName && (resetConfirmName || '').trim() === teamName.trim()) ? 1 : 0.5,
                }}>
                {resetting
                  ? <div className="spinner" style={{ width: 14, height: 14 }} />
                  : <><Trash2 size={14} /> Reset Connect</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

export default function TeamPage() {
  const { teamId, section } = useParams();
  const navigate   = useNavigate();
  const { isCoach, isManager, loading: roleLoading } = useTeamRole(teamId);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Apr 2026 — outlet context from the layout. Used by the Delete Team
  // flow to clear stale activeTeam after deletion AND refresh the
  // teams list so the dashboard picks a different active team. Other
  // pages on the same layout already pull these; this page didn't
  // need them until the Delete Team feature shipped. Wrapped in a
  // try/null pattern so the page doesn't crash if rendered outside
  // the layout (e.g. in an isolated test environment).
  const outletCtx = useOutletContext();
  const setActiveTeam = outletCtx?.setActiveTeam;
  const loadTeams     = outletCtx?.loadTeams;
  // Apr 2026 — Team Settings broken into URL-addressable sub-pages.
  // The :section URL param controls which card(s) render. Default
  // 'team-info' if missing or unrecognised, so a hand-typed
  // /team/X/settings still lands somewhere visible.
  //
  // Apr 30 2026 — 'spares' was its own sub-page until coaches reported
  // it duplicated waitlist functionality. Consolidated into a single
  // "Spare and Waitlist Management" page at /settings/waitlist. The
  // 'spares' value remains in VALID_SECTIONS so old bookmarks /
  // notification-email links don't 404; we coerce it to 'waitlist' so
  // both URLs render the same content.
  const VALID_SECTIONS = ['team-info', 'games', 'scoreboard', 'reminders', 'spares', 'waitlist', 'pricing', 'payouts', 'balancing', 'drinks', 'chat', 'privacy', 'danger'];
  const rawSection    = VALID_SECTIONS.includes(section) ? section : 'team-info';
  const activeSection = rawSection === 'spares' ? 'waitlist' : rawSection;

  const [team,    setTeam]    = useState(null);
  const [form,    setForm]    = useState({});
  // 2026-07-11 (batch-B G2) — one-time "set up Stripe" nudge shown near the
  // Payments toggle once a coach turns it ON. Persisted per-team in
  // localStorage so it only nags once ever (not once per page visit) —
  // dismissed either by clicking its own "Got it" or by clicking through
  // to the Stripe section, both of which set the flag. Lazy-initialized
  // from localStorage so a coach who already dismissed it on a prior visit
  // doesn't see it flash on before the effect runs.
  const [stripeNudgeDismissed, setStripeNudgeDismissed] = useState(() => {
    try { return localStorage.getItem(`gd_stripe_nudge_dismissed_${teamId}`) === '1'; }
    catch { return false; }
  });
  const dismissStripeNudge = () => {
    setStripeNudgeDismissed(true);
    try { localStorage.setItem(`gd_stripe_nudge_dismissed_${teamId}`, '1'); } catch { /* best effort */ }
  };
  // Snapshot of the form at load time (or after a successful save). Comparing
  // the current form against this snapshot tells us if the coach has unsaved
  // edits — we surface a sticky banner so they don't forget to hit Save.
  const [initialForm, setInitialForm] = useState({});
  // Apr 30 2026 (App_Change_List #43-#45) — cascade list candidates.
  // Lazy-loaded the first time a coach opens the "specific spares"
  // picker on a cascade list. Held at page level so the spare and
  // goalie editors share one fetch.
  const [cascadeCandidates,        setCascadeCandidates]        = useState([]);
  const [cascadeCandidatesLoading, setCascadeCandidatesLoading] = useState(false);
  const [cascadeCandidatesLoaded,  setCascadeCandidatesLoaded]  = useState(false);
  // v2.5.181 — Pair / Keep-Apart Players. Toggle gates the feature;
  // opening the editor lazy-loads the roster (same pattern as the
  // cascade picker). Constraints are held as structured state and
  // serialized into form.balancePairsJson on every change so the
  // existing save path (balance_pairs_json) ships them unchanged.
  const [showPairModal,    setShowPairModal]    = useState(false);
  const [pairRoster,       setPairRoster]       = useState([]);
  const [pairRosterLoading,setPairRosterLoading]= useState(false);
  const [pairRosterLoaded, setPairRosterLoaded] = useState(false);
  const [pairDraft,        setPairDraft]        = useState({ a: '', b: '', strength: 'soft' });
  const [apartDraft,       setApartDraft]       = useState({ a: '', b: '', strength: 'soft' });
  // v2.5.181 — "Clear all chat now" flow. Two-step: first click arms a
  // confirm state, second click within the modal performs it.
  const [showClearChatModal, setShowClearChatModal] = useState(false);
  const [clearingChat,       setClearingChat]       = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState(false);
  const [showLeave, setShowLeave] = useState(false);
  const [leaving,   setLeaving]   = useState(false);
  // Apr 2026 — Delete Team flow. Two-step confirmation:
  //   step 1 ('confirm'): warn + ask user to type the team name
  //   step 2 ('finalWarning'): only when sole coach — extra "are you
  //          sure" with explicit acknowledgment of "no other coach
  //          can take over"
  // showDelete=null means modal closed.
  const [showDelete,         setShowDelete]         = useState(null); // null | 'confirm' | 'finalWarning'
  const [deleteConfirmName,  setDeleteConfirmName]  = useState('');
  const [deleteAck,          setDeleteAck]          = useState(false); // sole-coach acknowledgment checkbox
  const [deleteCoachCount,   setDeleteCoachCount]   = useState(null); // null = not yet loaded
  const [deleting,           setDeleting]           = useState(false);
  const [seeding,     setSeeding]     = useState(false);
  const [seedConfirm, setSeedConfirm] = useState(false);
  // Apr 2026: removed showFeeBreakdown / showSeasonFeeBreakdown state.
  // The expandable Stripe/platform breakdown was replaced by a single
  // blended "Processing fee ≈ $X" summary — coach doesn't need to see
  // the split between Stripe's cut and the platform's cut.

  useEffect(() => { loadTeam(); }, [teamId]);

  const loadTeam = async () => {
    try {
      const { data } = await api.get(`/teams/${teamId}`);
      setTeam(data);
      const formValues = {
        name:                   data.name || '',
        leagueName:             data.league_name || '',
        arenaName:              data.arena_name || '',
        arenaAddress:           data.arena_address || '',
        gameDay:                data.game_day || 'Monday',
        gameTime:               data.game_time?.slice(0,5) || '20:00',
        teamSport:              data.settings?.team_sport || 'hockey',
        spareRequestDaysBefore: data.settings?.spare_request_days_before ?? 3,
        minPlayersToPlay:       data.settings?.min_players_to_play ?? 10,
        // Apr 2026 (Tom_Notes B4) — team-level default game
        // duration in minutes. Per-game override on games.duration_minutes
        // takes precedence; this is the fallback at display time.
        defaultGameDurationMinutes: data.settings?.default_game_duration_minutes || '',
        weeklyReminderDay:      data.settings?.weekly_reminder_day ?? 1,
        weeklyReminderHour:     data.settings?.weekly_reminder_hour ?? 9,
        // May 4 2026 — coach toggle: include spares in reminder #1?
        // Default TRUE preserves legacy behaviour (spares get the
        // weekly awareness email so they can volunteer to fill in).
        // Some coaches prefer FT-only — they can flip this off here.
        weeklyReminderIncludeSpares: data.settings?.weekly_reminder_include_spares !== false,
        // New: days before each game to send the reminder. Replaces the
        // legacy "weekly reminder day" model. Default 7.
        reminderDaysBefore:     data.settings?.reminder_days_before ?? 7,
        // v2.5.104 — explicit on/off for the first pre-game reminder.
        // Previously this reminder was always-on with no kill switch.
        // Defaults TRUE so existing teams keep their current behavior
        // after deploy (don't silently stop emails for coaches who
        // expect them).
        preGameReminderEnabled: data.settings?.pre_game_reminder_enabled !== false,
        // Chunk 6 (Apr 2026): optional second pre-game reminder with its
        // own days-before and on/off toggle. Fires at the same hour as
        // reminder #1. Default off for backwards compatibility.
        reminder2Enabled:       data.settings?.reminder_2_enabled ?? false,
        reminder2DaysBefore:    data.settings?.reminder_2_days_before ?? 3,
        // May 4 2026 — separate hour for reminder #2 (was inheriting
        // the team's weekly_reminder_hour). null = inherit (back-compat).
        reminder2Hour:          data.settings?.reminder_2_hour ?? '',
        // Same-day reminder — fires X hours before game start on game day
        // itself. Independent of the N-days-before reminder.
        gameDayReminderEnabled:     data.settings?.gameday_reminder_enabled     ?? false,
        gameDayReminderHoursBefore: data.settings?.gameday_reminder_hours_before ?? 12,
        // v2.5.139 — no-game-this-week reminder. Default ON (opt-out)
        // since users got it before. The hours-before applies to the
        // team's regular game time (game_day + game_time) — so for a
        // Sunday 8pm team with hours_before=24, the email fires
        // Saturday at 8pm.
        noGameWeekReminderEnabled:     data.settings?.no_game_week_reminder_enabled ?? true,
        noGameWeekReminderHoursBefore: data.settings?.no_game_week_reminder_hours_before ?? 24,
        teamColor:              data.settings?.team_color || '#00d4ff',
        // Jersey colors — shown in the Balanced Teams card + rink diagram
        // so players know which sweater to bring. Defaults match the
        // classic pickup-hockey convention (home=black, away=white).
        homeJerseyColor:        data.settings?.home_jersey_color || 'black',
        awayJerseyColor:        data.settings?.away_jersey_color || 'white',
        // Payment settings
        paymentEnabled:         data.settings?.payment_enabled ?? false,
        paymentType:            data.settings?.payment_type || 'per_game',
        sparePayRequired:       data.settings?.spare_pay_required ?? false,
        // Apr 2026 — goalies generally play free even when other
        // spares pay. Default ON because that matches the common
        // rec-hockey norm: any goalie showing up is a win, and
        // most coaches don't want to charge them. Coaches who
        // explicitly want to charge goalies can flip this off.
        // Read site only honors this when sparePayRequired is on,
        // so it's safe to always include in the form state.
        spareGoaliesFree:       data.settings?.spare_goalies_free !== false,
        // Apr 2026 — pay-mode refinement. When sparePayRequired is on,
        // coach picks ONE of:
        //   'pay_on_promote' — default. Spare joins waitlist free;
        //      pays only if coach promotes them off the waitlist.
        //      Matches the common rec-hockey workflow where pickup
        //      spots aren't guaranteed.
        //   'pay_upfront'    — stricter. Spare must pay immediately
        //      to check in; no waitlist-first detour. Suits teams
        //      that want confirmed payment the moment a spare opts in.
        sparePayMode:           data.settings?.spare_pay_mode || 'pay_on_promote',
        // Platform fee behaviour — default: pass to spare + round up to $0.25
        sparePassPlatformFee:   data.settings?.spare_pass_platform_fee !== false,
        spareRoundUpQuarters:   data.settings?.spare_round_up_quarters !== false,
        spareAutoWaitlist:      data.settings?.spare_auto_waitlist ?? false,
        spareAutoRelease:       data.settings?.spare_auto_release  ?? false,
        // Sticky promotion — when a coach has promoted a spare off the
        // waitlist for a game and the spare later checks out + back in,
        // should they bypass the waitlist (sticky) or be re-waitlisted?
        // Default true = sticky (current behavior, preserves existing
        // teams that haven't explicitly opted out).
        // v2.5.104 — coach feedback: hide the sticky-promotion option
        // from the UI for now. Default flipped from TRUE → FALSE so
        // teams that haven't explicitly set it get the "re-decide each
        // time" behaviour (the option the user wants as the new
        // default). Backend plumbing is intact: a coach can still
        // PATCH /teams settings.spare_promotion_sticky=true via API
        // if they need the old behaviour, and the radio UI lives in
        // git history if we ever want to surface it again.
        sparePromotionSticky:   data.settings?.spare_promotion_sticky === true,
        // 3rd spare option — auto-invite spares.
        // Apr 30 2026 — switched from "hours after auto-jab fires"
        // (which required auto-jab to be enabled and tied the two
        // together) to "N days before game at hour H if check-in
        // is below threshold". The new model is independent of
        // auto-jab and predictable. Old settings remain in JSON
        // for back-compat reads but the UI doesn't expose them.
        spareAutoInviteEnabled:    data.settings?.spare_auto_invite_enabled ?? false,
        // May 4 2026 — option to exclude goalie spares from the
        // single-blast spare auto-invite. Default true (include) for
        // back-compat — coaches with goalie auto-invite separately
        // configured can flip this off to avoid duplicate emails.
        spareAutoInviteIncludeGoalies: data.settings?.spare_auto_invite_include_goalies !== false,
        // May 4 2026 — gate spare RSVPs to after the spare-invite
        // email has fired. Default true = current permissive behavior.
        waitlistAllowSparesBeforeInvite: data.settings?.waitlist_allow_spares_before_invite !== false,
        // NEW (Apr 30 2026) — primary trigger. Default 2 days before.
        spareAutoInviteDaysBefore: data.settings?.spare_auto_invite_days_before ?? 2,
        // NEW (Apr 30 2026) — hour of day to send (0–23 server tz),
        // default 9am. Same convention as auto_nudge_hour.
        spareAutoInviteHour:       data.settings?.spare_auto_invite_hour       ?? 9,
        // NEW (Apr 30 2026) — minimum check-in threshold. Auto-invite
        // ONLY fires if currently-in skater count < this number. Was
        // an optional opt-in (skipIfEnough) before; now always
        // required so the coach can't accidentally spam every game's
        // spare list. Threshold is on actively-checked-in skaters
        // (goalies don't count). Default 16 = typical hockey roster.
        spareAutoInviteThreshold:  data.settings?.spare_auto_invite_threshold
                                    ?? data.settings?.spare_auto_invite_skip_threshold
                                    ?? 16,
        // Legacy (kept for back-compat reads only — UI controls removed):
        spareAutoInviteHours:          data.settings?.spare_auto_invite_hours ?? 24,
        spareAutoInviteSkipIfEnough:   data.settings?.spare_auto_invite_skip_if_enough ?? false,
        spareAutoInviteSkipThreshold:  data.settings?.spare_auto_invite_skip_threshold ?? 16,
        // May 4 2026 — goalie auto-invite form load (was missing
        // entirely from formValues — the toggle started undefined
        // until the coach manually toggled it once). Now reads the
        // saved value with sensible defaults matching the backend.
        goalieAutoInviteEnabled:     data.settings?.goalie_auto_invite_enabled  ?? false,
        goalieAutoInviteDaysBefore:  data.settings?.goalie_auto_invite_days_before ?? 3,
        goalieAutoInviteHour:        data.settings?.goalie_auto_invite_hour     ?? 9,
        goalieAutoInviteThreshold:   data.settings?.goalie_auto_invite_threshold ?? 2,
        // May 4 2026 — sub-toggle: also fire goalie-spare invite when
        // a FT goalie checks out. Only meaningful when parent
        // goalieAutoInviteEnabled is true (UI gates it).
        goalieAutoInviteOnCheckoutEnabled:    data.settings?.goalie_auto_invite_on_checkout_enabled === true,
        goalieAutoInviteOnCheckoutDelayHours: data.settings?.goalie_auto_invite_on_checkout_delay_hours ?? 0,
        // Apr 2026 — Waitlist auto-management. Three independent toggles.
        // All default OFF so existing teams are unaffected.
        waitlistAutoReleaseEnabled:        data.settings?.waitlist_auto_release_enabled        ?? false,
        waitlistAutoReleaseHoursBefore:    data.settings?.waitlist_auto_release_hours_before    ?? 1,
        waitlistAutoPromoteBelowEnabled:   data.settings?.waitlist_auto_promote_below_enabled   ?? false,
        waitlistAutoPromoteBelowThreshold: data.settings?.waitlist_auto_promote_below_threshold ?? (data.settings?.min_players_to_play || 10),
        // v2.5.104 — coach feedback: "Auto-promote when count drops
        // below add in an additional timeframe input of N hours
        // before the game starts — such as 48 hour, 24 hours — allow
        // the user to input the # of hours before the game starts,
        // so that this toggle has both a min threshold of players
        // and a timeframe of requirements."
        // null/0 = no time gate (legacy behaviour: promote at any
        // time the count drops). Positive integer = only promote
        // when we're within X hours of game start.
        waitlistAutoPromoteBelowHoursBefore: data.settings?.waitlist_auto_promote_below_hours_before ?? null,
        lowAttendanceAlertEnabled:         data.settings?.low_attendance_alert_enabled         ?? false,
        lowAttendanceAlertThreshold:       data.settings?.low_attendance_alert_threshold       ?? (data.settings?.min_players_to_play || 10),
        lowAttendanceAlertHoursBefore:     data.settings?.low_attendance_alert_hours_before     ?? 24,
        // May 4 2026 (v2.5.69) — coach-checkout notifications. Each
        // toggle independently enables an email + push notification to
        // every coach/manager when a player of the matching tier checks
        // out (status flips from anything to 'out'). All four default
        // OFF so existing teams aren't suddenly spammed; coach opts in
        // per tier. Notifications fire ONCE per (game, user, tier-flip)
        // — a player who flips out → in → out gets two notifications.
        notifyCoachOnFtPlayerCheckout:     data.settings?.notify_coach_on_ft_player_checkout     === true,
        notifyCoachOnFtGoalieCheckout:     data.settings?.notify_coach_on_ft_goalie_checkout     === true,
        notifyCoachOnSparePlayerCheckout:  data.settings?.notify_coach_on_spare_player_checkout  === true,
        notifyCoachOnSpareGoalieCheckout:  data.settings?.notify_coach_on_spare_goalie_checkout  === true,
        // v2.5.142 — proximity window for the 4 checkout alerts. Only
        // fires the alert if the game is within this many hours of
        // now. Default 24h (day-of).
        notifyCoachCheckoutWindowHours:    data.settings?.notify_coach_checkout_window_hours ?? 24,
        // v2.5.165 — per-tier checkout windows. Each toggle gets its own
        // "within how many hours" timing. Fall back to the shared value
        // (then 24) so existing teams' single setting carries into each
        // tier until the coach customises them.
        notifyCoachCheckoutWindowFtPlayer:    data.settings?.notify_coach_checkout_window_hours_ft_player    ?? data.settings?.notify_coach_checkout_window_hours ?? 24,
        notifyCoachCheckoutWindowFtGoalie:    data.settings?.notify_coach_checkout_window_hours_ft_goalie    ?? data.settings?.notify_coach_checkout_window_hours ?? 24,
        notifyCoachCheckoutWindowSparePlayer: data.settings?.notify_coach_checkout_window_hours_spare_player ?? data.settings?.notify_coach_checkout_window_hours ?? 24,
        notifyCoachCheckoutWindowSpareGoalie: data.settings?.notify_coach_checkout_window_hours_spare_goalie ?? data.settings?.notify_coach_checkout_window_hours ?? 24,
        // Chunk 9 (Apr 2026) — overflow lockout window
        overflowLockoutHoursBefore:   data.settings?.overflow_lockout_hours_before ?? '',
        // Spare payment mode
        sparePaymentMode:       data.settings?.spare_payment_mode  || 'none',
        spareFee:               data.settings?.spare_fee_cents ? (data.settings.spare_fee_cents / 100).toFixed(2) : '',
        // Apr 2026 (Tom_Notes F10) — separate goalie spare fee.
        // Empty = goalies use the skater fee (or are free per
        // spare_goalies_free). When set, goalies pay this amount
        // and spare_goalies_free auto-flips off.
        spareGoalieFee:         data.settings?.spare_goalie_fee_cents ? (data.settings.spare_goalie_fee_cents / 100).toFixed(2) : '',
        // Apr 2026 — when payments are OFF but a spare fee is tracked,
        // the coach can opt in to show the amount in spare confirmation
        // + waitlist-promoted emails so the spare knows the price
        // (typically e-transfer / cash). Off by default — existing
        // teams won't suddenly start including the fee in emails.
        spareFeeShowInNotifications: data.settings?.spare_fee_show_in_notifications ?? false,
        // Apr 2026: memberFee form field removed. The "Per-Game Fee
        // (Members)" input was retired in the simplified Payment
        // Collection UI. member_fee_cents is still force-nulled on save
        // (below) so existing teams' stored values get cleared.
        // Season fees (full-time players paying full-season via Game Day)
        seasonFeeRequired:      data.settings?.season_fee_required ?? false,
        seasonFee:              data.settings?.season_fee_cents ? (data.settings.season_fee_cents / 100).toFixed(2) : '',
        seasonFeePassToPlayer:  data.settings?.season_fee_pass_to_player !== false,
        seasonFeeRoundUpQuarters: data.settings?.season_fee_round_up_quarters ?? false,
        paymentCurrency:        data.settings?.payment_currency || 'cad',
        paymentDescription:     data.settings?.payment_description || '',
        // Drinks
        drinksEnabled:          data.settings?.drinks_enabled ?? false,
        drinksMode:             data.settings?.drinks_mode || 'volunteer',
        // Auto-nudge
        autoNudgeEnabled:       data.settings?.auto_nudge_enabled  ?? false,
        autoNudgeDaysBefore:    data.settings?.auto_nudge_days_before ?? 2,
        autoNudgeHour:          data.settings?.auto_nudge_hour ?? 9,
        autoNudgeGroup:         data.settings?.auto_nudge_group || 'all',
        // Multi-select recipients (Apr 2026). When the team has saved an
        // array, use it. Otherwise derive from the legacy single-select:
        //   'all'         → all four groups
        //   'ft_players'  → just FT skaters
        //   'ft_goalies'  → just FT goalies
        //   'spares'      → spares (interpreted as both spare skaters AND
        //                   spare goalies, matching the old single-string
        //                   semantics where 'spares' meant "any non-primary"
        //                   regardless of position)
        // The derived array is what the UI renders; saving converts back
        // and updates BOTH fields so old + new servers both behave.
        autoNudgeGroups: (() => {
          const arr = data.settings?.auto_nudge_groups;
          if (Array.isArray(arr) && arr.length > 0) return arr;
          const legacy = data.settings?.auto_nudge_group || 'all';
          if (legacy === 'all')        return ['ft_players','ft_goalies','spares','spare_goalies'];
          if (legacy === 'spares')     return ['spares','spare_goalies'];
          return [legacy]; // 'ft_players' | 'ft_goalies'
        })(),
        // ── Chunk 1 (Apr 2026) ───────────────────────────────────────────
        // Lookahead guardrail — prevents RSVPs to games more than N games
        // out. Off by default; coaches/managers always bypass server-side.
        checkinLookaheadEnabled:        data.settings?.checkin_lookahead_enabled ?? false,
        checkinLookaheadGames:          data.settings?.checkin_lookahead_games ?? 2,
        checkinLookaheadExemptGoalies:  data.settings?.checkin_lookahead_exempt_goalies ?? false, // 2026-09-17
        noShowRibbing:                  data.settings?.no_show_ribbing !== false, // 2026-09-22 default ON
        statRecorders:                  Array.isArray(data.settings?.stat_recorders) ? data.settings.stat_recorders : [], // 2026-09-23
        // 2026-09-19 (Phase 7A) — which stats this team records. Absent means
        // the team never chose, which reads as the defaults, not "none".
        trackedStats: Array.isArray(data.settings?.tracked_stats) && data.settings.tracked_stats.length
          ? data.settings.tracked_stats
          : [...DEFAULT_TRACKED],
        // Auto-balance + email teams a fixed window before each game.
        // The cron job is in a later chunk — this UI just persists the
        // toggle so coaches can flip it now and the job picks it up later.
        autoBalanceEnabled:             data.settings?.auto_balance_enabled ?? false,
        autoBalanceHoursBefore:         data.settings?.auto_balance_hours_before ?? 24,
        // v2.5.105 — split the legacy "auto-balance + email" toggle
        // into TWO independent toggles. `autoEmailTeamsEnabled` is
        // the new unified email gate that works for BOTH paths
        // (auto-balance cron AND manual coach assignment). Coach
        // feedback: "let's split the option to Auto-Balance Teams
        // and email. Have email as its own toggle, so that a coach
        // can turn it on or off regardless if they are manually or
        // automatically creating teams."
        //
        // Default = true to preserve legacy behaviour for existing
        // teams (the cron always emailed before this split). The
        // backend defaults to true via COALESCE for the same
        // reason. Flip OFF to suppress all team-assignment emails.
        autoEmailTeamsEnabled:          data.settings?.auto_email_teams_enabled !== false,
        // 2026-07-06 — re-balance automatically when the roster changes
        // (drop-out / late addition) after teams exist. Opt-in, default OFF.
        rebalanceOnChangeEnabled:       data.settings?.rebalance_on_change_enabled === true,
        // Apr 2026 — variety bias for team balancing. 0 disables,
        // 0.4 is the default (moderate nudge), 1.0 maximizes
        // anti-repetition. Default to 0.4 for new and existing
        // teams via the ?? operator below — coaches who want repeatable
        // teams set this to 0.
        balanceVarietyWeight:           Number(data.settings?.balance_variety_weight ?? 0.4),
        balanceVarietyLookback:         parseInt(data.settings?.balance_variety_lookback) || 4,
        // Apr 2026 (Tom_Notes F12) — Create Teams new fields:
        // useAppForBalancing flips manual-vs-AI mode. Default true
        // for backward compat (existing behaviour was always AI).
        useAppForBalancing:             data.settings?.use_app_for_balancing !== false,
        // May 4 2026 (v2.5.69) — sub-mode under "Use the app to create
        // teams". Mutually exclusive with itself: 'auto' = use the
        // balancer, 'manual' = bulk-pick into Home/Away. Only meaningful
        // when useAppForBalancing is true (the parent toggle gates the
        // whole feature). Defaults 'auto' for back-compat — existing
        // teams that had useAppForBalancing=true keep their current
        // balancer behaviour.
        teamCreationMode:               data.settings?.team_creation_mode === 'manual' ? 'manual' : 'auto',
        // sendTeamsHoursBefore controls when the team-assignment
        // email goes out, separate from when balancing happens.
        // Default 6 — common ask was "balance day-before but email
        // closer to game time so it doesn't get buried."
        sendTeamsHoursBefore:           parseInt(data.settings?.send_teams_hours_before) || 6,
        // Apr 30 2026 — when in manual mode (useAppForBalancing=false
        // or autoBalanceEnabled=false), the coach can opt in to having
        // the system email out their manually-assigned teams N hours
        // before the game. Default OFF — existing teams just email on
        // demand from the Game page.
        sendTeamsManualEnabled:         data.settings?.send_teams_manual_enabled === true,
        // Pair/keep-apart constraint JSON — advanced. Stored raw
        // string; backend parses on save. Default '' = no constraints.
        balancePairsJson:               data.settings?.balance_pairs_json || '',
        // May 2 2026 — coach toggle: include goalies in the balanced-
        // teams algorithm or leave them unassigned for the coach to
        // slot in manually. Defaults TRUE for backward compatibility.
        // The Game page's per-game "Goalies IN/OUT" toggle still
        // exists and overrides per call; this team-level setting just
        // controls the DEFAULT value of that toggle on each game.
        balanceIncludeGoalies:          data.settings?.balance_include_goalies !== false,
        // 2026-07-25 (per Gilles) — goalie side-swap convention for
        // multi-mini nights: mini 1 as assigned, both goalies switch
        // ends each time a new mini starts. Read by the goalie stats
        // section (W/L + GF/GA attribution only). Default OFF.
        goalieSwapEachMini:             data.settings?.goalie_swap_each_mini === true,
        // Apr 2026 (Tom_Notes F9) — cascading rank-based invite
        // settings. Off by default; existing teams keep the legacy
        // "blast all spares at once" behaviour.
        spareInviteCascadeEnabled:      data.settings?.spare_invite_cascade_enabled === true,
        spareInviteCascadeHours:        parseInt(data.settings?.spare_invite_cascade_hours) || 4,
        goalieInviteCascadeEnabled:     data.settings?.goalie_invite_cascade_enabled === true,
        goalieInviteCascadeHours:       parseInt(data.settings?.goalie_invite_cascade_hours) || 8,
        // Apr 30 2026 (App_Change_List #43-#45) — list-based cascade.
        // Up to 3 named lists per team for spares + 3 for goalies.
        // Each list either targets specific user IDs or selected
        // rating tiers, and has a delay (hours) after the previous
        // list fires. When non-empty, supersedes the legacy rating-
        // tier cascade above. Default empty so existing teams aren't
        // forced into the new mode until a coach configures lists.
        spareInviteLists:               Array.isArray(data.settings?.spare_invite_lists)
                                          ? data.settings.spare_invite_lists
                                          : [],
        goalieInviteLists:              Array.isArray(data.settings?.goalie_invite_lists)
                                          ? data.settings.goalie_invite_lists
                                          : [],
        // Apr 2026 (Tom_Notes F5) — disable overflow once spare
        // invites have been sent. Default off so existing teams
        // keep behaviour. Only meaningful when defaultAllowOverflow
        // is also on.
        disableOverflowAfterSpareInvites: data.settings?.disable_overflow_after_spare_invites === true,
        // Apr 2026 (Tom_Notes F13) — auto-mark FT players "in" when
        // the first reminder fires. Off by default; existing teams
        // keep the "silence = no response" semantics.
        autoMarkFtIn:                    data.settings?.auto_mark_ft_in_on_reminder === true,
        // Apr 2026 (Tom_Notes B4 + F4 audit) — game duration was
        // previously tracked under TWO keys (`game_duration_minutes`
        // legacy, `default_game_duration_minutes` from B4). Consolidated
        // to the latter since that's what the GamePage display path
        // reads as the team-level fallback. Legacy state removed.
        spareBypassWaitlistAfterRequest: data.settings?.spare_bypass_waitlist_after_request ?? false,
        // ── Miscellaneous (Apr 2026) ──
        // Show each player's attendance history (games in / out) on the
        // Game page availability rows. Default off so existing teams
        // keep the cleaner row layout; coaches who want the
        // reliability-at-a-glance signal toggle it on under
        // Team Settings → Miscellaneous.
        showAttendanceCounts:   data.settings?.show_attendance_counts === true,
        // Default max players per game (team-level default)
        defaultMaxPlayers:      data.defaultMaxPlayers || '',
        defaultAllowOverflow:   data.defaultAllowOverflow || false,
        // Country — scopes arena search results. Blank = worldwide.
        country:                data.settings?.country || '',
        // Privacy (Bug 9): coach-controlled visibility toggles for non-coach
        // team members. Defaults preserve historical behavior:
        //   - showPlayerNamesToPlayers: true → names visible (existing behavior)
        //   - showRatingsToPlayers: false → ratings coach-private (existing behavior)
        showPlayerNamesToPlayers: data.settings?.show_player_names_to_players !== false,
        showRatingsToPlayers:     data.settings?.show_ratings_to_players === true,
        // Chat: email every chat message to all team members. Default false.
        chatEmailAllMessages:     data.settings?.chat_email_all_messages === true,
        // Tri-state chat notification scope. If the server setting is absent,
        // Apr 2026 — default flipped from 'off' to 'everyone'. Coach
        // feedback: "regular chat should reach everyone by default; if
        // a player wants to mute they turn off email notifications in
        // their own profile, not the team admin." Existing teams that
        // have an explicit setting keep it (mode = stored value);
        // legacy chat_email_all_messages=true still maps to 'everyone';
        // legacy chat_email_all_messages=false now stays 'off' (the
        // coach made an explicit choice). Only the COMPLETELY UNSET
        // case flips to 'everyone'.
        chatNotifyMode:           data.settings?.chat_notify_mode
                                    || (data.settings?.chat_email_all_messages === true ? 'everyone'
                                       : data.settings?.chat_email_all_messages === false ? 'off'
                                       : 'everyone'),
        // v2.5.181 — per-team chat retention (days). Default 180 when
        // unset. 0 = never auto-delete.
        chatRetentionDays:        data.settings?.chat_retention_days ?? 180,
        // Apr 30 2026 — scoreboard edit-permission flag. Default
        // false (everyone on the roster can edit). Coaches flip
        // ON to restrict to coaches/managers only. Loaded with
        // explicit `=== true` so missing-key (existing teams)
        // correctly defaults to OFF.
        scoreboardCoachOnly:      data.settings?.scoreboard_coach_only === true,
        // Apr 30 2026 (followup) — master scoreboard on/off.
        // Default true. Existing teams (missing key) stay enabled
        // — they've been using the feature already, so we don't
        // want their scoreboards to vanish on this deploy. Coaches
        // can flip OFF at /team/X/settings/scoreboard which hides
        // the scoreboard card from game pages and the Scoreboards
        // menu item from the team nav.
        scoreboardEnabled:        data.settings?.scoreboard_enabled !== false,
      };
      setForm(formValues);
      // Snapshot for unsaved-changes detection. JSON.stringify comparison is
      // fine here — form values are primitives only (strings, numbers, bools).
      setInitialForm(formValues);
    } catch { toast.error('Failed to load team'); }
    setLoading(false);
  };


  const notifyRoster = async () => {
    setNotifying(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/notify-roster`);
      toast.success(`✅ Sent to ${data.emailsSent} email${data.emailsSent !== 1 ? 's' : ''} + ${data.inAppSent} in-app notification${data.inAppSent !== 1 ? 's' : ''}`, { duration: 5000 });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send notifications');
    }
    setNotifying(false);
  };

  // ── Roster export helpers (coach-only) ────────────────────────────────────
  // Shared between the Players page and Team Settings so the coach can grab
  // contact info from either surface. Loads fresh member data on demand so the
  // TeamPage doesn't pay the fetch cost until the coach actually clicks.
  const [exportingRoster, setExportingRoster] = useState(false);

  const splitName = (fullName) => {
    const trimmed = (fullName || '').trim();
    if (!trimmed) return { first: '', last: '' };
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) return { first: parts[0], last: '' };
    return { first: parts[0], last: parts.slice(1).join(' ') };
  };

  const fetchAllMembers = async () => {
    // GET /teams/:teamId/members returns a flat array of { ...member, isPending?: true }
    // — pending invites are merged in with isPending: true. Both groups potentially
    // have .email and .phone so we can export all contacts in one list.
    const { data } = await api.get(`/teams/${teamId}/members`);
    return Array.isArray(data) ? data : [];
  };

  // Apr 30 2026 (App_Change_List #43-#45) — lazy-load member roster
  // for the cascade list editor. Called when a coach opens the
  // "specific spares" picker on any list. We hit the same /members
  // endpoint as the export flow and cache the result for the rest
  // of the page session — coaches typically configure all lists in
  // one sitting, so a single fetch is sufficient.
  // 2026-09-23 — members eligible as designated live-stat recorders
  // (anyone with an account; the coach is always allowed and not listed).
  const [recorderCandidates, setRecorderCandidates] = useState(null);
  const loadRecorderCandidates = async () => {
    if (recorderCandidates) return;
    try {
      const all = await fetchAllMembers();
      setRecorderCandidates(all
        .filter(m => m.userId && !m.isPending && !m.isPlaceholder && !m.isDeleted && m.member_role !== 'coach')
        .map(m => ({ userId: m.userId, name: m.name, role: m.member_role }))
        .sort((a, b) => String(a.name).localeCompare(String(b.name))));
    } catch { setRecorderCandidates([]); }
  };

  const loadCascadeCandidates = async () => {
    if (cascadeCandidatesLoaded || cascadeCandidatesLoading) return;
    setCascadeCandidatesLoading(true);
    try {
      const all = await fetchAllMembers();
      // Map to the minimal shape the editor renders: userId, name, rating.
      // Filter out: pending invites (no userId), placeholders (isPlaceholder=true),
      // tombstoned accounts (isDeleted=true), and full-timers (only spares
      // are eligible to be cascade-invited; full-timers are auto-invited
      // along with the team reminder).
      const candidates = all
        .filter(m =>
          m.userId
          && !m.isPending
          && !m.isPlaceholder
          && !m.isDeleted
          && m.isPrimary === false
        )
        .map(m => ({
          userId:   m.userId,
          name:     m.name,
          rating:   m.rating ?? null,
          position: m.position ?? null,
        }));
      setCascadeCandidates(candidates);
      setCascadeCandidatesLoaded(true);
    } catch (err) {
      toast.error('Could not load roster for cascade lists');
    } finally {
      setCascadeCandidatesLoading(false);
    }
  };

  // ── v2.5.181 Pair / Keep-Apart helpers ──────────────────────────
  // Parse the stored JSON string into structured constraints. Tolerates
  // the legacy [[a,b],…] shape (→ hard) so anything a coach hand-authored
  // before the UI still shows up correctly.
  const parsePairConstraints = (jsonStr) => {
    const empty = { pairs: [], keepApart: [] };
    if (!jsonStr) return empty;
    let obj;
    try { obj = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr; }
    catch { return empty; }
    if (!obj || typeof obj !== 'object') return empty;
    const norm = (arr) => Array.isArray(arr) ? arr.map(it =>
      Array.isArray(it) ? { a: String(it[0]), b: String(it[1]), strength: 'hard' }
        : (it && it.a && it.b ? { a: String(it.a), b: String(it.b), strength: it.strength === 'soft' ? 'soft' : 'hard' } : null)
    ).filter(Boolean) : [];
    return { pairs: norm(obj.pairs), keepApart: norm(obj.keepApart) };
  };

  // Current structured view of the constraints (derived from form state).
  const pairConstraints = parsePairConstraints(form.balancePairsJson);

  // Write structured constraints back into form.balancePairsJson (the
  // existing save path serializes this verbatim). Empty → '' so the
  // balancer treats it as "no constraints".
  const writePairConstraints = (next) => {
    const hasAny = (next.pairs?.length || 0) + (next.keepApart?.length || 0) > 0;
    setForm(f => ({ ...f, balancePairsJson: hasAny ? JSON.stringify(next) : '' }));
  };

  const loadPairRoster = async () => {
    if (pairRosterLoaded || pairRosterLoading) return;
    setPairRosterLoading(true);
    try {
      const all = await fetchAllMembers();
      // Full roster minus pending invites / placeholders / deleted —
      // pairing applies to real, balanceable players (full-timers AND
      // spares, since either can be in a balanced game).
      const list = all
        .filter(m => m.userId && !m.isPending && !m.isPlaceholder && !m.isDeleted)
        .map(m => ({ userId: m.userId, name: m.name, rating: m.rating ?? null }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setPairRoster(list);
      setPairRosterLoaded(true);
    } catch {
      toast.error('Could not load roster');
    } finally {
      setPairRosterLoading(false);
    }
  };

  const openPairModal = () => { setShowPairModal(true); loadPairRoster(); };

  const nameForId = (id) => pairRoster.find(p => p.userId === id)?.name || 'Unknown player';

  // Add a constraint of the given kind ('pairs' | 'keepApart'). Guards
  // against self-pairs and duplicates (either ordering).
  const addConstraint = (kind, draft) => {
    const { a, b, strength } = draft;
    if (!a || !b) { toast.error('Pick two players'); return; }
    if (a === b)  { toast.error('Pick two different players'); return; }
    const existing = pairConstraints[kind] || [];
    const dup = existing.some(c =>
      (c.a === a && c.b === b) || (c.a === b && c.b === a));
    if (dup) { toast.error('That pairing already exists'); return; }
    const next = {
      ...pairConstraints,
      [kind]: [...existing, { a, b, strength: strength === 'hard' ? 'hard' : 'soft' }],
    };
    writePairConstraints(next);
    // Reset the relevant draft.
    if (kind === 'pairs') setPairDraft({ a: '', b: '', strength: 'soft' });
    else setApartDraft({ a: '', b: '', strength: 'soft' });
  };

  const removeConstraint = (kind, idx) => {
    const next = { ...pairConstraints, [kind]: (pairConstraints[kind] || []).filter((_, i) => i !== idx) };
    writePairConstraints(next);
  };

  // Flip a single constraint's strength soft↔hard in place.
  const toggleConstraintStrength = (kind, idx) => {
    const next = {
      ...pairConstraints,
      [kind]: (pairConstraints[kind] || []).map((c, i) =>
        i === idx ? { ...c, strength: c.strength === 'hard' ? 'soft' : 'hard' } : c),
    };
    writePairConstraints(next);
  };

  // v2.5.181 — wipe all chat messages + files for this team now.
  const clearAllChatNow = async () => {
    setClearingChat(true);
    try {
      const { data } = await api.delete(`/teams/${teamId}/chat/all`);
      toast.success(
        `Chat cleared — ${data.messagesDeleted ?? 0} message(s) and ${data.filesDeleted ?? 0} file(s) removed.`
      );
      setShowClearChatModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not clear chat');
    } finally {
      setClearingChat(false);
    }
  };

  const copyAllEmailsFromSettings = async () => {
    setExportingRoster(true);
    try {
      const all = await fetchAllMembers();
      const seen = new Set();
      const emails = [];
      for (const m of all) {
        const e = (m.email || '').trim();
        if (!e) continue;
        const k = e.toLowerCase();
        if (seen.has(k)) continue;
        seen.add(k);
        emails.push(e);
      }
      if (emails.length === 0) {
        toast.error('No email addresses on the team yet');
      } else {
        const joined = emails.join(', ');
        try {
          await navigator.clipboard.writeText(joined);
          toast.success(`📋 Copied ${emails.length} email${emails.length !== 1 ? 's' : ''} — paste into To/BCC`);
        } catch {
          window.prompt('Copy this list:', joined);
        }
      }
    } catch {
      toast.error('Failed to load player list');
    }
    setExportingRoster(false);
  };

  const exportRosterCSV = async () => {
    setExportingRoster(true);
    try {
      const all = await fetchAllMembers();
      const rows = [
        ['First Name', 'Last Name', 'Email', 'Phone', 'Role', 'Status'],
        ...all.map(m => {
          const { first, last } = splitName(m.name);
          const status = m.isPending
            ? (m.inviteSent ? 'Invite Sent' : 'Invite Pending')
            : (m.lastLogin ? 'Active' : 'Not Logged In');
          return [first, last, m.email || '', m.phone || '', m.role || 'player', status];
        }),
      ];
      const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      // UTF-8 BOM helps Excel detect encoding for non-ASCII names
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(form.name || 'team').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-players-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${all.length} player${all.length !== 1 ? 's' : ''} exported`);
    } catch {
      toast.error('Failed to export player list');
    }
    setExportingRoster(false);
  };

  const saveTeam = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: saveResp } = await api.put(`/teams/${teamId}`, {
        name:         form.name,
        leagueName:   form.leagueName,
        arenaName:    form.arenaName,
        arenaAddress: form.arenaAddress,
        gameDay:      form.gameDay,
        gameTime:     form.gameTime,
        settings: {
          spare_request_days_before: parseInt(form.spareRequestDaysBefore),
          // Apr 30 2026 (App_Change_List #31) — clamp 0..200 (was
          // implicit min 4 from the old input). NaN-safe so an empty
          // field falls back to 10 instead of NaN, which would round-
          // trip through the API as null.
          min_players_to_play:       (() => {
            const v = parseInt(form.minPlayersToPlay, 10);
            return Number.isFinite(v) && v >= 0 && v <= 200 ? v : 10;
          })(),
          // Apr 2026 (Tom_Notes B4) — team-level default duration.
          // Empty/0 → null so per-game override OR display-time
          // "not set" fallback applies. Clamped 1-480 minutes.
          default_game_duration_minutes: (() => {
            const v = parseInt(form.defaultGameDurationMinutes);
            return Number.isFinite(v) && v >= 1 && v <= 480 ? v : null;
          })(),
          weekly_reminder_day:       parseInt(form.weeklyReminderDay),
          weekly_reminder_hour:      parseInt(form.weeklyReminderHour),
          // May 4 2026 — coach toggle for reminder #1 audience.
          weekly_reminder_include_spares: form.weeklyReminderIncludeSpares !== false,
          reminder_days_before:      Math.max(1, Math.min(14, parseInt(form.reminderDaysBefore) || 7)),
          // v2.5.104 — explicit kill switch for reminder #1. The
          // legacy behaviour was always-on; coaches who want to
          // stop the weekly nudge entirely can now toggle this off.
          pre_game_reminder_enabled: form.preGameReminderEnabled !== false,
          // Chunk 6 reminder #2 — optional second pre-game reminder.
          // Clamped 1-30 days (wider than #1 because some coaches want
          // a month-ahead heads-up). The scheduler dedupes via
          // scheduled_job_log so a tick double-fire can't cause a
          // duplicate email.
          reminder_2_enabled:        !!form.reminder2Enabled,
          reminder_2_days_before:    Math.max(1, Math.min(30, parseInt(form.reminder2DaysBefore) || 3)),
          // May 4 2026 — empty string means "inherit weekly_reminder_hour"
          // (back-compat for teams that haven't set it). Otherwise clamp 0-23.
          reminder_2_hour:           (form.reminder2Hour === '' || form.reminder2Hour == null)
            ? null
            : Math.max(0, Math.min(23, parseInt(form.reminder2Hour) || 0)),
          // Clamp game-day reminder to 1-23 hours before — 0 would mean
          // "right at game time" (useless) and 24+ belongs to the
          // N-days-before reminder instead.
          gameday_reminder_enabled:      !!form.gameDayReminderEnabled,
          gameday_reminder_hours_before: Math.max(1, Math.min(23, parseInt(form.gameDayReminderHoursBefore) || 12)),
          // v2.5.139 — no-game-this-week reminder. hours_before is in
          // hours (1 = 1h before, 168 = 1 week before). Clamp to
          // 1..168 — anything outside that range is operator error.
          no_game_week_reminder_enabled:      !!form.noGameWeekReminderEnabled,
          no_game_week_reminder_hours_before: Math.max(1, Math.min(168, parseInt(form.noGameWeekReminderHoursBefore) || 24)),
          team_color:                form.teamColor,
          team_sport:                form.teamSport || 'hockey',
          // Jersey colors (free-text so coaches can use any color name —
          // "black", "white", "red", "pink", "neon yellow", etc.)
          home_jersey_color:         (form.homeJerseyColor || 'black').trim().toLowerCase(),
          away_jersey_color:         (form.awayJerseyColor || 'white').trim().toLowerCase(),
          // Payment
          payment_enabled:     form.paymentEnabled,
          payment_type:        form.paymentType,
          spare_pay_required:  form.sparePayRequired,
          // Apr 2026 — goalies-free toggle. Always saved so a coach
          // who pre-configures it before turning pay-required on
          // gets the expected behavior the moment they flip pay-
          // required on. Backend only honors this setting when
          // sparePayRequired is also on, so saving it standalone
          // is harmless.
          spare_goalies_free:  form.spareGoaliesFree !== false,
          // Apr 2026 — pay mode (only meaningful when sparePayRequired).
          // Clamped to known values; anything else falls back to default.
          spare_pay_mode:      ['pay_on_promote', 'pay_upfront'].includes(form.sparePayMode)
                                 ? form.sparePayMode
                                 : 'pay_on_promote',
          // Platform fee behaviour (default true on both)
          spare_pass_platform_fee: form.sparePassPlatformFee !== false,
          spare_round_up_quarters: form.spareRoundUpQuarters !== false,
          spare_auto_waitlist: form.spareAutoWaitlist,
          spare_auto_release:  form.spareAutoRelease,
          // Sticky promotion (see hydration block for semantics)
          spare_promotion_sticky: form.sparePromotionSticky !== false,
          // Auto-invite spares. Apr 30 2026 — primary triggers are
          // now days-before-game + hour + threshold; the legacy
          // hours-after-jab + skip-if-enough toggles are still
          // written so the JSON shape doesn't lose data, but the
          // scheduler only reads the new keys.
          //
          // NaN-safe parse via `Number.isFinite(+x) ? +x : default`
          // — `parseInt(...) ?? d` doesn't catch NaN (?? checks only
          // null/undefined) and `parseInt(...) || d` would coerce a
          // legitimate 0 to default. Days-before allows 0 ("day of
          // the game") and hour allows 0 (midnight).
          spare_auto_invite_enabled:        form.spareAutoInviteEnabled,
          spare_auto_invite_include_goalies: form.spareAutoInviteIncludeGoalies !== false,
          waitlist_allow_spares_before_invite: form.waitlistAllowSparesBeforeInvite !== false,
          spare_auto_invite_days_before:    Math.max(0, Math.min(14,
            Number.isFinite(+form.spareAutoInviteDaysBefore) ? +form.spareAutoInviteDaysBefore : 2)),
          spare_auto_invite_hour:           Math.max(0, Math.min(23,
            Number.isFinite(+form.spareAutoInviteHour) ? +form.spareAutoInviteHour : 9)),
          spare_auto_invite_threshold:      Math.max(1, Math.min(50, parseInt(form.spareAutoInviteThreshold) || 16)),
          // Legacy keys preserved on save so an admin who downgrades
          // the backend doesn't lose the values.
          spare_auto_invite_hours:          parseInt(form.spareAutoInviteHours) || 24,
          spare_auto_invite_skip_if_enough: !!form.spareAutoInviteSkipIfEnough,
          spare_auto_invite_skip_threshold: Math.max(1, Math.min(50, parseInt(form.spareAutoInviteSkipThreshold) || 16)),
          // ── Goalie auto-invite (Apr 30 2026) ──
          // Mirrors spare auto-invite. Threshold defaults to 2
          // (typical full goalie roster); the scheduler invites
          // goalie spares when fewer goalies are checked in than
          // this number. NaN-safe parse so 0 (day-of) and hour=0
          // (12 AM) survive the round-trip.
          goalie_auto_invite_enabled:        !!form.goalieAutoInviteEnabled,
          goalie_auto_invite_days_before:    Math.max(0, Math.min(14,
            Number.isFinite(+form.goalieAutoInviteDaysBefore) ? +form.goalieAutoInviteDaysBefore : 3)),
          goalie_auto_invite_hour:           Math.max(0, Math.min(23,
            Number.isFinite(+form.goalieAutoInviteHour) ? +form.goalieAutoInviteHour : 9)),
          goalie_auto_invite_threshold:      Math.max(1, Math.min(10, parseInt(form.goalieAutoInviteThreshold) || 2)),
          // May 4 2026 — FT-checkout sub-toggle. Saved alongside
          // the parent so they always stay in sync; backend's
          // dual-gate (parent && sub) handles the case where coach
          // turned the parent off without explicitly turning this off.
          goalie_auto_invite_on_checkout_enabled:    !!form.goalieAutoInviteOnCheckoutEnabled,
          goalie_auto_invite_on_checkout_delay_hours: Math.max(0, Math.min(24,
            parseInt(form.goalieAutoInviteOnCheckoutDelayHours, 10) || 0)),
          // Apr 2026 — waitlist auto-management. Each clamped to a
          // sane range so a typo can't blow up the cron (e.g. 99999
          // hours before game would never fire, but 0 would fire
          // every tick once the game started).
          waitlist_auto_release_enabled:        !!form.waitlistAutoReleaseEnabled,
          waitlist_auto_release_hours_before:   Math.max(1, Math.min(168, parseInt(form.waitlistAutoReleaseHoursBefore) || 1)),
          waitlist_auto_promote_below_enabled:  !!form.waitlistAutoPromoteBelowEnabled,
          waitlist_auto_promote_below_threshold: Math.max(1, Math.min(50, parseInt(form.waitlistAutoPromoteBelowThreshold) || 10)),
          // v2.5.104 — null/0 = no time gate (legacy behaviour);
          // positive int = only auto-promote when within X hours of
          // game start. Clamped 0-168 (one week max).
          waitlist_auto_promote_below_hours_before: (() => {
            const n = parseInt(form.waitlistAutoPromoteBelowHoursBefore);
            if (!Number.isFinite(n) || n <= 0) return null;
            return Math.min(168, Math.max(1, n));
          })(),
          low_attendance_alert_enabled:         !!form.lowAttendanceAlertEnabled,
          low_attendance_alert_threshold:       Math.max(1, Math.min(50, parseInt(form.lowAttendanceAlertThreshold) || 10)),
          low_attendance_alert_hours_before:    Math.max(1, Math.min(168, parseInt(form.lowAttendanceAlertHoursBefore) || 24)),
          // May 4 2026 (v2.5.69) — coach checkout-notification toggles.
          // Each is independent; coach can mix-and-match (e.g. only
          // care about FT goalie checkouts since spares are easier to
          // backfill). All four default false; only persisted when
          // coach explicitly opts in.
          notify_coach_on_ft_player_checkout:     !!form.notifyCoachOnFtPlayerCheckout,
          notify_coach_on_ft_goalie_checkout:     !!form.notifyCoachOnFtGoalieCheckout,
          notify_coach_on_spare_player_checkout:  !!form.notifyCoachOnSparePlayerCheckout,
          notify_coach_on_spare_goalie_checkout:  !!form.notifyCoachOnSpareGoalieCheckout,
          // v2.5.142 — shared proximity window. Clamped 1..720 hours
          // (1 hour to 30 days). Default 24 if blank/invalid.
          notify_coach_checkout_window_hours: Math.max(
            1,
            Math.min(720, parseInt(form.notifyCoachCheckoutWindowHours, 10) || 24)
          ),
          // v2.5.165 — per-tier checkout windows. Same clamp (1..720).
          notify_coach_checkout_window_hours_ft_player: Math.max(1, Math.min(720,
            parseInt(form.notifyCoachCheckoutWindowFtPlayer, 10) || 24)),
          notify_coach_checkout_window_hours_ft_goalie: Math.max(1, Math.min(720,
            parseInt(form.notifyCoachCheckoutWindowFtGoalie, 10) || 24)),
          notify_coach_checkout_window_hours_spare_player: Math.max(1, Math.min(720,
            parseInt(form.notifyCoachCheckoutWindowSparePlayer, 10) || 24)),
          notify_coach_checkout_window_hours_spare_goalie: Math.max(1, Math.min(720,
            parseInt(form.notifyCoachCheckoutWindowSpareGoalie, 10) || 24)),
          // Chunk 9 — overflow lockout window
          // Empty string / 0 / non-numeric → null (no lockout). Clamped 1-168 hours (1 hour to 1 week).
          overflow_lockout_hours_before: (() => {
            const v = parseInt(form.overflowLockoutHoursBefore);
            return Number.isFinite(v) && v > 0 ? Math.min(168, v) : null;
          })(),
          // Spare payment mode
          spare_payment_mode: form.sparePaymentMode,
          spare_fee_cents:     form.spareFee     ? Math.round(parseFloat(form.spareFee)  * 100) : null,
          // Apr 2026 (Tom_Notes F10) — separate goalie spare fee.
          // Null when blank (goalies fall back to spare_fee_cents
          // or are free); cents value when set.
          spare_goalie_fee_cents: form.spareGoalieFee ? Math.round(parseFloat(form.spareGoalieFee) * 100) : null,
          // Apr 2026 — show fee in spare confirmation emails even when
          // Stripe collection is off (tracking-only mode).
          spare_fee_show_in_notifications: !!form.spareFeeShowInNotifications,
          // Apr 2026 — the per-game/season "member fee" input was
          // retired. Force null on save so legacy values on existing
          // teams get cleared the next time a coach saves their
          // settings. Payment page + payments.js both now read
          // season_fee_cents for season fees instead.
          member_fee_cents:    null,
          // Season fees (full-time players paying full season via Game Day)
          season_fee_required: form.seasonFeeRequired,
          season_fee_cents:    form.seasonFee ? Math.round(parseFloat(form.seasonFee) * 100) : null,
          season_fee_pass_to_player: form.seasonFeePassToPlayer !== false,
          season_fee_round_up_quarters: !!form.seasonFeeRoundUpQuarters,
          payment_currency:    form.paymentCurrency,
          payment_description: form.paymentDescription || null,
          // Drinks
          drinks_enabled: form.drinksEnabled,
          drinks_mode:    form.drinksMode,
          // Auto-nudge
          auto_nudge_enabled:     form.autoNudgeEnabled,
          auto_nudge_days_before: parseInt(form.autoNudgeDaysBefore) || 2,
          auto_nudge_hour:        parseInt(form.autoNudgeHour) || 9,
          // Multi-select array — primary source of truth (Apr 2026).
          auto_nudge_groups:      Array.isArray(form.autoNudgeGroups) ? form.autoNudgeGroups : [],
          // Legacy single-string — kept in sync so a rolled-back server
          // would still nudge a roughly-equivalent group. We collapse
          // the array down to the closest single-string match:
          //   all 4 selected     → 'all'
          //   spares + sp.goalies→ 'spares' (legacy semantic)
          //   exactly one        → that value
          //   anything else      → 'all' (best-effort fallback)
          auto_nudge_group: (() => {
            const arr = form.autoNudgeGroups || [];
            const has = (g) => arr.includes(g);
            if (arr.length === 4) return 'all';
            if (arr.length === 2 && has('spares') && has('spare_goalies')) return 'spares';
            if (arr.length === 1) return arr[0];
            return 'all';
          })(),
          // ── Chunk 1 ────────────────────────────────────────────────────
          checkin_lookahead_enabled: form.checkinLookaheadEnabled,
          checkin_lookahead_games:   Math.max(1, parseInt(form.checkinLookaheadGames, 10) || 2),
          checkin_lookahead_exempt_goalies: form.checkinLookaheadExemptGoalies === true, // 2026-09-17
          no_show_ribbing: form.noShowRibbing !== false, // 2026-09-22
          stat_recorders: Array.isArray(form.statRecorders) ? form.statRecorders : [], // 2026-09-23
          tracked_stats: Array.isArray(form.trackedStats) ? form.trackedStats : [...DEFAULT_TRACKED], // 2026-09-19
          // v2.5.107 — when use-app is OFF, normalize the auto-balance
          // setting to false in the persisted JSON. Coach feedback
          // explicitly asks for the sub-toggles to be turned OFF
          // (not just hidden) when use-app is off, so this is
          // intentional data-clear rather than visual-only hide.
          // Side effect: if the coach flips use-app OFF, saves, then
          // turns it back ON, the previously-enabled sub-toggles
          // will appear OFF and must be re-enabled. That's the
          // accepted UX trade per user spec.
          // v2.5.149 — also force-false in MANUAL creation mode. The
          // auto-balance cron candidate query only checks
          // auto_balance_enabled (not team_creation_mode), so a team
          // that enabled the schedule in auto mode and then switched
          // to manual would keep getting auto-balanced by the cron —
          // wiping the coach's manual assignments. The schedule toggle
          // is hidden in manual mode (4A nested it under Auto-generate),
          // so this guarantees the persisted flag matches what's
          // reachable in the UI. Auto mode keeps the coach's choice.
          auto_balance_enabled:      (form.useAppForBalancing !== false
                                       && form.teamCreationMode !== 'manual')
                                       ? form.autoBalanceEnabled
                                       : false,
          auto_balance_hours_before: Math.max(1, parseInt(form.autoBalanceHoursBefore, 10) || 24),
          // v2.5.105 — unified email gate (works for both auto + manual paths).
          // v2.5.107 — force-false when use-app is OFF. Coach feedback:
          // "if Use the app to create teams turn off, ensure that ...
          // Automatically email created teams is turned off." Pairs
          // with the UI which hides the toggle in that state. Also
          // mirror to legacy sendTeamsManualEnabled below.
          auto_email_teams_enabled:  form.useAppForBalancing !== false
                                       ? (form.autoEmailTeamsEnabled !== false)
                                       : false,
          // 2026-07-06 — rebalance-on-change. Requires the AUTO creation
          // path (a re-run in manual mode would overwrite hand-built
          // teams with algorithm output), so force-false unless use-app
          // is ON and the mode is auto. Opt-in: strict === true.
          rebalance_on_change_enabled: (form.useAppForBalancing !== false
                                       && form.teamCreationMode !== 'manual')
                                       ? (form.rebalanceOnChangeEnabled === true)
                                       : false,
          // Apr 2026 — variety bias settings. Clamp weight to 0..1
          // (UI uses a slider so this is defensive); lookback to 1..20.
          balance_variety_weight:    Math.max(0, Math.min(1, Number(form.balanceVarietyWeight) || 0)),
          balance_variety_lookback:  Math.max(1, Math.min(20, parseInt(form.balanceVarietyLookback) || 4)),
          // Apr 2026 (Tom_Notes F12) — Create Teams settings.
          use_app_for_balancing:     form.useAppForBalancing !== false,
          // May 4 2026 (v2.5.69) — sub-mode under Use App. Only
          // meaningful when use_app_for_balancing=true. Coerced to
          // 'auto' or 'manual' here since the form always has one
          // of those two values; defensive `=== 'manual'` ensures
          // typos default to auto rather than persisting garbage.
          team_creation_mode:        form.teamCreationMode === 'manual' ? 'manual' : 'auto',
          send_teams_hours_before:   Math.max(0, Math.min(72, parseInt(form.sendTeamsHoursBefore) || 6)),
          // v2.5.107 — force-false when use-app is OFF (mirrors auto_email_teams_enabled).
          send_teams_manual_enabled: form.useAppForBalancing !== false ? !!form.sendTeamsManualEnabled : false,
          // Pair JSON — saved as a string; backend can parse/validate
          // when the matrix UI lands later. Empty string is fine.
          balance_pairs_json:        (form.balancePairsJson || '').trim(),
          // May 2 2026 — coach toggle: include goalies in balanced teams.
          // v2.5.107 — force-false when use-app is OFF. The UI hides
          // the goalie-balance toggle in that state; this keeps the
          // saved JSON consistent.
          balance_include_goalies:   form.useAppForBalancing !== false
                                       ? (form.balanceIncludeGoalies !== false)
                                       : false,
          // 2026-07-25 — goalie side-swap-per-mini (stats attribution
          // only; not gated on useAppForBalancing since goalie stats
          // apply however teams are formed).
          goalie_swap_each_mini:     form.goalieSwapEachMini === true,
          // Apr 2026 (Tom_Notes F9) — cascading invites by rank.
          spare_invite_cascade_enabled:  !!form.spareInviteCascadeEnabled,
          spare_invite_cascade_hours:    Math.max(1, Math.min(48, parseInt(form.spareInviteCascadeHours) || 4)),
          goalie_invite_cascade_enabled: !!form.goalieInviteCascadeEnabled,
          goalie_invite_cascade_hours:   Math.max(1, Math.min(72, parseInt(form.goalieInviteCascadeHours) || 8)),
          // Apr 30 2026 (App_Change_List #43-#45) — list-based cascade.
          // Backend route allowlists + sanitizes (caps at 3 entries,
          // validates shape per item). Sending [] = clear all lists =
          // fall back to legacy rating-tier cascade or single-blast.
          spare_invite_lists:            Array.isArray(form.spareInviteLists) ? form.spareInviteLists : [],
          goalie_invite_lists:           Array.isArray(form.goalieInviteLists) ? form.goalieInviteLists : [],
          // Apr 2026 (Tom_Notes F5)
          disable_overflow_after_spare_invites: !!form.disableOverflowAfterSpareInvites,
          // Apr 2026 (Tom_Notes F13)
          auto_mark_ft_in_on_reminder: !!form.autoMarkFtIn,
          // Apr 2026 (B4 audit) — `game_duration_minutes` legacy
          // save mapping removed. The canonical key is now
          // `default_game_duration_minutes` (set above), which is
          // what the GamePage display reads as fallback.
          spare_bypass_waitlist_after_request: form.spareBypassWaitlistAfterRequest,
          // Apr 2026 Miscellaneous — per-row attendance history toggle.
          show_attendance_counts: !!form.showAttendanceCounts,
          // Country — scopes arena search; empty means worldwide
          country:                (form.country || '').trim().toLowerCase(),
          // Privacy (Bug 9)
          // 2026-07-27 — toggle removed from the Privacy card; names
          // are always visible to teammates now. Force-true on every
          // save so any team stuck at false self-heals.
          show_player_names_to_players: true,
          show_ratings_to_players:      !!form.showRatingsToPlayers,
          // Chat notification scope — new tri-state setting. Also keep the
          // legacy boolean chat_email_all_messages in sync so other callers
          // that still read the old field continue to work (mode 'everyone'
          // → true; 'off' or 'coaches' → false). Removing the legacy field
          // entirely is a separate cleanup. Apr 2026 — default flipped
          // from 'off' to 'everyone' on the save path too, in case the
          // form somehow loaded with no value (defensive — the load
          // path now also defaults to 'everyone').
          chat_notify_mode:             form.chatNotifyMode || 'everyone',
          chat_email_all_messages:      form.chatNotifyMode === 'everyone',
          // v2.5.181 — per-team chat retention (days). Backend clamps:
          // 0/'' → never delete, else 1..3650.
          chat_retention_days:          form.chatRetentionDays === '' || form.chatRetentionDays === null
                                          ? 0
                                          : parseInt(form.chatRetentionDays, 10),
          // Apr 30 2026 — scoreboard edit-permission. Sent as bool
          // (backend whitelist passes it through; team_settings
          // stores it in the JSON column).
          scoreboard_coach_only:        form.scoreboardCoachOnly === true,
          // Apr 30 2026 (followup) — master on/off. Sent only as
          // explicit boolean; backend coerces and persists.
          scoreboard_enabled:           form.scoreboardEnabled !== false,
        },
        // Default max players (separate columns on teams table)
        defaultMaxPlayers:    form.defaultMaxPlayers ? parseInt(form.defaultMaxPlayers) : null,
        defaultAllowOverflow: form.defaultAllowOverflow,
      });
      toast.success('Team settings saved!');
      // Apr 2026 (Tom_Notes F10) — informative follow-up toast when
      // the backend auto-flipped spare_goalies_free off because the
      // coach set an explicit goalie fee. Toast on a delay so it
      // appears AFTER the success toast (otherwise stacked toasts
      // can confuse the eye).
      if (saveResp?.goalieFeeConflict) {
        setTimeout(() => {
          toast(
            '🥅 "Goalies play free" was turned off automatically because you set a goalie spare fee.',
            { duration: 6500 }
          );
        }, 400);
      }
      // Reset the dirty-detection snapshot — loadTeam() rebuilds it from the
      // just-saved values, so the "unsaved changes" banner dismisses itself.
      loadTeam();
    } catch (err) {
      // May 1 2026 — log full error context to browser console for
      // diagnosis when coaches report save failures. Server now
      // returns a more informative error message via err.response
      // .data.error. Console log gives us the full chain when
      // a coach reports the issue.
      console.error('[saveTeam] failed:', {
        status: err.response?.status,
        serverError: err.response?.data?.error,
        message: err.message,
      });
      toast.error(err.response?.data?.error || 'Failed to save');
    }
    setSaving(false);
  };

  const leaveTeam = async () => {
    setLeaving(true);
    try {
      await api.post(`/teams/${teamId}/leave`);
      toast.success(`Left ${team?.name}`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to leave team');
      setShowLeave(false);
    }
    setLeaving(false);
  };

  // Apr 2026 — Delete Team flow. The "confirm" step always shows;
  // the "finalWarning" step only shows for the sole coach (extra
  // "are you sure?" with explicit checkbox acknowledgment that the
  // team is gone for everyone after deletion).
  const openDeleteModal = async () => {
    setShowDelete('confirm');
    setDeleteConfirmName('');
    setDeleteAck(false);
    setDeleteCoachCount(null);
    // Fetch members in the background to figure out if this is the
    // sole coach. Non-fatal if it fails — the deletion itself doesn't
    // depend on this; we just use the count to decide whether to show
    // the second confirmation step.
    try {
      const { data } = await api.get(`/teams/${teamId}/members`);
      const coaches = (data || []).filter(m =>
        m.member_role === 'coach' && !m.isPending
      );
      setDeleteCoachCount(coaches.length);
    } catch {
      // Default to "not sole coach" on fetch failure — we'd rather
      // skip the extra warning than block the delete entirely.
      setDeleteCoachCount(2);
    }
  };

  const closeDeleteModal = () => {
    setShowDelete(null);
    setDeleteConfirmName('');
    setDeleteAck(false);
    setDeleteCoachCount(null);
  };

  const proceedToFinalWarning = () => {
    // Only proceed if name matches. Trim both sides — coaches sometimes
    // accidentally add a trailing space when copy-pasting.
    if ((deleteConfirmName || '').trim() !== (team?.name || '').trim()) {
      toast.error('Team name does not match. Type it exactly to confirm.');
      return;
    }
    if (deleteCoachCount === 1) {
      // Sole coach — show the extra "are you sure" warning.
      setShowDelete('finalWarning');
    } else {
      // Co-coaches present — go straight to the actual delete.
      executeDelete();
    }
  };

  const executeDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/teams/${teamId}`, {
        data: { confirmName: (deleteConfirmName || '').trim() },
      });
      toast.success(`Deleted "${team?.name}"`);
      // Apr 2026 — clear activeTeam + reload teams BEFORE navigating
      // so the dashboard doesn't briefly try to render with the
      // stale (deleted) team. setActiveTeam(null) lets pickActiveTeam
      // choose a different team from the refreshed list. loadTeams
      // refetches /teams which now omits the deleted one.
      try { setActiveTeam?.(null); } catch { /* layout-less, no-op */ }
      try { await loadTeams?.(); } catch { /* non-fatal */ }
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete team');
      setShowDelete(null); // close on error so the user can re-open
                           // and try again with the right name
    }
    setDeleting(false);
  };

  // Fix #46: seedRoster was referenced in the JSX but never defined.
  // Calls the backend dev-seed endpoint (blocked in production server-side).
  const seedRoster = async () => {
    if (!seedConfirm) { setSeedConfirm(true); return; }
    setSeedConfirm(false);
    setSeeding(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/dev-seed`);
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Seeding failed');
    }
    setSeeding(false);
  };

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  // Dirty detection — compares current form against the snapshot taken at
  // load time or after the last successful save. JSON.stringify is safe here
  // because form values are only primitives. Gated on having any snapshot.
  const canEditRole = isCoach || isManager;
  const isDirty = canEditRole &&
    Object.keys(initialForm).length > 0 &&
    JSON.stringify(form) !== JSON.stringify(initialForm);

  // Warn on browser close/reload when there are unsaved changes. Most browsers
  // ignore the returnValue string and show their generic prompt, but setting
  // it is required for the prompt to appear at all.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  if (loading || roleLoading) return <PageSpinner />;

  const canEdit = canEditRole;

  // Smooth-scroll the Save button into view — invoked from the banner.
  const scrollToSave = () => {
    const btn = document.getElementById('team-settings-save-button');
    if (btn) {
      btn.scrollIntoView({ behavior: 'smooth', block: 'center' });
      btn.classList.add('pulse-attention');
      setTimeout(() => btn.classList.remove('pulse-attention'), 2000);
    }
  };

  return (
    // May 4 2026 (v2.5.68) — provide compact-toggle-descriptions setting
    // to every nested ToggleSetting via context. Default false (legacy
    // verbose layout). Read from team.compactToggleDescriptions which
    // GET /teams/:teamId surfaces from system_settings.
    <TogglePresentationContext.Provider value={{ compact: team?.compactToggleDescriptions === true }}>
    <div className="animate-fade-in max-w-2xl">
      {/* Unsaved-changes sticky banner — shown only when dirty and editable.
          Apr 2026 — also gated to sub-pages that actually expose a Save
          button. On 'drinks', 'payouts', and 'danger' the main form
          Save isn't visible (different state ownership), so showing
          "click Save Settings" with no Save button to click would be
          a UX dead-end. */}
      {isDirty && activeSection !== 'drinks' && activeSection !== 'danger' && activeSection !== 'payouts' && (
        <div
          style={{
            position: 'sticky', top: 0, zIndex: 40,
            marginBottom: 12, marginTop: -4,
            padding: '10px 14px',
            borderRadius: 10,
            background: 'rgba(255,149,0,0.12)',
            border: '1.5px solid rgba(255,149,0,0.4)',
            display: 'flex', alignItems: 'center', gap: 10,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}>
          <AlertTriangle size={16} style={{ color: 'var(--puck)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="text-sm font-semibold" style={{ color: 'var(--puck)' }}>
              You have unsaved changes
            </div>
            <div className="text-xs" style={{ color: 'var(--text-2)', marginTop: 1 }}>
              Scroll down and click the blue <strong>Save Settings</strong> button to apply your changes.
            </div>
          </div>
          <button
            type="button"
            onClick={scrollToSave}
            className="text-xs font-bold px-3 py-1.5 rounded-lg flex-shrink-0"
            style={{
              background: 'var(--puck)', color: '#000', border: 'none', cursor: 'pointer',
            }}>
            Go to Save ↓
          </button>
        </div>
      )}

      <div className="flex items-center gap-3 mb-6">
        <Settings size={24} style={{ color: 'var(--ice)' }} />
        <div>
          <h1 className="section-title text-4xl">Team Settings</h1>
          {/* Apr 2026 — show the active sub-page name as a breadcrumb-
              style subtitle. Replaces the in-page chip nav as the
              "where am I?" cue; the sidebar handles navigation. The
              labels come from the shared TEAM_SETTINGS_SUBSECTIONS
              module so the sidebar and breadcrumb stay in sync if
              the labels ever change. */}
          {(() => {
            const entry = TEAM_SETTINGS_SUBSECTIONS.find(s => s.id === activeSection);
            if (!entry) return null;
            // Strip leading emoji + space from labels (e.g. "🏦 Payouts" →
            // "Payouts") for the breadcrumb. The emoji helps discoverability
            // in the sidebar but reads cleaner as plain text under the
            // heading. Simple regex avoids Unicode property escapes for
            // broader transformer compatibility.
            const label = entry.label.replace(/^[^\w\s]+\s+/, '');
            return (
              <p className="text-sm mt-1" style={{ color: 'var(--text-2)' }}>
                <span style={{ opacity: 0.55 }}>›</span> <span style={{ color: 'var(--ice)', fontWeight: 600 }}>{label}</span>
              </p>
            );
          })()}
          {!canEdit && (
            <p className="text-xs text-secondary mt-1">View only — only coaches can edit settings</p>
          )}
        </div>
      </div>

      {/* Apr 2026 — In-page chip nav removed. Sub-page navigation now
          lives in the sidebar (each layout's "Team Settings" item
          expands to show the 12 sub-pages when the user is on any
          settings route). Keeping nav in one place — the sidebar —
          matches every other section of the app and reduces visual
          noise above the actual settings cards. */}

      <form onSubmit={canEdit ? saveTeam : e => e.preventDefault()} className="space-y-5">
        {/* Apr 2026 — sub-page routing. Each card below is wrapped in a
            section guard so only the active sub-page renders. The form
            state is shared across all sub-pages so a coach can navigate
            between sections without losing edits, and the single Save
            Settings button (at the bottom) commits everything in one
            PUT regardless of which sub-page is currently visible. */}
        {/* Basic info */}
        {activeSection === 'team-info' && (<>
        <div className="card">
          <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
            <Users size={16} style={{ color: 'var(--ice)' }} /> Team Info
          </h2>
          <div className="space-y-4">
            <div>
              <label className="label">Team Name *</label>
              <input type="text" className="input-field" required autoComplete="organization" value={form.name} onChange={set('name')} disabled={!canEdit} />
            </div>
            <div>
              <label className="label">League Name</label>
              <input type="text" className="input-field" placeholder="Monday Night League" value={form.leagueName} onChange={set('leagueName')} disabled={!canEdit} />
            </div>
            {/* Sport — Apr 2026 (Tom_Notes F3). Currently used only for
                a few cosmetic touches (hint text, default jersey
                colours). The icons-only swap is intentional: we don't
                rename "ice time" → "field time" etc. since most app
                copy reads naturally without the sport noun. Adding
                more sport-specific behaviour later (different cap
                rules per sport, sport-aware AI prompts, etc.) only
                needs this single source of truth. */}
            {/* Sport — Apr 2026 (Tom_Notes F3) — moved into Team Info
                from the old Team Color section. The original
                dropdown lived under "Team Color" because both
                affected visual styling, but Sport is fundamentally
                identity (what we play), not appearance, so it
                belongs in Team Info. The richer option list (15+
                sports including ball hockey, curling, lacrosse) is
                preserved from the old position. */}
            <div>
              <label className="label">Sport</label>
              {/* 2026-07-10 (coach decision, backlog Q1) — list trimmed to
                  the sports folks actually run; the rest shown as a
                  disabled "coming soon" group (custom per-sport dashboards
                  are parked for the future). A team already saved with a
                  now-hidden sport keeps it selectable so nothing breaks. */}
              <select className="input-field" value={form.teamSport || 'hockey'} onChange={set('teamSport')} disabled={!canEdit}>
                {(() => {
                  const KEPT = [
                    { value: 'hockey',     emoji: '🏒', label: 'Ice Hockey' },
                    { value: 'ball-hockey',emoji: '🏑', label: 'Ball Hockey' },
                    { value: 'soccer',     emoji: '⚽', label: 'Soccer / Football' },
                    { value: 'baseball',   emoji: '⚾', label: 'Baseball' },
                    { value: 'softball',   emoji: '🥎', label: 'Softball' },
                    { value: 'basketball', emoji: '🏀', label: 'Basketball' },
                    { value: 'volleyball', emoji: '🏐', label: 'Volleyball' },
                    { value: 'lacrosse',   emoji: '🥍', label: 'Lacrosse' },
                    { value: 'other',      emoji: '🏅', label: 'Other' },
                  ];
                  const TBD = [
                    { value: 'football',   emoji: '🏈', label: 'American Football' },
                    { value: 'tennis',     emoji: '🎾', label: 'Tennis' },
                    { value: 'badminton',  emoji: '🏸', label: 'Badminton' },
                    { value: 'curling',    emoji: '🥌', label: 'Curling' },
                    { value: 'rugby',      emoji: '🏉', label: 'Rugby' },
                    { value: 'cricket',    emoji: '🏏', label: 'Cricket' },
                  ];
                  const current = form.teamSport || 'hockey';
                  const legacy = TBD.find(s => s.value === current);
                  return (
                    <>
                      {KEPT.map(s => (
                        <option key={s.value} value={s.value}>{s.emoji} {s.label}</option>
                      ))}
                      {legacy && (
                        <option key={legacy.value} value={legacy.value}>{legacy.emoji} {legacy.label}</option>
                      )}
                      <optgroup label="Coming soon (TBD)">
                        {TBD.filter(s => s.value !== current).map(s => (
                          <option key={s.value} value={s.value} disabled>{s.emoji} {s.label}</option>
                        ))}
                      </optgroup>
                    </>
                  );
                })()}
              </select>
              <p className="text-xs text-secondary mt-1" style={{ opacity: 0.7 }}>
                Used for icon styling. Game-day workflow (RSVPs, balancing, spares, etc.) works the same regardless of sport.
              </p>
            </div>
          </div>
        </div>

        {/* ── Home Arena removed (Apr 2026, Tom_Notes F1) ──
            The team-level "default arena" was misleading — most
            teams play at multiple venues across a season, and the
            actual arena per game is set on each game (and copied in
            via the schedule import). The form fields stay in state
            with their last saved value so existing data isn't lost
            on save; they're just no longer editable here. */}

        {/* ── Default Schedule removed (Apr 2026, Tom_Notes F2 / Q5) ──
            "Regular Game Day / Regular Game Time" was redundant —
            each game has its own date+time, and the schedule import
            populates them from the source. The fields stay in state
            so prior saved values aren't wiped. */}

        {/* Team Color — coaches only */}
        {canEdit && (
          <div className="card">
            <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
              <span>🎨</span> Team Color
            </h2>
            <p className="text-sm text-secondary mb-4">
              Pick an accent color used throughout the app for your team.
            </p>

            {/* ── Sport dropdown moved to Team Info (Apr 2026, F3) ── */}

            <div className="flex items-center gap-4 flex-wrap">
              {['#00d4ff','#00ff88','#ff9500','#ff4444','#a78bfa','#f472b6','#fbbf24','#34d399'].map(c => (
                <button key={c} onClick={() => set('teamColor')({ target: { value: c } })}
                  className="w-8 h-8 rounded-full transition-all"
                  style={{
                    background: c,
                    transform: form.teamColor === c ? 'scale(1.25)' : 'scale(1)',
                    boxShadow: form.teamColor === c ? `0 0 0 2px #000, 0 0 0 4px ${c}` : 'none',
                  }} />
              ))}
              <input type="color" value={form.teamColor || '#00d4ff'}
                onChange={set('teamColor')}
                className="w-8 h-8 rounded-full cursor-pointer border-0 bg-transparent"
                title="Custom color" />
            </div>

            {/* ── Jersey colors — for balanced-teams games ──
                Shown on the Balanced Teams card + rink diagram so every
                player knows which sweater to bring. Typical pickup convention
                is home=black / away=white, but coaches can override with any
                free-text color. Two quick-pick rows let coaches one-tap the
                common defaults; a free-text input handles the rest. ── */}
            {/* Apr 30 2026 (App_Change_List #30) — Jersey Colors moved
                out of Team Info and into the Create Teams section
                ("⚖️ Create Teams"). Coach feedback: jersey colors
                logically belong with the team-creation workflow
                (they apply to home/away assignments) rather than
                with the team's identity (name, sport, color). The
                form fields homeJerseyColor/awayJerseyColor still live
                in the same `form` state object, so save/load are
                untouched. */}
          </div>
        )}
        </>)}
        {/* end activeSection === 'team-info' */}

        {/* ── Game Settings ──
            Apr 2026 (Tom_Notes F4) — promoted to position #2 (just
            after Team Info / Team Color). Was originally at position
            #7 below the spare/payment cards; coaches said the cap
            and game-duration defaults belong near the team's
            identity since you set them up once when the team is
            created and rarely revisit. */}
        {activeSection === 'games' && canEdit && (
          <div className="card">
            {/* Apr 2026 (Tom_Notes F-rename) — was "🏒 Max Players Per
                Game". Renamed to "Game Settings" to host both the
                player-cap default AND the game-duration default
                (F4). The cap controls remain identical; the new
                duration field is display-only and can be overridden
                per-game on the Game page. */}
            <h2 className="font-bold text-lg flex items-center gap-2 mb-4">
              🏒 Game Settings
            </h2>
            <p className="text-sm text-secondary mb-4">
              Defaults applied to every new game. You can override these per-game (or per-season for the cap).
            </p>

            {/* ── Default Game Duration (Apr 2026, Tom_Notes F4) ──
                Display-only value — used for "ends at" math on the
                Game page header, calendar export end times, and
                schedule list duration badges. Stepped at 10-minute
                intervals because most rec leagues book ice in those
                blocks (50/60/75/90 min are by far the most common).
                Per-game override lives on the Game page (a coach
                can shorten/lengthen any single game).
                Apr 2026 (B4 audit) — consolidated to bind directly
                to defaultGameDurationMinutes / default_game_duration_minutes,
                which is the canonical key the GamePage reads as a
                fallback. Empty string = "no team default" (skip
                the duration display when no per-game override
                exists either). */}
            <div className="mb-4">
              <label className="label">
                Default Game Duration <span className="opacity-50 text-xs">(optional)</span>
              </label>
              <select className="input-field"
                style={{ maxWidth: 220 }}
                value={form.defaultGameDurationMinutes || ''}
                onChange={e => setForm(p => ({ ...p, defaultGameDurationMinutes: e.target.value }))}>
                <option value="">— no default —</option>
                {/* 10-minute steps from 30 to 180 — covers everything
                    from short pickup to a full ice-hockey game. */}
                {Array.from({ length: 16 }, (_, i) => 30 + i * 10).map(m => (
                  <option key={m} value={m}>
                    {m < 60
                      ? `${m} minutes`
                      : `${Math.floor(m / 60)}h ${m % 60 ? `${m % 60}m` : ''}`.trim()}
                  </option>
                ))}
              </select>
              <FieldHelp>
                Used to show duration on game cards. Coaches can override per-game in the schedule editor.
              </FieldHelp>
            </div>

            <p className="text-sm text-secondary mb-4" style={{ fontWeight: 600 }}>
              Player Cap
            </p>
            {/* Min Players moved here from Reminders (Apr 2026,
                Tom_Notes F4) — game-shape settings now live in one
                place. Threshold drives spare-request emails and
                "list looks short" warnings throughout the app.
                Apr 30 2026 (App_Change_List #31) — minimum dropped
                from 4 → 0. Coach feedback: rec/pickup teams
                sometimes want NO floor on player count (e.g. "we'll
                play with whoever shows"), and the hard 4-min was
                blocking that case. Setting to 0 effectively disables
                the spare-request and short-list warnings since
                attendance can never drop below 0; coaches who don't
                want those alerts can use this value instead of
                scattered toggles elsewhere. */}
            <div className="mb-4">
              <label className="label">Min Players to Play (0 disables short-list alerts)</label>
              <input type="number" min="0" max="30" className="input-field"
                style={{ maxWidth: 200 }}
                value={form.minPlayersToPlay} onChange={set('minPlayersToPlay')} />
              <FieldHelp>
                Player threshold for spare requests, low-attendance alerts, and
                short-list warnings on the dashboard. Set to <strong>0</strong>
                if you want no minimum (we'll play with whoever shows up).
              </FieldHelp>
            </div>
            {/* Apr 30 2026 (App_Change_List #32a) — Games tab layout
                cleaned up. Was a 2-col grid jamming Max Players next
                to the overflow toggle, with the disable-after-spare
                toggle nested deeper. New layout:
                  1) Max Players (own row, narrow input — has logical
                     max around 30 for hockey; 200-cap is a safety net)
                  2) Overflow Full-Times (own box, full width)
                  3) Lockout window + disable-after-spare-invites
                     nested ONLY when Allow Overflow is on
                Coach feedback: previous layout suggested Max and
                overflow were related at the same hierarchy — they're
                not. Max sets the hard cap; overflow is the policy
                for what to do at the cap. Sequencing them makes
                the cause/effect read naturally.
                v2.5.97 — coach asked specifically for the Max Players
                paragraph to be tooltip-collapsable (item 12 example).
                Now uses FieldHelp which respects the super-admin
                compact_toggle_descriptions flag. */}
            <div className="mb-4">
              <label className="label">Max Players (blank = unlimited)</label>
              <input type="number" min="1" max="200" className="input-field"
                style={{ maxWidth: 200 }}
                placeholder="e.g. 20"
                value={form.defaultMaxPlayers}
                onChange={e => setForm(p => ({ ...p, defaultMaxPlayers: e.target.value }))} />
              <FieldHelp>
                Hard cap on the roster. When reached, additional RSVPs go to the
                waitlist (or overflow, depending on the toggle below).
              </FieldHelp>
            </div>

            {/* Overflow toggle — own box now (App_Change_List #32a).
                Sits as the next-step decision after Max: "what do you
                want to happen when we hit the cap?" */}
            <div className="mb-3">
              <ToggleSetting
                emoji="📈"
                title="Allow overflow (full-timers only)"
                shortDesc="Full-timers can RSVP past the cap; spares always go to the waitlist."
                longDesc="Full-time members can still RSVP 'in' once the game is at max — they overflow past the cap. Spares always go to the waitlist when the game is full, regardless of this setting."
                tooltip="Lets your regular players say yes even after the game is full. Spares always wait for a spot to open up."
                value={form.defaultAllowOverflow}
                onChange={(next) => setForm(p => ({ ...p, defaultAllowOverflow: next }))}>
                {/* Chunk 9 (Apr 2026) — overflow lockout window.
                    Optional: once the game is X hours away, stop
                    allowing overflow (roster is effectively locked
                    at max_players). */}
                <label className="label">Overflow lockout (hours before game)</label>
                <input
                  type="number" min="0" max="168"
                  className="input-field"
                  style={{ maxWidth: 200 }}
                  value={form.overflowLockoutHoursBefore}
                  onChange={e => setForm(p => ({ ...p, overflowLockoutHoursBefore: e.target.value }))}
                  placeholder="Leave blank for none"
                />
                <FieldHelp>
                  Stops allowing overflow this many hours before game time.
                  Prevents last-minute "I'm IN" taps from pushing a game past
                  its intended size. Leave blank (or 0) to keep overflow
                  open through game start.
                </FieldHelp>

                {/* Apr 2026 (Tom_Notes F5) — second overflow lock.
                    Once the spare-request email goes out, full-timers
                    shouldn't be allowed to keep overflowing past the cap
                    because that starves the spares the team just invited.
                    Independent of the hours-before lockout above; both
                    apply if both are on (whichever fires first wins). */}
                <div className="mt-3">
                  <ToggleSetting
                    emoji="🚫"
                    title="Disable overflow once spare invites are sent"
                    shortDesc="Lock the cap the moment spare invites fire."
                    longDesc="As soon as the spare request email fires for a game, lock the cap so late-RSVP'ing full-timers don't push past max and starve the spares you just invited. Spares can still claim open spots; full-timers go to the waitlist instead of overflow."
                    tooltip="Once you've invited spares, the game stops accepting extra regulars — so the spares you invited actually get the open spots."
                    accentColor="var(--puck)"
                    value={form.disableOverflowAfterSpareInvites}
                    onChange={(next) => setForm(p => ({ ...p, disableOverflowAfterSpareInvites: next }))}
                  />
                </div>
              </ToggleSetting>
            </div>
          </div>
        )}

        {/* Scoreboard settings — Apr 30 2026.
            Two toggles in priority order:
            1. Master on/off — "Enable Scoreboard". When OFF, the
               scoreboard is hidden from game pages, dashboard,
               AND the standalone "Scoreboards" menu item is
               removed from the team's nav. Default ON.
            2. Coach-only edits — when ON, only coaches and
               managers can change scores. When OFF (default),
               every roster member can edit during games.
            The second toggle is only visible/active when the
            first is ON — saves a coach from configuring perms
            for a feature they've turned off. */}
        {activeSection === 'scoreboard' && (<>
          <div className="card">
            <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
              <span style={{ fontSize: 18 }}>🏆</span> Scoreboard
            </h2>
            <p className="text-secondary text-sm mb-4">
              Track game scores live. Your team's scoreboard appears
              on each game's page and (when there's a game today) on
              your Dashboard. A "Scoreboards" link in the team menu
              shows every game's score for season recap and stats.
            </p>

            {canEdit ? (
              <>
                {/* Apr 30 2026 (App_Change_List #41) — converted from
                    checkbox-card to ToggleSetting. Same data flow,
                    cleaner visual. The coach-only-edits toggle is
                    nested inside the master-enabled body so it's
                    only visible when the parent is ON. */}
                <ToggleSetting
                  emoji="🏒"
                  title="Enable scoreboard"
                  shortDesc="Track game scores from the game page and Dashboard."
                  longDesc="The scoreboard appears on game pages and (when there's a game today) on your Dashboard. A 'Scoreboards' link in the team menu shows every game's score for season recap and stats. Existing score history is preserved either way."
                  tooltip="Turn off if you don't track scores or use a different system. Historical scores stay in the database."
                  value={form.scoreboardEnabled !== false}
                  onChange={(next) => setForm(p => ({ ...p, scoreboardEnabled: next }))}>
                  <ToggleSetting
                    title="Coach-only edits"
                    shortDesc="Restrict who can change the score during games."
                    longDesc="When ON, players cannot change the score — only coaches and managers can. When OFF (default), everyone on the roster can edit during games."
                    tooltip="Useful for leagues that want a single source of truth for game results."
                    value={form.scoreboardCoachOnly === true}
                    onChange={(next) => setForm(p => ({ ...p, scoreboardCoachOnly: next }))}
                  />
                </ToggleSetting>
              </>
            ) : (
              // Non-coaches viewing this page (rare — typed URL).
              <div className="p-3 rounded-lg text-sm text-secondary"
                   style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)' }}>
                Scoreboard is{' '}
                <strong style={{ color: 'var(--text)' }}>
                  {form.scoreboardEnabled === false
                    ? 'disabled'
                    : (form.scoreboardCoachOnly ? 'coach-only' : 'open to all team members')}
                </strong>.
                <p className="mt-2 text-xs">Only coaches can change this setting.</p>
              </div>
            )}
          </div>
        </>)}

        {/* Reminders — coaches only
            Apr 2026 (Tom_Notes F-rename) — was "Automation Settings".
            Renamed to "Reminders" to match the user's mental model:
            this section is about WHEN reminder emails fire (manual
            reminder offset, second reminder, auto-jab). Auto-balance
            and balance variety moved out of here into a new
            "Create Teams" section since they're not reminders. */}
        {activeSection === 'reminders' && canEdit && (<>
          <div className="card">
            <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Clock size={16} style={{ color: 'var(--ice)' }} /> Reminders
            </h2>
            <div className="space-y-4">
              {/* Min Players moved to "Max Players Per Game" card per
                  Tom_Notes F4 — game-shape settings live together
                  there. The form field stays in state and saves the
                  same way; only the visual location changed. */}
              {/* ── Reminder #1 — pre-game reminder (everyone on roster) ──
                  Apr 30 2026 — label clarified to make recipient set
                  unambiguous. Coach feedback: "Reminder #1" / "Reminder
                  #2" / "Auto-Jab" all sounded the same; coaches
                  couldn't tell which targeted no-responders vs the
                  whole roster. Reminder 1 + 2 = roster-wide pre-game
                  reminders; Auto-Jab specifically targets only people
                  who haven't RSVP'd. Label below makes that explicit.
                  v2.5.104 — wrapped in a ToggleSetting so coaches can
                  disable the reminder entirely. Default ON preserves
                  legacy always-on behavior. When OFF, the cron skips
                  this team's reminder (cron-side gate at
                  scheduler.js: COALESCE pre_game_reminder_enabled
                  TRUE). The include-spares toggle below remains a
                  sibling — affects the audience when this reminder
                  IS on; harmless when off. ── */}
              <ToggleSetting
                emoji="🔔"
                title="Pre-game reminder (everyone)"
                shortDesc="Roster-wide nudge N days before each game."
                longDesc="Sends a reminder to your full roster N days before each game so people remember to RSVP. The send-time + days-ahead controls show up once enabled."
                tooltip="A reminder sent before each game to all your full-time players plus any spares you've invited to that game. Turn off to stop these entirely."
                value={form.preGameReminderEnabled !== false}
                onChange={(next) => setForm(p => ({ ...p, preGameReminderEnabled: next }))}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Days before game</label>
                    <input type="number" min="1" max="14" className="input-field"
                      value={form.reminderDaysBefore} onChange={set('reminderDaysBefore')} />
                    <FieldHelp>
                      Sent this many days before each game (1–14). Audience
                      depends on the "Include spares" toggle below.
                    </FieldHelp>
                  </div>
                  <div>
                    <label className="label">Specify Time to Send Game Reminder</label>
                    <input type="number" min="0" max="23" className="input-field"
                      value={form.weeklyReminderHour} onChange={set('weeklyReminderHour')} />
                    <FieldHelp>
                      Hour of day, 24-hour format (0–23). Uses your server timezone.
                      Applies to both pre-game reminders.
                    </FieldHelp>
                  </div>
                </div>
              </ToggleSetting>

              {/* May 4 2026 — coach toggle: include spares in reminder #1.
                  Default ON preserves the legacy behavior (spares get the
                  weekly awareness email so they can volunteer to fill in
                  for any open spots). Some coaches whose spare pool is
                  large or dormant prefer FT-only reminders so they're
                  not blasting people who aren't on the active roster.
                  Reminder #2 is always FT-only — this only affects #1. */}
              <ToggleSetting
                emoji="👥"
                title="Include spares in the first reminder"
                shortDesc="Spares get the weekly awareness email too."
                longDesc="When ON (default), the first reminder goes to everyone on the team — full-time players AND spares — so spares know there's a game coming up and can volunteer to fill in. When OFF, the first reminder goes ONLY to full-time players (including full-time goalies); spares are skipped. Reminder #2 is always FT-only regardless of this setting."
                tooltip="Off = quieter inbox for your spare pool. On = spare pool stays informed about every game and can flag interest in filling spots."
                value={form.weeklyReminderIncludeSpares !== false}
                onChange={(next) => setForm(f => ({ ...f, weeklyReminderIncludeSpares: next }))}
              />

              {/* ── Reminder #2 — second pre-game reminder ──
                  Apr 30 2026 (App_Change_List #40, #41) — second reminder
                  per the change list spec: targets FT players (incl. FT
                  goalies) who haven't yet set their attendance. Spares
                  are excluded; FTs who already RSVP'd in/out/maybe are
                  excluded. Backend filter at sendGameReminders(game,
                  'second').
                  v2.5.97 — coach feedback: "Take out the FT auto job
                  reference." Removed "(FT auto-jab)" from the title
                  and "auto-jab" terminology from descriptions —
                  coaches found the internal-jargon name confusing.
                  Behavior unchanged. */}
              <ToggleSetting
                emoji="🔔"
                title="Second pre-game reminder"
                shortDesc="Nudge full-timers who haven't RSVP'd yet."
                longDesc="A targeted second reminder closer to game day. Goes ONLY to full-time players (incl. full-time goalies) who haven't yet set their attendance — spares and already-RSVP'd FTs are skipped. Useful for chasing down stragglers without re-emailing the whole roster."
                tooltip="Goes only to full-timers who haven't replied yet. Set it for fewer days before the game than the first reminder. Leave the time blank to match the first reminder, or pick a later hour (e.g. first at 9 AM, this one at 4 PM)."
                value={form.reminder2Enabled}
                onChange={(next) => setForm(f => ({ ...f, reminder2Enabled: next }))}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label className="label">Days before game</label>
                    <input type="number" min="1" max="30" className="input-field"
                      value={form.reminder2DaysBefore}
                      onChange={set('reminder2DaysBefore')} />
                  </div>
                  {/* May 4 2026 — separate time-of-day picker. Empty
                      = inherit weekly_reminder_hour. The select shows
                      every hour 0-23 in 12-hour format. */}
                  <div>
                    <label className="label">Send at hour</label>
                    <select className="input-field"
                      value={form.reminder2Hour}
                      onChange={set('reminder2Hour')}>
                      <option value="">Inherit (use first reminder time)</option>
                      {Array.from({ length: 24 }, (_, h) => {
                        const hh12 = ((h + 11) % 12) + 1;
                        const ap   = h < 12 ? 'AM' : 'PM';
                        return <option key={h} value={h}>{hh12}:00 {ap}</option>;
                      })}
                    </select>
                  </div>
                </div>
              </ToggleSetting>

              {/* Game-day same-day reminder — fires X hours before game time
                  on game day itself, separate from the multi-day-ahead
                  reminder above. Sent to everyone checked in (including
                  waitlisted players, with a "still on waitlist — stay tuned"
                  variant). ── */}
              <div className="mt-3">
                <ToggleSetting
                  emoji="🚨"
                  title="Same-day game reminder"
                  shortDesc="Optional: a reminder on game day, X hours before puck drop."
                  longDesc="Send a reminder on game day, X hours before puck drop. Checked-in players get 'see you tonight!'; waitlisted players get 'still on waitlist — stay tuned.'"
                  tooltip="Separate from the multi-day-ahead reminders above. Goes to confirmed-in and waitlisted players only — not the whole roster."
                  value={form.gameDayReminderEnabled}
                  onChange={(next) => setForm(f => ({ ...f, gameDayReminderEnabled: next }))}>
                  <label className="label">Hours before game</label>
                  <input type="number" min="1" max="23" className="input-field"
                    style={{ maxWidth: 120 }}
                    value={form.gameDayReminderHoursBefore}
                    onChange={set('gameDayReminderHoursBefore')} />
                  <FieldHelp>
                    1–23 hours. Typical values: 3 (afternoon-of), 6 (earlier-afternoon),
                    12 (morning-of for an evening game).
                  </FieldHelp>
                </ToggleSetting>
              </div>

              {/* v2.5.139 — No-game-this-week reminder. Defaults ON
                  (opt-out). Fires hours_before the team's REGULAR
                  game time (game_day + game_time from team settings)
                  on weeks where there's no game scheduled. ── */}
              <div className="mt-3">
                <ToggleSetting
                  emoji="📅"
                  title="No-games-this-week notice"
                  shortDesc="Heads-up email when there are no games on the schedule for the upcoming week."
                  longDesc="On weeks when no games are scheduled, send the team a heads-up email. Fires before your team's regular game time so it lands when players would otherwise be checking the schedule."
                  tooltip="Helpful for rec/pickup teams whose schedule has gaps. Disable if your team plays a fixed weekly schedule with no breaks."
                  value={form.noGameWeekReminderEnabled}
                  onChange={(next) => setForm(f => ({ ...f, noGameWeekReminderEnabled: next }))}>
                  <label className="label">Hours before regular game time</label>
                  <input type="number" min="1" max="168" className="input-field"
                    style={{ maxWidth: 120 }}
                    value={form.noGameWeekReminderHoursBefore}
                    onChange={set('noGameWeekReminderHoursBefore')} />
                  <FieldHelp>
                    1–168 hours (up to 1 week). Typical values: 24 (the day before),
                    48 (two days before), 72 (mid-week). The email lands this many
                    hours before what would have been your next regular game time.
                  </FieldHelp>
                </ToggleSetting>
              </div>

              {/* ── Check-in lookahead guardrail ──
                  Limits how far ahead a player can RSVP. When OFF, players
                  can RSVP to any future game (legacy behaviour). When ON,
                  the backend refuses RSVPs to games beyond position N in
                  the upcoming-games list, returning a clear error message.
                  May 4 2026 — coach/manager bypass removed (see
                  backend/src/routes/games.js line ~944). Coaches who
                  genuinely need to RSVP a player far ahead can use the
                  per-player override endpoint (admin-only, intentional). */}
              <div className="mt-3">
                <ToggleSetting
                  emoji="🔒"
                  title="Limit how far ahead players can check in"
                  shortDesc="Prevent RSVPs to games more than a few weeks out."
                  longDesc="Prevents anyone (players AND coaches) from committing to games far in the future. When on, only the next N upcoming games are open for RSVP — anything further ahead shows a 'check back closer to the date' message."
                  tooltip="Useful for pickup leagues with rolling spare lists — keeps the most-recent 1–3 games on top of mind without players locking into their whole season at once. As of May 2026, coaches no longer bypass this; if you need to RSVP a player to a far-ahead game, use the per-player override on the Game page."
                  value={form.checkinLookaheadEnabled}
                  onChange={(next) => setForm(f => ({ ...f, checkinLookaheadEnabled: next }))}>
                  <label className="label">How many upcoming games are open?</label>
                  <input type="number" min="1" max="30" className="input-field"
                    style={{ maxWidth: 120 }}
                    value={form.checkinLookaheadGames}
                    onChange={set('checkinLookaheadGames')} />
                  <FieldHelp>
                    1 = just the very next game. 2 = next 2 games. Typical pickup leagues use 1–3.
                  </FieldHelp>
                  {/* 2026-09-17 (per Gilles) — goalies are scarce; let them
                      commit to (or bow out of) any future game even while
                      skaters are limited to the next N. Backend guard in
                      games.js skips goalies when this is on. */}
                  <label className="flex items-center gap-3 mt-3 cursor-pointer" style={{ fontSize: 13 }}>
                    <input type="checkbox"
                      checked={form.checkinLookaheadExemptGoalies === true}
                      onChange={e => setForm(f => ({ ...f, checkinLookaheadExemptGoalies: e.target.checked }))} />
                    <span>
                      <span className="font-semibold">🥅 Goalies can sign in / out any time</span>
                      <span className="block text-xs text-secondary">Goalies skip this limit — they can RSVP to any future game. Skaters are still limited to the next {Math.max(1, parseInt(form.checkinLookaheadGames, 10) || 2)}.</span>
                    </span>
                  </label>
                </ToggleSetting>
              </div>

              {/* ── No-show ribbing (2026-09-22, per Gilles) ── */}
              <div className="mt-3">
                <ToggleSetting
                  emoji="👻"
                  title="Rib repeat no-shows when they check in"
                  shortDesc="A player with a past no-show gets an UNRELIABLE badge and a cheeky bot remark on the game page."
                  longDesc="When someone the coach flagged as a no-show checks in to a later game, their row shows a 👻 UNRELIABLE badge and a one-liner from the bot ('Are you really coming this time?'). The whole team sees it. It escalates with repeat offences and stops the moment the coach clears their no-shows."
                  tooltip="Good-natured beer-league accountability. Turn it off if your team would rather not."
                  value={form.noShowRibbing !== false}
                  onChange={(next) => setForm(f => ({ ...f, noShowRibbing: next }))}
                />
              </div>

              {/* ── Which stats we track (2026-09-19, Phase 7A) ──
                  Drives three things at once: what the bench tracker is
                  prompted for, which columns show on the game page, and what
                  rolls into season totals. Turning one off hides the column;
                  it does not delete anything already recorded. */}
              <div className="mt-3 rounded-xl" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', padding: 14 }}>
                <div className="text-sm font-bold mb-1">📊 Stats to track</div>
                <div className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
                  Pick what your team actually records during games. Fewer is better —
                  every one you turn on is another thing somebody has to notice on the bench.
                </div>
                {STAT_GROUPS.map(g => (
                  <div key={g.key} className="mb-2.5">
                    <div className="text-xs font-bold mb-1" style={{ color: 'var(--ice)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {g.label}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {g.stats.map(st => {
                        const on = (form.trackedStats || []).includes(st.code);
                        return (
                          <button
                            key={st.code}
                            type="button"
                            onClick={() => setForm(f => {
                              const cur = Array.isArray(f.trackedStats) ? f.trackedStats : [];
                              return { ...f, trackedStats: on ? cur.filter(c => c !== st.code) : [...cur, st.code] };
                            })}
                            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold"
                            style={{
                              cursor: 'pointer',
                              border: `1px solid ${on ? 'var(--ice)' : 'var(--border)'}`,
                              background: on ? 'rgba(0,212,255,0.10)' : 'transparent',
                              color: on ? 'var(--ice)' : 'var(--text-2)',
                            }}>
                            {on ? '✓ ' : ''}{st.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-3 mt-2">
                  <button type="button"
                    onClick={() => setForm(f => ({ ...f, trackedStats: [...DEFAULT_TRACKED] }))}
                    className="text-xs font-semibold"
                    style={{ color: 'var(--ice)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    Reset to recommended
                  </button>
                  <span className="text-xs" style={{ color: 'var(--text-3)' }}>
                    {(form.trackedStats || []).length} selected
                  </span>
                </div>
              </div>

              {/* ── 2026-09-23 — Live stats: who may record ── */}
              <div className="mt-3 rounded-xl" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', padding: 14 }}>
                <div className="text-sm font-bold mb-1">🎙 Live stats — who can record</div>
                <div className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
                  The coach can always record. Add anyone else who may hit Record on
                  the scoreboard. Two recordings can run on the same game: the first one started
                  feeds the live stats, the second is a backup capture.
                </div>
                {recorderCandidates === null ? (
                  <button type="button" onClick={loadRecorderCandidates}
                    className="text-xs font-semibold"
                    style={{ color: 'var(--ice)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    {(form.statRecorders || []).length
                      ? `${form.statRecorders.length} designated — tap to change`
                      : 'Choose recorders'}
                  </button>
                ) : recorderCandidates.length === 0 ? (
                  <div className="text-xs" style={{ color: 'var(--text-3)' }}>No other members with accounts yet.</div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {recorderCandidates.map(m => {
                      const on = (form.statRecorders || []).includes(m.userId);
                      return (
                        <button key={m.userId} type="button"
                          onClick={() => setForm(f => {
                            const cur = Array.isArray(f.statRecorders) ? f.statRecorders : [];
                            if (on) return { ...f, statRecorders: cur.filter(id => id !== m.userId) };
                            if (cur.length >= 10) return f;
                            return { ...f, statRecorders: [...cur, m.userId] };
                          })}
                          className="px-2.5 py-1.5 rounded-lg text-xs font-semibold"
                          style={{
                            cursor: 'pointer',
                            border: `1px solid ${on ? 'var(--ice)' : 'var(--border)'}`,
                            background: on ? 'rgba(0,212,255,0.10)' : 'transparent',
                            color: on ? 'var(--ice)' : 'var(--text-2)',
                          }}>
                          {on ? '✓ ' : ''}{m.name}{m.role === 'manager' ? ' (mgr)' : ''}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── Auto-mark FT players in (Apr 2026, Tom_Notes F13) ──
                  Some leagues default to "everyone full-time is in
                  unless they say otherwise." This toggle flips the
                  semantics: when on, the cron pre-fills every FT
                  member's status to 'in' for upcoming games when
                  the first reminder fires. Players who don't want to
                  play tap "Out" to opt out, instead of having to
                  proactively tap "In" to opt in.

                  Default OFF — keeps the existing behaviour where
                  silence means "no response."
                  Backend reads `auto_mark_ft_in_on_reminder` setting
                  inside the reminder cron (when implemented). */}
              <div className="mt-3">
                <ToggleSetting
                  emoji="✅"
                  title="Auto-mark full-timers IN by default"
                  shortDesc="Default every FT player to 'in' when the first reminder fires."
                  longDesc="When the first reminder fires, pre-fill every full-timer's status as 'in.' Players tap Out to opt out. Use this when your league assumes attendance — saves the team from chasing RSVPs every week. Spares always have to opt-in manually."
                  tooltip="Inverts the RSVP semantics: silence becomes 'in' instead of 'no response.' Coaches in well-attended leagues find this saves a lot of nudging."
                  accentColor="var(--goal)"
                  value={form.autoMarkFtIn}
                  onChange={(next) => setForm(p => ({ ...p, autoMarkFtIn: next }))}
                />
              </div>

              {/* ── Auto-balance + Variety bias moved to Create Teams ──
                  Apr 2026 (Tom_Notes F6) — these aren't really
                  reminders; they're balancing-engine controls. The
                  "Create Teams" section further down hosts them now,
                  alongside new manual-mode + pair-with-player
                  constraints (F12). The settings keys themselves are
                  unchanged so saved values keep applying. */}
            </div>
          </div>

          {/* Apr 30 2026 (App_Change_List #33) — "Nudge no-responders
              (auto-jab)" card removed. Coaches asked us to keep just
              Reminder #1 + Reminder #2 as the automatic email layer.
              Manual jabs are still available per-game from the Game
              page's "Awaiting Response — Confirm Jab" modal — that
              path gives the coach explicit control over who gets a
              nudge and when. The auto_nudge_* settings remain in the
              team JSONB (they're allowlisted) so saved values aren't
              lost, but no UI exposes them and no cron reads them. */}
        </>)}
        {/* end activeSection === 'reminders' */}

        {activeSection === 'waitlist' && canEdit && (
          <div className="card">
            {/* Apr 30 2026 — consolidated "Spare and Waitlist Management".
                Was two separate sub-pages (/settings/spares and
                /settings/waitlist). Coaches reported the split was
                confusing because every spare-related setting also
                affects the waitlist (and vice versa). The /spares
                URL still resolves to this page for back-compat. */}
            <h2 className="font-bold text-lg flex items-center gap-2 mb-4">
              🏒 Spare &amp; Waitlist Management
            </h2>
            <p className="text-sm text-secondary mb-4">
              Decide how spares get invited, what the waitlist does on its own,
              and how cascading invitations work. Full-time players always get
              first priority on game spots.
            </p>
            <div className="space-y-3">

              {/* May 4 2026 — explicit sub-section header. Coach asked to
                  organize this card into three labeled groups: Spare
                  Management, Goalie Management, Waitlist Management
                  (the last lives in its own card below this one). */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 0 6px',
                borderBottom: '1px solid var(--border)',
                marginBottom: 4,
              }}>
                <span style={{ fontSize: 16 }}>👤</span>
                <h3 style={{
                  fontSize: 13, fontWeight: 800,
                  color: 'var(--text)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  margin: 0,
                }}>
                  Spare Management
                </h3>
              </div>
              {/* ── 1) Auto-invite spares ── (FIRST per coach feedback)
                  Old auto-invite was hidden inside the spare-toggles
                  list. Coach feedback: the auto-invite is the most
                  decisive control on this page (it's what triggers
                  the "we're short, need spares" email blast), so it
                  belongs at the top where coaches setting up a new
                  team see it immediately. The old auto-waitlist /
                  sticky / auto-release toggles still live below.
                  Apr 30 2026 — old behaviour was "N hours after
                  auto-jab fires"; new behaviour is "N days before
                  each game at hour H, only if check-in is below
                  threshold". Independent of auto-jab. The threshold
                  is now an always-on gate, not an opt-in guardrail. */}
              <ToggleSetting
                emoji="📨"
                title="Auto-invite Spares"
                shortDesc="Email spares automatically based on a check-in threshold."
                longDesc="Sends a spare-request email to all spares automatically, a chosen number of days before each game, at a chosen time of day — but only if check-in is still below the threshold you set. First spares to RSVP (up to game max) play; the rest go to the waitlist."
                tooltip="Independent of any other email feature. Fires on its own schedule based on the game date, only when the team is short."
                accentColor="var(--puck)"
                value={form.spareAutoInviteEnabled}
                onChange={(next) => setForm(p => ({ ...p, spareAutoInviteEnabled: next }))}>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Days before game</label>
                    <select className="input-field mt-1"
                      value={form.spareAutoInviteDaysBefore}
                      onChange={e => setForm(p => ({ ...p, spareAutoInviteDaysBefore: e.target.value }))}>
                      {[0, 1, 2, 3, 4, 5, 7, 10, 14].map(d => (
                        <option key={d} value={d}>
                          {d === 0 ? 'Day of the game' : `${d} day${d !== 1 ? 's' : ''} before`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Send at hour</label>
                    <select className="input-field mt-1"
                      value={form.spareAutoInviteHour}
                      onChange={e => setForm(p => ({ ...p, spareAutoInviteHour: e.target.value }))}>
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>
                          {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i-12}:00 PM`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="label" style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Send only if checked-in skater count is below
                    {/* v2.5.149 — verbose example moved into this
                        tooltip (coach asked to declutter the page).
                        Concise: what the number means + goalies note. */}
                    <FieldHelp anchor>
                      Spares only get emailed when fewer skaters than this have checked in. Goalies aren't counted.
                    </FieldHelp>
                  </label>
                  <input type="number" min="1" max="50" className="input-field mt-1"
                    style={{ maxWidth: 120 }}
                    value={form.spareAutoInviteThreshold}
                    onChange={e => setForm(p => ({ ...p, spareAutoInviteThreshold: e.target.value }))} />
                </div>
                {/* May 4 2026 — option to include or exclude goalie spares
                    in this single-blast auto-invite. Default ON for back-
                    compat. Coaches who use the separate "Auto-invite Goalie
                    Spares when Full-Time Goalies don't respond" pipeline
                    flip this OFF so goalie spares don't get a duplicate
                    email from the skater-blast. ── */}
                {/* v2.5.97 — coach feedback (item 15): "In the spare and
                    waitlist management look for the include goalie spares
                    auto invite email underneath the auto invite spares.
                    It's a duplicate to the goalie management section.
                    Just remove it from this section as we have it in the
                    goalie management section already." Removed the
                    duplicated toggle here. The single source of truth
                    for goalie-spare auto-invite is now the toggle in the
                    Goalie Management sub-section below (form key
                    `spareAutoInviteIncludeGoalies` continues to be
                    written from there). Coaches who previously toggled
                    this here will see the existing value reflected in
                    the Goalie Management section after deploy. */}
              </ToggleSetting>

              {/* ── Spare Cascade by List (App_Change_List #43, #44) ──
                  Gated on spareAutoInviteEnabled — when auto-invite is
                  off, this UI is hidden entirely. Per coach feedback,
                  cascading is the per-wave delivery model on top of the
                  auto-invite engine; surfacing it without the engine
                  was confusing.

                  Renamed from "Cascade Spares by Rank" → "Cascade Spares
                  by List": coaches now build named lists with either
                  rank-tier OR specific-spare selection. Up to 3 lists.
                  When list-mode is configured (non-empty list array),
                  the scheduler uses it; when empty, falls back to the
                  legacy rating-tier cascade (still controlled by the
                  `spareInviteCascadeEnabled` toggle + hours field). */}
              {form.spareAutoInviteEnabled && (
                <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                  <ToggleSetting
                    emoji="🎯"
                    title="Cascade Spares by List"
                    shortDesc="Invite spares in waves — by named list or rating tier."
                    longDesc="Build up to 3 named waves. Each wave can target specific spares (a hand-picked group) OR an entire rating tier. Each wave fires after a configurable delay since the previous wave. When no lists are configured below, falls back to a single by-rank cascade with the hours field as the wave interval."
                    tooltip="Lists fire in order. The first list fires immediately when the auto-invite trigger conditions are met; later lists wait their configured hours. Rating-tier waves include unrated spares on the Beginner (5) tier so they aren't silently skipped."
                    value={form.spareInviteCascadeEnabled}
                    onChange={(next) => setForm(p => ({ ...p, spareInviteCascadeEnabled: next }))}>
                    {/* Primary UI — list builder. */}
                    <CascadeListEditor
                      pool="spare"
                      lists={form.spareInviteLists || []}
                      onChange={(next) => setForm(p => ({ ...p, spareInviteLists: next }))}
                      candidates={cascadeCandidates.filter(c => c.position !== 'goalie')}
                      candidatesLoading={cascadeCandidatesLoading}
                      onLoadCandidates={loadCascadeCandidates}
                    />

                    {/* Fallback — when no lists are configured, the
                        scheduler uses the simple "all spares by rating
                        tier, N hours apart" cascade. The hours field
                        below is the wave interval for that fallback;
                        we keep it accessible (collapsed) so coaches
                        upgrading to list-mode don't lose their old
                        setting and coaches who want simple-mode can
                        still tune it. */}
                    {(form.spareInviteLists || []).length === 0 && (
                      <div className="mt-3 pt-3"
                        style={{ borderTop: '1px dashed var(--border)' }}>
                        <p className="text-xs mb-1.5" style={{ color: 'var(--text-2)' }}>
                          <strong>Default Mode</strong> — no lists configured. The
                          scheduler falls back to inviting spares by rating tier,
                          waiting this many hours between waves:
                        </p>
                        <input type="number" min="1" max="48"
                          className="input-field"
                          style={{ maxWidth: 140 }}
                          value={form.spareInviteCascadeHours ?? 4}
                          onChange={e => setForm(p => ({ ...p, spareInviteCascadeHours: parseInt(e.target.value) || 4 }))} />
                      </div>
                    )}
                  </ToggleSetting>
                </div>
              )}

              <div className="mt-2">
                {/* May 4 2026 — Goalie Management sub-section header.
                    Distinct from Spare Management above; goalie supply
                    is a separate pool with its own auto-invite +
                    cascade pipeline. ── */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '12px 0 6px',
                  borderBottom: '1px solid var(--border)',
                  marginBottom: 8,
                  marginTop: 8,
                }}>
                  <span style={{ fontSize: 16 }}>🥅</span>
                  <h3 style={{
                    fontSize: 13, fontWeight: 800,
                    color: 'var(--text)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    margin: 0,
                  }}>
                    Goalie Management
                  </h3>
                </div>

                {/* v2.5.97 — coach feedback (item 16): "Move the auto
                    invite goalies a full-time goalie checks out to the
                    top of the list as the first option in the Goalie
                    management section." Promoted from a nested child
                    of the scheduled-blast toggle into a sibling here
                    at the top. The two are now independent — coaches
                    who only want last-minute checkout coverage can
                    enable just this one without committing to a
                    scheduled spare-goalie blast. The settings keys
                    `goalie_auto_invite_on_checkout_enabled` and
                    `goalie_auto_invite_on_checkout_delay_hours` are
                    unchanged so existing data carries over. */}
                <ToggleSetting
                  emoji="🚪"
                  title="Auto-invite spare goalies if a Full-Time goalie checks OUT"
                  shortDesc="Last-minute bail trigger. Threshold below still applies."
                  longDesc="When an FT goalie was checked in and then changes their RSVP to 'out,' fire the goalie-spare blast immediately or after a delay. Useful for last-minute bails. The threshold from the scheduled blast below still applies — if another FT goalie is still in, no email goes out."
                  tooltip="A delay gives the FT goalie a chance to change their mind before the spare blast fires. If they re-check-in during the delay, the blast is suppressed (threshold check at send time)."
                  accentColor="#ff9500"
                  value={form.goalieAutoInviteOnCheckoutEnabled === true}
                  onChange={(next) => setForm(p => ({ ...p, goalieAutoInviteOnCheckoutEnabled: next }))}>
                  <div>
                    <label className="label">Delay before sending</label>
                    <select className="input-field mt-1"
                      style={{ maxWidth: 240 }}
                      value={form.goalieAutoInviteOnCheckoutDelayHours ?? 0}
                      onChange={e => setForm(p => ({ ...p, goalieAutoInviteOnCheckoutDelayHours: parseInt(e.target.value, 10) || 0 }))}>
                      <option value={0}>Send immediately</option>
                      <option value={1}>Wait 1 hour</option>
                      <option value={2}>Wait 2 hours</option>
                      <option value={4}>Wait 4 hours</option>
                      <option value={8}>Wait 8 hours</option>
                      <option value={12}>Wait 12 hours</option>
                      <option value={24}>Wait 24 hours</option>
                    </select>
                    <FieldHelp>
                      A delay gives the FT goalie a chance to change their mind
                      before the spare blast fires. If they re-check-in during the
                      delay, the blast is suppressed (threshold check at send time).
                    </FieldHelp>
                  </div>
                </ToggleSetting>

                <ToggleSetting
                  emoji="🥅"
                  title="Auto-invite Goalie Spares when Full-Time Goalies don't respond"
                  shortDesc="Email spare goalies on a schedule when goalies are short."
                  longDesc="Same idea as auto-invite for skaters, but for goalies. Fires a chosen number of days before the game at a chosen time — only if fewer than the threshold number of goalies are checked in. Use a small threshold (most teams have 1-2 full-time goalies) so the invite goes out anytime you're short a goalie."
                  tooltip="Goalies are a different supply pool than skaters; this lets you blast goalies separately without spamming your skater spares."
                  value={form.goalieAutoInviteEnabled}
                  onChange={(next) => setForm(p => ({ ...p, goalieAutoInviteEnabled: next }))}>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label">Days before game</label>
                      <select className="input-field mt-1"
                        value={form.goalieAutoInviteDaysBefore}
                        onChange={e => setForm(p => ({ ...p, goalieAutoInviteDaysBefore: e.target.value }))}>
                        {[0, 1, 2, 3, 4, 5, 7, 10, 14].map(d => (
                          <option key={d} value={d}>
                            {d === 0 ? 'Day of the game' : `${d} day${d !== 1 ? 's' : ''} before`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="label">Send at hour</label>
                      <select className="input-field mt-1"
                        value={form.goalieAutoInviteHour}
                        onChange={e => setForm(p => ({ ...p, goalieAutoInviteHour: e.target.value }))}>
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>
                            {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i-12}:00 PM`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="label">Send only if checked-in goalie count is below</label>
                    <input type="number" min="1" max="10" className="input-field mt-1"
                      style={{ maxWidth: 120 }}
                      value={form.goalieAutoInviteThreshold}
                      onChange={e => setForm(p => ({ ...p, goalieAutoInviteThreshold: e.target.value }))} />
                    <FieldHelp>
                      Most teams set this to <strong>2</strong> — when only
                      1 or 0 goalies are checked in, send the goalie-spare
                      blast. Set to <strong>1</strong> if you only need one
                      goalie per game.
                    </FieldHelp>
                  </div>

                  {/* v2.5.x — the "include goalie spares in the skater
                      spare-invite" toggle was removed. The auto-invite
                      spares blast is now SKATERS ONLY by design; goalie
                      spares come exclusively from the goalie flows above,
                      so the toggle no longer does anything. */}
                </ToggleSetting>
              </div>

              {/* ── Goalie Cascade by List (App_Change_List #45) ──
                  Same model as the spare cascade above, applied to
                  the goalie pool. Gated on goalieAutoInviteEnabled. */}
              {form.goalieAutoInviteEnabled && (
                <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                  <ToggleSetting
                    emoji="🥅"
                    title="Cascade Goalies by List"
                    shortDesc="Invite goalie spares in waves — by named list or rating tier."
                    longDesc="Build up to 3 named waves of goalie spares. Each wave fires after a configurable delay since the previous wave. Goalie pools are usually small, so a single list with a longer delay between rating tiers is often plenty."
                    tooltip="Lists fire in order. The first list fires immediately when the goalie auto-invite trigger conditions are met. Goalies tend to need more notice than skaters — default fallback is 8 hours between rating-tier waves."
                    value={form.goalieInviteCascadeEnabled}
                    onChange={(next) => setForm(p => ({ ...p, goalieInviteCascadeEnabled: next }))}>
                    <CascadeListEditor
                      pool="goalie"
                      lists={form.goalieInviteLists || []}
                      onChange={(next) => setForm(p => ({ ...p, goalieInviteLists: next }))}
                      candidates={cascadeCandidates.filter(c => c.position === 'goalie')}
                      candidatesLoading={cascadeCandidatesLoading}
                      onLoadCandidates={loadCascadeCandidates}
                    />

                    {(form.goalieInviteLists || []).length === 0 && (
                      <div className="mt-3 pt-3"
                        style={{ borderTop: '1px dashed var(--border)' }}>
                        <p className="text-xs mb-1.5" style={{ color: 'var(--text-2)' }}>
                          <strong>Default Mode</strong> — no lists configured. Falls
                          back to inviting goalies by rating tier, waiting this
                          many hours between waves:
                        </p>
                        <input type="number" min="1" max="72"
                          className="input-field"
                          style={{ maxWidth: 140 }}
                          value={form.goalieInviteCascadeHours ?? 8}
                          onChange={e => setForm(p => ({ ...p, goalieInviteCascadeHours: parseInt(e.target.value) || 8 }))} />
                      </div>
                    )}
                  </ToggleSetting>
                </div>
              )}

              {/* ── 3) Spare check-in behaviour ─────────────────
                  May 4 2026 (v2.5.69) — coach feedback: this block was
                  rendering after Goalie Management, which made it look
                  like it belonged to the goalie management group. Moved
                  the auto-waitlist + sticky-promotion + auto-release
                  toggles down to the Waitlist Management sub-card where
                  they logically belong (those toggles control HOW
                  spares interact with the waitlist on check-in, which
                  is exactly the topic of that card). The "How a re-
                  promoted spare's check-in is handled" radios travel
                  with auto-waitlist since they're its child. ── */}
            {/* end of consolidated spare-management content */}
            </div>
          </div>
        )}

        {/* ── Waitlist Management sub-page (Apr 2026) ──
            Extracted from inside the Spare Reminders card per coach
            request. Settings still bind to the same form keys
            (spareAutoWaitlist, sparePromotionSticky, spareAutoRelease,
            waitlist_auto_promote_below_*, low_attendance_alert_*,
            spareInviteCascade*, goalieInviteCascade*) so a coach
            navigating between Spare Reminders and Waitlist Management
            sees consistent values without re-loading. The visual
            subheading that used to live inline is replaced by the
            card header here. */}
        {/* Apr 30 2026 — what was a separate "Waitlist Management"
            card is now a sub-card under the consolidated Spare &
            Waitlist Management section. Cards stack visually; a
            sub-header makes the topical break clear without using
            another full-size H2. */}
        {activeSection === 'waitlist' && canEdit && (
          <div className="card">
            <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
              <span style={{ color: 'var(--ice)' }}>⏳</span> Waitlist Management
            </h2>
            <p className="text-secondary text-sm mb-4">
              How the waitlist behaves when players bail or attendance dips
              below your minimum.
            </p>

            {/* May 4 2026 (v2.5.69) — Spare check-in behaviour. Was
                rendering above this card under the Goalie Management
                subsection (visually misleading — these toggles are
                about waitlist behaviour, not goalies). Moved here per
                coach feedback so the auto-waitlist + sticky-promotion
                + auto-release controls live in the same card as the
                rest of the waitlist behaviour. The previous orange
                banner that warned "this card has no effect when
                auto-waitlist is off" is removed because the toggle is
                now in this card — the coach can flip it inline. ── */}
            {/* v2.5.104 — coach feedback: "Automatically put Spares on
                waitlist when they check in should be a sub category of
                Allow spares to join the waitlist before spare invites
                are sent." Restructured: the "before invites" toggle is
                now the parent. The auto-waitlist + bypass-waitlist
                toggles render as children (only when parent is ON).
                When the parent is OFF, spares simply can't RSVP until
                the invite email fires — the children become
                irrelevant because spares can't check in to be put on
                a waitlist in the first place. Default ON preserves
                legacy behaviour for existing teams. ── */}
            <div className="space-y-3 mb-5">
              <ToggleSetting
                emoji="🎟"
                title="Allow spares to join the waitlist before spare invites are sent"
                shortDesc="When OFF, spares can't RSVP until the auto-invite email fires."
                longDesc="Controls whether spares can voluntarily check in before the team's spare-invite email goes out. ON (default): spares can RSVP any time, even days before the invite — useful for spares who already know they're free. OFF: spares are blocked from RSVPing until the spare-invite email actually fires for that game (relies on games.spare_request_sent_at). Useful for teams that want spare check-ins concentrated in a known window for fairness."
                tooltip="Doesn't affect full-time players — they can always RSVP regardless of this setting. The child toggles below control HOW spares behave when they do RSVP."
                accentColor="var(--ice)"
                value={form.waitlistAllowSparesBeforeInvite !== false}
                onChange={(next) => setForm(p => ({ ...p, waitlistAllowSparesBeforeInvite: next }))}>

                {/* ── Children: how spare check-ins are handled ── */}
                <div className="space-y-3 mt-2">
                  <ToggleSetting
                    emoji="⏳"
                    title="Always put Spares on waitlist when they check in"
                    shortDesc="Spares land on the waitlist for coach approval instead of joining directly."
                    longDesc="Spares who tap 'I'm In' are placed on the waitlist instead of directly joining the player list. The coach manually releases them when there's room — full-timers always get first dibs."
                    tooltip="Mutually exclusive with 'Allow Spares to bypass the waitlist when invited' below. Disabled when that one is on."
                    accentColor="#a78bfa"
                    /* v2.5.107 — bidirectional mutex. Coach feedback:
                       "when Allow Spares to bypass the waitlist when
                       invited is toggled on, then make Always put
                       Spares on waitlist when they check in non
                       clickable / not selectable until Allow Spares
                       to bypass the waitlist when invited is toggled
                       off." Previously the disable was one-way
                       (Bypass got disabled when this was on, but
                       not vice versa). Now both directions are
                       symmetric — whichever toggle is on, the other
                       is disabled. */
                    disabled={form.spareAutoRelease}
                    value={form.spareAutoWaitlist}
                    onChange={(next) => setForm(p => ({ ...p, spareAutoWaitlist: next, spareAutoRelease: false }))}>
                    {/* v2.5.104 — sticky-promotion radios hidden (form
                        key stays writeable via API; backend defaults
                        flipped to non-sticky). See git history at
                        this revision for the radio UI. */}
                  </ToggleSetting>

                  <ToggleSetting
                    emoji="✅"
                    title="Allow Spares to bypass the waitlist when invited"
                    shortDesc="Spares fill open spots immediately, same as full-time players."
                    longDesc="Spares fill open spots immediately, same as full-time players. They only hit the waitlist if the game is already at max capacity. Cannot be combined with auto-waitlist above."
                    tooltip="Mutually exclusive with 'Always put Spares on waitlist when they check in'. Disabled when that setting is on."
                    accentColor="var(--goal)"
                    disabled={form.spareAutoWaitlist}
                    value={form.spareAutoRelease}
                    /* v2.5.107 — defensively reset the sibling toggle
                       when this one is flipped ON, mirroring the
                       Always-put-on-waitlist onChange. Belt-and-
                       suspenders: the disabled gate above prevents
                       this state from being reachable via UI, but if
                       someone hits it via API or a stale form state
                       this keeps the persisted JSON sane. */
                    onChange={(next) => setForm(p => ({ ...p, spareAutoRelease: next, spareAutoWaitlist: next ? false : p.spareAutoWaitlist }))}
                  />

                  {!form.spareAutoWaitlist && !form.spareAutoRelease && (
                    <p className="text-xs text-secondary px-1">
                      With neither option selected, spares behave like full-time players — they fill spots up to the max,
                      then go to the waitlist. The coach can always manually release waitlisted spares from the game page.
                    </p>
                  )}
                </div>
              </ToggleSetting>
            </div>

            <div className="space-y-4">
              {/* ── Waitlist Auto-Management (Apr 2026) ──
                  Apr 2026 (Tom_Notes F9) — auto-release-before-game
                  toggle removed per user request. The remaining two
                  toggles (auto-promote-below and low-attendance
                  alert) cover the most common waitlist needs.
                  Cascading rank-based invites moved into their own
                  block below. */}

              {/* v2.5.104 — "Allow spares to join the waitlist before
                  spare invites are sent" promoted to the parent toggle
                  ABOVE (with auto-waitlist + bypass as its children).
                  The standalone toggle that used to live HERE has been
                  removed; the form key `waitlistAllowSparesBeforeInvite`
                  is unchanged and saves the same way. ── */}

              {/* May 4 2026 (v2.5.60) — moved here from the now-deleted
                  "Manual spare invitations & advanced" card. The toggle
                  is conditionally rendered only when both the auto-
                  waitlist and auto-invite parents are on, since it's
                  meaningless otherwise (controls behavior AFTER the
                  spare request email fires). ── */}
              {form.spareAutoWaitlist && form.spareAutoInviteEnabled && (
                <div className="mt-3">
                  <ToggleSetting
                    emoji="🚀"
                    title="After spare request sent, allow direct check-in (bypass waitlist)"
                    shortDesc="First-come-first-serve check-in up to the cap once invites are out."
                    longDesc="Once the spare invitation email has gone out, treat new spare RSVPs as first-come-first-serve up to the game max. Already-waitlisted spares are auto-promoted first (in waitlist order), then new check-ins go directly to confirmed. The player cap still applies — once full, additional check-ins land on the waitlist as usual."
                    tooltip="Only applies after the spare request email fires. Before that, spares still hit the waitlist as configured above."
                    accentColor="var(--goal)"
                    value={form.spareBypassWaitlistAfterRequest}
                    onChange={(next) => setForm(p => ({ ...p, spareBypassWaitlistAfterRequest: next }))}
                  />
                </div>
              )}

              {/* Auto-release toggle removed (Apr 2026, Tom_Notes F9).
                  The waitlist_auto_release_enabled / hours_before
                  fields are still saved as-is so existing teams with
                  this on don't lose state silently — they just can't
                  toggle it from the UI anymore. To actually disable
                  on the backend, set the flag false via the API. */}

              {/* 2) Auto-promote when count drops below threshold */}
              <div className="mt-3">
                <ToggleSetting
                  emoji="📈"
                  title="Auto-promote when count drops below"
                  shortDesc="Refill empty spots from the waitlist automatically."
                  longDesc="Whenever the checked-in count falls under the threshold (someone bails last-minute), automatically promote the next person on the waitlist. Re-fires for each drop until the waitlist is empty or count is back at threshold. Optionally restrict to a time window before game start."
                  tooltip="Promotion order respects waitlist priority (purple-top, then responded_at). Goalies count toward the threshold."
                  accentColor="var(--goal)"
                  value={form.waitlistAutoPromoteBelowEnabled}
                  onChange={(next) => setForm(p => ({ ...p, waitlistAutoPromoteBelowEnabled: next }))}>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Promote when count drops below</label>
                      <input type="number" min="1" max="50" className="input-field mt-1"
                        value={form.waitlistAutoPromoteBelowThreshold}
                        onChange={e => setForm(p => ({ ...p, waitlistAutoPromoteBelowThreshold: e.target.value }))} />
                      <FieldHelp>
                        Default is your team's "min players to play" value. Goalies count toward this number.
                      </FieldHelp>
                    </div>
                    {/* v2.5.104 — time-gate input. Coach feedback: this
                        toggle should have BOTH a min threshold of
                        players AND a timeframe requirement (e.g. only
                        auto-promote within 24 hours of game start).
                        Blank or 0 = no time gate (legacy behaviour). */}
                    <div>
                      <label className="label">Only within this many hours of game</label>
                      <input type="number" min="0" max="168" className="input-field mt-1"
                        placeholder="Leave blank = anytime"
                        value={form.waitlistAutoPromoteBelowHoursBefore ?? ''}
                        onChange={e => setForm(p => ({ ...p, waitlistAutoPromoteBelowHoursBefore: e.target.value === '' ? null : e.target.value }))} />
                      <FieldHelp>
                        Optional. Set to e.g. <strong>24</strong> or <strong>48</strong> to
                        only auto-promote in the final stretch — useful if you want spares
                        to mostly self-organize earlier in the week and only auto-fill
                        right before puck drop. Blank/0 = promote at any time.
                      </FieldHelp>
                    </div>
                  </div>
                </ToggleSetting>
              </div>

              {/* May 4 2026 (v2.5.69) — "Notify coaches when attendance
                  is low" toggle relocated to Chat & Notifications per
                  coach feedback. Conceptually it's a coach-notification
                  setting (alongside chat-notify-mode and the new
                  RSVP-checkout notification toggles), not a waitlist
                  setting. The form field `lowAttendanceAlertEnabled`
                  and its companions still bind to the same backend
                  settings keys — only the visual location changed. ── */}

              {/* ── Cascading rank-based invites (Apr 2026, Tom_Notes F9) ──
                  Coaches asked: when the player list is short, invite
                  spares in waves by rank — top-rated spares first,
                  then a few hours later the next tier, etc. Avoids a
                  free-for-all where all spares race for spots and
                  the bottom rank gets in just because they tap fast.

                  Same pattern for goalies (often only 1-2 slots, so
                  the wave gap is set independently).

                  This UI exposes the toggles + interval; backend cron
                  reads `spare_invite_cascade_*` settings to schedule
                  the actual sends. Default OFF — existing behaviour
                  (single blast to all spares) preserved. */}
              {/* Apr 30 2026 — Cascading invites by rank moved up to the
                  first card, directly under "Auto-invite Spares".
                  Cascading is the per-tier delivery mechanism for
                  the auto-invite, so they belong adjacent in the UI
                  and cascading is gated on auto-invite being on. */}
            </div>
          </div>
        )}
        {/* end activeSection === 'waitlist' */}

        {/* Apr 2026 — these two trailing controls (Spare Request Days
            Before + bypass-waitlist sub-toggle) are spare-related but
            originally lived after the waitlist subsection inside the
            Spare Reminders card. Re-opening the 'spares' guard so they
            keep appearing on the Spare Reminders sub-page. The wrapper
            card structure is recreated below. */}
        {/* Apr 30 2026 — third sub-card "Manual spare invitations &
            advanced" was removed in v2.5.60 per coach request: the
            "Spare Request Days Before Game" input was effectively
            duplicated by the modern "Auto-invite Spares when game
            looks short" toggle (Spare Management) and was confusing
            coaches who didn't know which was the source of truth.
            The bypass-waitlist sub-toggle that lived in this card
            was relocated to Waitlist Management above where it
            logically belongs (it controls waitlist behavior).

            BACKEND NOTE: spare_request_days_before still exists in
            teams.settings and the scheduler still honors it (defaults
            to 3 if missing). Existing teams keep their saved value
            silently round-tripping through GET/PATCH; new teams get
            the default. No coach-facing UI to change it anymore. */}

        {/* ── Create Teams card moved down (Apr 2026, Tom_Notes F12) ──
            Was originally rendered here, between Spare Reminders and
            Game Settings. Spec wants it AFTER Payment Collection
            (position #8) since it's about team-day workflow rather
            than team-level identity. The card moved; settings
            bindings unchanged. */}

        {/* ── Game Settings card moved up (Apr 2026, Tom_Notes F4) ──
            The card was originally rendered here. Coaches reported
            the player-cap and game-duration defaults were "buried"
            below the spare/payment sections — they wanted them near
            Team Info since that's where they set up a new team.
            The card now renders right after Team Color, just before
            Reminders. Same content + same form bindings. */}

        {activeSection === 'pricing' && canEdit && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-lg flex items-center gap-2">
                💳 Payment Collection
              </h2>
              {/* Master toggle */}
              <button
                type="button"
                onClick={() => setForm(p => {
                  const nextEnabled = !p.paymentEnabled;
                  if (nextEnabled) {
                    // Turning ON: default spares to "pay required" with
                    // pay_on_promote mode so the 2-option radio below
                    // renders a meaningful current selection instead of
                    // a ghost state. Coach can switch to pay_upfront
                    // or turn payments back off if they want something
                    // else. Won't clobber an existing sparePayMode that
                    // was already set.
                    return {
                      ...p,
                      paymentEnabled:   true,
                      sparePayRequired: true,
                      sparePayMode:     p.sparePayMode || 'pay_on_promote',
                      sparePaymentMode: 'none',
                    };
                  }
                  // Turning OFF: clear sparePayRequired so backend
                  // doesn't try to charge when Stripe's disabled. Leave
                  // sparePayMode alone in case the coach toggles back on.
                  return { ...p, paymentEnabled: false, sparePayRequired: false };
                })}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold transition-all"
                style={{
                  background: form.paymentEnabled ? 'var(--goal-a12)' : 'var(--surface-3)',
                  border: `1.5px solid ${form.paymentEnabled ? 'var(--goal)' : 'var(--border)'}`,
                  color: form.paymentEnabled ? 'var(--goal)' : 'var(--text-2)',
                }}>
                <span style={{
                  display: 'inline-block', width: 10, height: 10, borderRadius: 5,
                  background: form.paymentEnabled ? 'var(--goal)' : 'var(--text-3)',
                  boxShadow: form.paymentEnabled ? '0 0 6px var(--goal)' : 'none',
                }} />
                {form.paymentEnabled ? 'Payments ON' : 'Payments OFF'}
              </button>
            </div>

            {/* 2026-07-11 (batch-B G2) — one-time inline prompt: once
                payments are ON, nudge the coach toward the Stripe Connect
                section (renamed from "Payouts" — see teamSettingsSubNav.js)
                since payments won't actually collect anything until a
                Stripe account is connected there. "One-time" = persisted
                in localStorage (see stripeNudgeDismissed above), not
                re-shown every visit once acknowledged. Doesn't duplicate
                the (separate, always-on) "Payments are off" banner that
                already lives on the Stripe section itself — this one only
                shows here, in Pricing, right after the toggle flips on. */}
            {form.paymentEnabled && !stripeNudgeDismissed && (
              <div className="p-3 rounded-xl mb-4 flex items-start gap-3 flex-wrap" style={{
                background: 'rgba(0,212,255,0.08)', border: '1.5px solid rgba(0,212,255,0.3)',
              }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <p className="text-sm font-semibold mb-1" style={{ color: 'var(--ice)' }}>
                    💳 Payments need a Stripe Connect account
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-2)', lineHeight: 1.5 }}>
                    Set it up in Stripe so collected fees actually land in your bank.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { dismissStripeNudge(); navigate(`/team/${teamId}/settings/payouts`); }}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg"
                    style={{ background: 'var(--ice)', color: '#001018', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    Set up in Stripe →
                  </button>
                  <button
                    type="button"
                    onClick={dismissStripeNudge}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg"
                    style={{ background: 'transparent', color: 'var(--text-2)', border: '1px solid var(--border)', cursor: 'pointer' }}>
                    Got it
                  </button>
                </div>
              </div>
            )}

            {/* Apr 2026 — legacy-state warning. Teams saved under the
                retired "Spare Payment Tracking" card may have
                spare_payment_mode='track_and_invoice' while ALSO
                having Payments turned on. The two flows are
                mutually exclusive: Stripe charging wants
                spare_payment_mode='none'. Surface this so the coach
                knows why "pay on promote" isn't firing. Saving the
                form (backend normalizes on save) resolves it. */}
            {form.paymentEnabled && form.sparePaymentMode === 'track_and_invoice' && (
              <div className="p-3 rounded-xl mb-4 text-sm"
                style={{ background: 'rgba(255,193,7,0.12)', border: '1px solid rgba(255,193,7,0.35)', color: '#8a6a00' }}>
                <p className="font-bold mb-1">⚠ Legacy setting detected</p>
                <p className="text-xs">
                  This team was previously set to "Track & invoice at season end" (an older mode).
                  Save your settings below to clear the legacy flag — otherwise spares will see the
                  track-and-invoice notice instead of being prompted to pay via Stripe.
                </p>
              </div>
            )}

            {!form.paymentEnabled && (
              <div className="space-y-3">
                <p className="text-sm text-secondary">
                  Enable to collect fees from spares and/or full-time players via Stripe.
                </p>

                {/* ── Spare Fees (Per Game) ──────────────────────────
                    v2.5.104 — coach feedback: "Can you make Spare costs
                    consistent from a cosmetic and optics / presentation
                    view with 'Season Fees'. Give them the same headers,
                    give them their own groups. Right now 'Spare Costs'
                    are blue and a different font / size than 'Season
                    Fees'. Make it more consistent."
                    Old style: small uppercase blue subheading
                    (text-xs, letterSpacing 0.12em, color var(--ice)).
                    New style: matches Season Fees below — h3 with
                    text-base font-bold + descriptive paragraph in
                    text-xs text-secondary. The two pricing groups now
                    read as parallel sections rather than one
                    looking like a sub-heading and the other a real
                    heading. ── */}
                <div className="pt-4 mt-1" style={{ borderTop: '1px solid var(--border)' }}>
                  <h3 className="text-base font-bold mb-1" style={{ color: 'var(--text)' }}>
                    Spare Fees (Per Game)
                  </h3>
                  <p className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
                    Track what each spare owes for filling in. Use even when payment collection is
                    off — handy for end-of-season invoicing or manual e-transfer reconciliation.
                  </p>
                </div>

                {/* Spare fee — always visible even when Stripe collection is off.
                    Coaches using "Track & Invoice at end of season" still need a
                    recorded spare fee so the app can tally what each spare owes. ── */}
                <div
                  className="p-4 rounded-xl"
                  style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                  <label className="label">Spare Fee (for tracking)</label>
                  <div style={{ position: 'relative', maxWidth: 220 }}>
                    <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-2)', fontWeight: 600 }}>$</span>
                    <input type="number" min="0" step="0.50" className="input-field"
                      style={{ paddingLeft: 24 }}
                      placeholder="0.00"
                      value={form.spareFee}
                      onChange={e => setForm(p => ({ ...p, spareFee: e.target.value }))} />
                  </div>
                  <p className="text-xs text-secondary mt-2" style={{ lineHeight: 1.5 }}>
                    Record the spare fee even when Stripe collection is off.
                    Useful when you're tracking what each spare owes for end-of-season invoicing,
                    or charging them manually via e-transfer.
                  </p>

                  {/* Jul 10 2026 (H1) — the Goalie Spare Fee input that
                      lived here MOVED to the dedicated "🥅 Goalie Fees
                      (Per Game)" section right below this card, together
                      with the "Goalies play free" toggle (which wasn't
                      reachable at all in tracking mode before — it only
                      rendered in the Stripe branch). Same settings keys
                      (spare_goalie_fee_cents / spare_goalies_free), just
                      relocated so goalie pricing reads as its own group. */}

                  {/* Apr 2026 — show fee in notifications when payments are off.
                      When a spare is promoted from the waitlist or confirmed for
                      a game, the emailed notification explicitly states what the
                      game will cost them (e-transfer amount). Helps coaches who
                      collect offline but still want the spare to know the price. */}
                  {form.spareFee && parseFloat(form.spareFee) > 0 && (
                    <div className="mt-3">
                      <ToggleSetting
                        emoji="💰"
                        title="Show fee in spare confirmation emails"
                        shortDesc={`Mention the $${parseFloat(form.spareFee).toFixed(2)} fee in confirmation/promotion emails.`}
                        longDesc={`When a spare is confirmed for a game (or promoted from waitlist), the email mentions the $${parseFloat(form.spareFee).toFixed(2)} fee so they know the cost. Useful if you collect via e-transfer or cash.`}
                        tooltip="Useful if you collect spare fees offline (e-transfer, cash, or Venmo) and want every spare to know the price up front."
                        value={form.spareFeeShowInNotifications}
                        onChange={(next) => setForm(p => ({ ...p, spareFeeShowInNotifications: next }))}
                      />
                    </div>
                  )}
                </div>

                {/* ── Goalie Fees (Per Game) — Jul 10 2026 (H1) ──────
                    Goalie pricing gets its own section, parallel to
                    Spare Fees above (same h3 + description pattern
                    from v2.5.104). Previously the goalie fee input was
                    buried inside the Spare Fees card and the "Goalies
                    play free" toggle didn't render in tracking mode at
                    all (Stripe branch only) — so a tracking-mode coach
                    couldn't flip it. Both controls live here now; the
                    settings keys are unchanged (spare_goalies_free +
                    spare_goalie_fee_cents) and flow through the same
                    PUT settings payload as before. */}
                <div className="pt-4 mt-1" style={{ borderTop: '1px solid var(--border)' }}>
                  <h3 className="text-base font-bold mb-1" style={{ color: 'var(--text)' }}>
                    🥅 Goalie Fees (Per Game)
                  </h3>
                  <p className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
                    What spare goalies owe for filling in. Most rec teams let goalies
                    play free — turn that off to track the skater fee (or a
                    goalie-specific rate) for goalies too.
                  </p>
                </div>

                <div
                  className="p-4 rounded-xl"
                  style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                  <ToggleSetting
                    emoji="🥅"
                    title="Goalies play free"
                    shortDesc="Spare goalies owe nothing for filling in."
                    longDesc="Spare goalies owe nothing. Most coaches keep this on — having a goalie show up is usually worth more than the spare fee. Turn off to track a fee for goalie spares too."
                    tooltip="Default ON. Goalies are scarce in most rec leagues, so most teams comp the goalie fee to keep the goalie pool happy."
                    accentColor="var(--goal)"
                    value={form.spareGoaliesFree !== false}
                    onChange={(next) => setForm(p => ({
                      ...p,
                      spareGoaliesFree: next,
                      // H1 — turning "free" ON clears any goalie fee in the
                      // same update. Otherwise the save would send fee>0 +
                      // free=true and the backend conflict resolver
                      // (teams.js PUT) would flip "free" straight back OFF
                      // and toast about it — confusing when the coach just
                      // turned it on. The coach's latest action wins.
                      spareGoalieFee: next ? '' : p.spareGoalieFee,
                    }))}
                  />

                  {/* Goalie fee — visible only when goalies are NOT free
                      (rule kept from v2.5.69: a fee is a no-op while the
                      toggle is on, so the toggle hides the input). The
                      old type-a-fee-unchecks-the-toggle handler was
                      dropped: it was already dead code once the input
                      became hidden while "free" is on (spareGoaliesFree
                      is always false when this renders). The backend
                      resolver still backstops any stale fee+free combo. */}
                  {!form.spareGoaliesFree && (
                    <div className="mt-4">
                      <label className="label">Goalie Spare Fee <span className="opacity-50 text-xs">(optional — leave blank to use skater fee)</span></label>
                      <div style={{ position: 'relative', maxWidth: 220 }}>
                        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-2)', fontWeight: 600 }}>$</span>
                        <input type="number" min="0" step="0.50" className="input-field"
                          style={{ paddingLeft: 24 }}
                          placeholder="0.00"
                          value={form.spareGoalieFee}
                          onChange={e => setForm(p => ({ ...p, spareGoalieFee: e.target.value }))} />
                      </div>
                      <p className="text-xs text-secondary mt-2" style={{ lineHeight: 1.5 }}>
                        Track a different amount for goalies than skaters. Leave blank
                        to use the regular spare fee for goalies too.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {form.paymentEnabled && (
              <div className="space-y-4">
                {/* Currency */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Currency</label>
                    <select className="input-field" value={form.paymentCurrency}
                      onChange={e => setForm(p => ({ ...p, paymentCurrency: e.target.value }))}>
                      <option value="cad">CAD — Canadian Dollar</option>
                      <option value="usd">USD — US Dollar</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">Payment Type</label>
                    <select className="input-field" value={form.paymentType}
                      onChange={e => setForm(p => ({ ...p, paymentType: e.target.value }))}>
                      <option value="per_game">Per Game</option>
                      <option value="season">Season (one-time)</option>
                    </select>
                  </div>
                </div>

                {/* Apr 2026: per-game/season "member fee" input removed
                    per coach feedback — confusing overlap with the dedicated
                    Season Fees section below. For per-game fees, use the
                    Spare Fee (which applies when a spare RSVPs in). For
                    season fees, use the "Season Fees (Full-Time Players)"
                    section below, which has its own processing-fee toggle
                    and round-up option. */}
                {/* ── Spare Fees (Per Game) — paymentEnabled branch ──
                    v2.5.104 — matches the !paymentEnabled branch
                    above and parallels Season Fees below. */}
                <div className="pt-4 mt-1" style={{ borderTop: '1px solid var(--border)' }}>
                  <h3 className="text-base font-bold mb-1" style={{ color: 'var(--text)' }}>
                    Spare Fees (Per Game)
                  </h3>
                  <p className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
                    Charged to spares when they join a game. Collected via Stripe (the platform
                    plus payment processing fees apply — see the Spare Fee Display Options below).
                  </p>
                </div>

                <div>
                  <label className="label">Spare Fee</label>
                  <div style={{ position: 'relative', maxWidth: 220 }}>
                    <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-2)', fontWeight: 600 }}>$</span>
                    <input type="number" min="0" step="0.50" className="input-field"
                      style={{ paddingLeft: 24 }}
                      placeholder="0.00"
                      value={form.spareFee}
                      onChange={e => setForm(p => ({ ...p, spareFee: e.target.value }))} />
                  </div>
                  <p className="text-xs text-secondary mt-1">Charged to spares when they join a game</p>
                </div>

                {/* Jul 10 2026 (H1) — the Goalie Spare Fee input that
                    rendered here MOVED to the dedicated "🥅 Goalie Fees
                    (Per Game)" section below (between Spare Fees and
                    Season Fees), together with the "Goalies play free"
                    toggle. Same settings keys, just regrouped. */}

                {/* v2.5.76: replaced 2-way radio with two independent
                    ToggleSettings to maintain UI consistency with
                    every other team setting (per coach feedback).
                    Tooltip carries the description so the row stays
                    compact. Mutex behaviour:
                      • Both can be OFF — equivalent to "no payment
                        required for spares" (back-compat with
                        sparePayRequired=false).
                      • Turning one ON automatically turns the other
                        OFF.
                      • Both ON is impossible by construction.
                    Internal mapping to the pre-existing
                    (sparePayRequired, sparePayMode) tuple:
                      • Pay-on-promote ON → required=true, mode='pay_on_promote'
                      • Pay-upfront ON   → required=true, mode='pay_upfront'
                      • Both OFF         → required=false, mode kept
                                           as last value so backend
                                           validation accepts the form. */}
                {form.spareFee && parseFloat(form.spareFee) > 0 && (() => {
                  const isOnPromote = form.sparePayRequired && form.sparePayMode !== 'pay_upfront';
                  const isUpfront   = form.sparePayRequired && form.sparePayMode === 'pay_upfront';

                  const setOnPromote = (next) => {
                    setForm(p => next
                      // Turning ON: enable, set mode, clear other.
                      ? { ...p, sparePayRequired: true,  sparePayMode: 'pay_on_promote', sparePaymentMode: 'none' }
                      // Turning OFF: disable both. Mode kept at
                      // 'pay_on_promote' so back-end validation
                      // accepts the form (mode column is enum-like).
                      : { ...p, sparePayRequired: false, sparePayMode: 'pay_on_promote' });
                  };
                  const setUpfront = (next) => {
                    setForm(p => next
                      ? { ...p, sparePayRequired: true,  sparePayMode: 'pay_upfront', sparePaymentMode: 'none' }
                      : { ...p, sparePayRequired: false, sparePayMode: 'pay_on_promote' });
                  };
                  // v2.5.149 — setUpfront retained for when the upfront
                  // toggle is re-enabled (see hidden block below). Void
                  // reference keeps strict no-unused-vars linters quiet
                  // without deleting the handler.
                  void setUpfront;

                  return (
                    <div>
                      <label className="label mb-2">When do spares pay?</label>
                      <div className="space-y-2">
                        <ToggleSetting
                          title="Pay once promoted off the waitlist"
                          tooltip='Spares join the waitlist at no charge. They&apos;re charged only when you promote them, when you open a first-come-first-pay window, or when "bypass waitlist after spare request" is enabled.'
                          value={isOnPromote}
                          onChange={setOnPromote}
                          accentColor="var(--ice)"
                        />
                        {/* v2.5.149 — "Pay upfront to check in" hidden
                            for now (coach: "this may not be used").
                            The toggle + its setUpfront handler are kept
                            in code so the feature can be re-enabled by
                            un-commenting; we don't rip out the backend
                            (spare_pay_mode='pay_upfront' still works if
                            set directly). If a team is somehow already
                            on upfront mode, surface a small note + a
                            one-tap switch back so they aren't stuck
                            with a hidden control. */}
                        {isUpfront && (
                          <div className="p-2 rounded text-xs" style={{ background: 'rgba(255,193,7,0.08)', border: '1px solid rgba(255,193,7,0.30)', color: 'var(--text-2)' }}>
                            This team is set to <strong>pay upfront to check in</strong>. That option is being phased out.{' '}
                            <button type="button"
                              onClick={() => setOnPromote(true)}
                              style={{ background: 'none', border: 'none', padding: 0, color: 'var(--ice)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                              Switch to pay-on-promote
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Fee handling — only relevant when spare payments are actually required.
                    SOURCE-OF-TRUTH:pricing.js — the math below duplicates computeSpareCharge()
                    from backend/src/utils/pricing.js for live preview as the coach types.
                    If you change the math, change it in both places.
                    
                    Apr 2026 simplification: the previous radio-pair
                    ("Pass portion" / "Absorb") was replaced with two
                    checkboxes that read more naturally:
                      ☐ Add processing fee on top of spare fee
                      ☐ Round up to nearest $0.25
                    Default values preserved (pass fee on, round up on)
                    so existing teams behave identically without resaving. */}
                {/* Jul 10 2026 (H1) — the "Goalies play free" toggle
                    (formerly here, gated on sparePayRequired) and the
                    spare-fee-but-goalies-free heads-up note MOVED to
                    the dedicated "🥅 Goalie Fees (Per Game)" section
                    below (between Spare Fees and Season Fees), so all
                    goalie pricing lives in one place. */}

                {form.sparePayRequired && form.spareFee && parseFloat(form.spareFee) > 0 && (() => {
                  const platformFeeCents = team?.platformFeeCents ?? 100;
                  const baseCents = Math.round(parseFloat(form.spareFee || '0') * 100);
                  const passFee   = form.sparePassPlatformFee !== false;
                  const roundUp   = form.spareRoundUpQuarters !== false;

                  // Stripe standard rate: 2.9% + 30¢ per successful card payment.
                  const STRIPE_PCT       = 0.029;
                  const STRIPE_FIXED_C   = 30;

                  // Compute the spare's charge. When passFee=true, we
                  // gross up so that AFTER Stripe deducts its fee AND
                  // we take the platform fee, the coach still nets the
                  // base amount. Formula:
                  //   net = charge - (charge * stripe_pct + stripe_fixed) - platform
                  //   charge = (net + stripe_fixed + platform) / (1 - stripe_pct)
                  // Without this, the coach would be short by the
                  // Stripe processing fee (~$0.88 on a $20 charge).
                  //
                  // Apr 2026 fix: previously we added just the platform
                  // fee on top ($1.00 surcharge), but told the coach the
                  // blended processing fee was ~$1.91. Spare was charged
                  // $21.00 but coach only netted $19.12 after Stripe.
                  // Now the math passes the real processing cost on.
                  let spareCents;
                  if (passFee) {
                    spareCents = Math.ceil(
                      (baseCents + STRIPE_FIXED_C + platformFeeCents) / (1 - STRIPE_PCT)
                    );
                  } else {
                    spareCents = baseCents;
                  }
                  if (roundUp) spareCents = Math.ceil(spareCents / 25) * 25;

                  // Blended processing fee = Stripe's cut on the final
                  // charge + the platform fee. Shown as a single dollar
                  // amount so the coach doesn't see the Stripe vs
                  // platform split.
                  // 2026-09-22 — Math.round, not ceil. Stripe rounds its
                  // percentage to the nearest cent, and the Finance page
                  // computes it the same way (ROUND(amount*0.029)+30). With
                  // ceil the preview said $1.83 on a $16 fee while the real
                  // ledger row showed $1.82 — the only place they disagreed.
                  const stripeFeeCents  = Math.round(spareCents * STRIPE_PCT) + STRIPE_FIXED_C;
                  const blendedFeeCents = stripeFeeCents + platformFeeCents;

                  // The "surcharge" shown to spare = blended processing
                  // fee (or the difference between charge and base when
                  // rounded up). This matches what the coach sees.
                  const surchargeCents = Math.max(0, spareCents - baseCents);

                  const fmt = (c) => `$${(c / 100).toFixed(2)}`;

                  return (
                    <div style={{
                      background: 'var(--surface-2)',
                      border: `1px solid var(--border)`,
                      borderRadius: 12,
                      padding: 16,
                    }}>
                      <p className="text-sm font-semibold mb-1">Processing fee handling</p>
                      <p className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
                        Processing fee ≈ <strong style={{ color: 'var(--text)' }}>{fmt(blendedFeeCents)}</strong> on
                        a {fmt(baseCents)} spare fee. Choose whether you want
                        to absorb that cost yourself or pass it on to the spare.
                      </p>

                      {/* Toggle 1 — add processing fee on top */}
                      <ToggleSetting
                        title="Charge spare fee + processing fee"
                        shortDesc={passFee
                          ? `Spare pays ${fmt(spareCents)} (${fmt(baseCents)} + processing). You net close to ${fmt(baseCents)}.`
                          : `Spare pays the flat ${fmt(baseCents)}. Processing fee comes out of your payout.`}
                        tooltip="ON: spare covers Stripe + platform fees; you receive close to the base amount. OFF: you absorb the fees and net less than the base."
                        value={passFee}
                        onChange={() => setForm(p => ({ ...p, sparePassPlatformFee: !passFee }))}
                      />

                      {/* Round up — only useful when passing the fee through;
                          when absorbing, the spare's total is already the clean
                          base amount. */}
                      {passFee && (
                        <div className="mt-2">
                          <ToggleSetting
                            title="Round spare total up to nearest $0.25"
                            shortDesc="Cleaner totals (e.g. $24.00 instead of $23.94). Extra cents go to you."
                            tooltip="Strictly cosmetic: rounds the spare's payment up to the nearest quarter-dollar. The extra few cents are added to your net."
                            value={roundUp}
                            onChange={() => setForm(p => ({ ...p, spareRoundUpQuarters: !roundUp }))}
                          />
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── Goalie Fees (Per Game) — Jul 10 2026 (H1) ────────────────────
                    Goalie pricing gets its own section between Spare Fees and
                    Season Fees (same h3 + description pattern as both). The two
                    controls MOVED here from mid-flow above: the goalie fee input
                    (was inside Spare Fees) and the "Goalies play free" toggle
                    (was below "When do spares pay?", gated on sparePayRequired).
                    Settings keys unchanged — spare_goalies_free and
                    spare_goalie_fee_cents flow through the same PUT settings
                    payload, and the backend conflict resolver in teams.js
                    (+ the goalieFeeConflict follow-up toast in saveTeam) still
                    backstop any fee-vs-free contradiction. */}
                <div className="mt-6 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                  <h3 className="text-base font-bold mb-1" style={{ color: 'var(--text)' }}>
                    🥅 Goalie Fees (Per Game)
                  </h3>
                  <p className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
                    What spare goalies are charged when they join a game. Most rec teams
                    let goalies play free — turn that off to charge goalies the skater
                    fee, or set a goalie-specific rate.
                  </p>

                  <ToggleSetting
                    emoji="🥅"
                    title="Goalies play free"
                    shortDesc="Spare goalies skip payment and check in directly."
                    longDesc="Spare goalies skip the payment step and check in directly. Most coaches keep this on — having a goalie show up is usually worth more than the spare fee. Turn off if your goalies are also expected to pay."
                    tooltip="Default ON. Goalies are scarce in most rec leagues, so most teams comp the goalie fee to keep the goalie pool happy."
                    accentColor="var(--goal)"
                    value={form.spareGoaliesFree !== false}
                    onChange={(next) => setForm(p => ({
                      ...p,
                      spareGoaliesFree: next,
                      // H1 — turning "free" ON clears any goalie fee in the same
                      // update. Otherwise the save would send fee>0 + free=true
                      // and the backend conflict resolver would flip "free"
                      // straight back OFF and toast about it — confusing when
                      // the coach just turned it on. Latest action wins.
                      spareGoalieFee: next ? '' : p.spareGoalieFee,
                    }))}
                  />

                  {/* Goalie fee — visible only when goalies are NOT free (rule
                      kept from v2.5.69: a fee is a no-op while the toggle is
                      on, so the toggle hides the input). The old handler that
                      unchecked the toggle as you typed was dropped — dead code
                      once the input became hidden while "free" is on
                      (spareGoaliesFree is always false when this renders). */}
                  {!form.spareGoaliesFree && (
                    <div className="mt-3">
                      <label className="label">Goalie Spare Fee <span className="opacity-50 text-xs">(optional — leave blank to charge the skater fee)</span></label>
                      <div style={{ position: 'relative', maxWidth: 220 }}>
                        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-2)', fontWeight: 600 }}>$</span>
                        <input type="number" min="0" step="0.50" className="input-field"
                          style={{ paddingLeft: 24 }}
                          placeholder="0.00"
                          value={form.spareGoalieFee}
                          onChange={e => setForm(p => ({ ...p, spareGoalieFee: e.target.value }))} />
                      </div>
                      <p className="text-xs text-secondary mt-1">
                        Charge goalies a different amount than skaters. The same
                        processing-fee handling as the spare fee applies.
                      </p>
                    </div>
                  )}

                  {/* Apr 2026 (Tom_Notes F10) — heads-up when a non-zero spare
                      fee is set while goalies play free. Intentional for most
                      rec teams, but new coaches sometimes set the fee thinking
                      it applies to everyone. Not a hard block, just context.
                      (Moved here with the toggle — "above" now points at the
                      toggle a few lines up in this same section.) */}
                  {form.sparePayRequired && form.spareGoaliesFree && form.spareFee && parseFloat(form.spareFee) > 0 && (
                    <div className="mt-2 p-3 rounded-xl text-xs flex items-start gap-2"
                      style={{
                        background: 'rgba(0,212,255,0.06)',
                        border: '1px solid rgba(0,212,255,0.18)',
                        color: 'var(--text-secondary)',
                      }}>
                      <span style={{ fontSize: 14, lineHeight: 1 }}>ℹ️</span>
                      <div>
                        <strong>Heads-up:</strong> Spare fee is set to{' '}
                        <strong>${parseFloat(form.spareFee).toFixed(2)}</strong> but
                        goalies play free. Skater spares will pay; goalie spares will
                        check in directly. If you want goalies to pay too, turn off
                        <strong> Goalies play free</strong> above.
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Season Fees (Full-Time Players) ──────────────────────────────
                    Coach-facing setup for collecting full-season fees via Game Day.
                    Toggle on, set amount, choose who covers processing. Live preview
                    shows a blended "processing fee" (Stripe + platform) without
                    exposing the platform's internal split. */}
                <div className="mt-6 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                  <h3 className="text-base font-bold mb-1" style={{ color: 'var(--text)' }}>
                    Season Fees (Full-Time Players)
                  </h3>
                  <p className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
                    Collect the full-season fee from each full-time player upfront via Game Day.
                    Leave off to collect externally (e-transfer, cash, etc.).
                  </p>

                  {/* Enable toggle */}
                  <ToggleSetting
                    emoji="📅"
                    title="Collect season fees via Game Day"
                    shortDesc="Full-time players get a 'Pay Season Fee' button via Stripe."
                    longDesc="Full-time players get a 'Pay Season Fee' button that charges via Stripe. The configured amount, processing-fee handling, and payment description below all apply when this is on."
                    tooltip="Leave off to collect externally (e-transfer, cash, etc.). Per-game spare fees are configured separately above."
                    value={form.seasonFeeRequired}
                    onChange={(next) => setForm(p => ({ ...p, seasonFeeRequired: next }))}>
                    {/* Season fee amount */}
                    <div style={{ maxWidth: 220 }}>
                      <label className="label">Season fee per player</label>
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-2)', fontSize: 14, pointerEvents: 'none' }}>$</span>
                        <input
                          type="number"
                          min="0"
                          step="5"
                          className="input-field"
                          style={{ paddingLeft: 24 }}
                          placeholder="300.00"
                          value={form.seasonFee}
                          onChange={e => setForm(p => ({ ...p, seasonFee: e.target.value }))} />
                      </div>
                    </div>

                      {/* Live preview — only show once an amount is entered.
                          Apr 2026 simplification: the previous radio-pair
                          ("Pass processing" / "Absorb") was replaced with a
                          matching pair of checkboxes to mirror the spare-fee
                          UI above — same mental model, same vocabulary. */}
                      {form.seasonFee && parseFloat(form.seasonFee) > 0 && (() => {
                        const flatCents     = team?.seasonFeeFlatCents ?? 200;
                        const percentBps    = team?.seasonFeePercentBps ?? 100;
                        const baseCents     = Math.round(parseFloat(form.seasonFee) * 100);
                        const passFee       = form.seasonFeePassToPlayer !== false;
                        const roundUp       = !!form.seasonFeeRoundUpQuarters;

                        // Platform take: flat + bps% of base.
                        const platformCents = flatCents + Math.ceil(baseCents * percentBps / 10000);

                        // Gross up the charge so the coach nets close
                        // to the base after Stripe (2.9% + 30¢) + the
                        // platform fee come out. Matches
                        // backend/src/routes/payments.js season_fee
                        // logic exactly. Apr 2026 fix.
                        const STRIPE_PCT     = 0.029;
                        const STRIPE_FIXED_C = 30;
                        let playerCents;
                        if (passFee) {
                          playerCents = Math.ceil(
                            (baseCents + STRIPE_FIXED_C + platformCents) / (1 - STRIPE_PCT)
                          );
                        } else {
                          playerCents = baseCents;
                        }
                        if (roundUp) playerCents = Math.ceil(playerCents / 25) * 25;

                        // Blended processing fee on the final charge.
                        const stripeCents    = Math.ceil(playerCents * STRIPE_PCT) + STRIPE_FIXED_C;
                        const blendedFeeCents = stripeCents + platformCents;

                        const fmt = (c) => `$${(c / 100).toFixed(2)}`;

                        return (
                          <div className="mt-3" style={{
                            background: 'var(--surface-2)', border: '1px solid var(--border)',
                            borderRadius: 12, padding: 14,
                          }}>
                            <p className="text-sm font-semibold mb-1">Processing fee handling</p>
                            <p className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
                              Processing fee ≈ <strong style={{ color: 'var(--text)' }}>{fmt(blendedFeeCents)}</strong> on
                              a {fmt(baseCents)} season fee.
                            </p>

                            <ToggleSetting
                              title="Charge season fee + processing fee"
                              shortDesc={passFee
                                ? `Player pays ${fmt(playerCents)} (${fmt(baseCents)} + processing). You net close to ${fmt(baseCents)}.`
                                : `Player pays the flat ${fmt(baseCents)}. Processing fee comes out of your payout.`}
                              tooltip="ON: player covers Stripe + platform fees. OFF: you absorb the fees and net less than the base amount."
                              value={passFee}
                              onChange={() => setForm(p => ({ ...p, seasonFeePassToPlayer: !passFee }))}
                            />

                            {passFee && (
                              <div className="mt-2">
                                <ToggleSetting
                                  title="Round season total up to nearest $0.25"
                                  shortDesc="Cleaner totals (e.g. $313.00 instead of $312.94)."
                                  tooltip="Strictly cosmetic: rounds the player's payment up to the nearest quarter-dollar. The extra cents are added to your net."
                                  value={roundUp}
                                  onChange={() => setForm(p => ({ ...p, seasonFeeRoundUpQuarters: !roundUp }))}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })()}
                  </ToggleSetting>
                </div>

                {/* Description */}
                <div>
                  <label className="label">Payment Description (shown on Stripe checkout)</label>
                  <input type="text" className="input-field" maxLength={120}
                    placeholder={`e.g. ${team?.name || 'Team'} ice time fee`}
                    value={form.paymentDescription}
                    onChange={e => setForm(p => ({ ...p, paymentDescription: e.target.value }))} />
                </div>

                {/* ── Payouts (Stripe Connect) moved to own sub-page (Apr 2026) ──
                    Was a sub-group inside the Pricing card. Coaches asked
                    for it on its own page so it's discoverable from the
                    sub-nav. The actual <StripeConnectPanel /> renders
                    on the 'payouts' sub-page below. Pricing now ends at
                    the description field above. */}
              </div>
            )}
          </div>
        )}

        {/* ── Payouts (Stripe Connect) ──
            Apr 2026 — promoted from a sub-group inside Pricing into a
            top-level sub-page so coaches can find it directly via the
            sub-nav. The Stripe onboarding flow only makes sense when
            Payments are enabled, so we surface a status banner with a
            shortcut to the Pricing page when paymentEnabled is false
            (mirrors the Waitlist Management pattern). */}
        {activeSection === 'payouts' && canEdit && (
          <div className="card">
            {/* 2026-07-11 (batch-B G2) — heading renamed from "Payouts
                (Stripe Connect)" to "Stripe" to match the sub-nav label
                rename in teamSettingsSubNav.js. Same section id/route
                ('payouts'), just clearer wording — this panel connects
                the Stripe account; payouts themselves follow Stripe's
                own schedule (explained below). */}
            <h2 className="font-bold text-lg flex items-center gap-2 mb-2">
              <span>🏦</span> Stripe
            </h2>
            <p className="text-sm text-secondary mb-4">
              Connect your Stripe account so spare and player payments land
              directly in your bank — minus a small processing fee. Without
              a connected account, payments are unavailable.
            </p>

            {!form.paymentEnabled && (
              <div className="p-3 rounded-xl mb-4" style={{
                background: 'rgba(255,149,0,0.08)',
                border: '1.5px solid rgba(255,149,0,0.35)',
              }}>
                <p className="text-sm font-semibold mb-1" style={{ color: 'var(--puck)' }}>
                  ⚠ Payments are off
                </p>
                <p className="text-xs mb-2" style={{ color: 'var(--text-2)', lineHeight: 1.5 }}>
                  Stripe Connect only matters when Payment Collection is enabled. Turn payments on in the Pricing sub-page, then come back here to connect your Stripe account.
                </p>
                <button
                  type="button"
                  onClick={() => navigate(`/team/${teamId}/settings/pricing`)}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg"
                  style={{
                    background: 'var(--puck)',
                    color: '#000',
                    border: 'none',
                    cursor: 'pointer',
                  }}>
                  Go to Pricing →
                </button>
              </div>
            )}

            {/* Stripe Connect onboarding panel — same component used
                inside Pricing previously. The component handles its
                own state (status, onboarding link, dashboard link). */}
            <StripeConnectPanel
              teamId={team?.id}
              teamName={team?.name}
              canEdit={canEdit}
            />

            <div className="p-3 rounded-xl text-sm mt-3"
              style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)' }}>
              <p className="font-semibold mb-1" style={{ color: 'var(--ice)' }}>How it works</p>
              <p className="text-secondary text-xs leading-relaxed">
                When payments are on, spares see a <strong>Pay Now</strong> button when checking in
                (pay upfront) or after being promoted (pay on promote). Funds land in
                the Stripe account you connect above, minus a small processing fee.
                Payouts follow your Stripe payout schedule — typically 2-3 business
                days to the bank account you link during onboarding.
              </p>
            </div>
          </div>
        )}

        {/* ── Create Teams ──
            Apr 2026 (Tom_Notes F12) — section for the balancing
            engine. Pulls in:
              • Auto-balance toggle + hours-before (was under Reminders)
              • Variety bias slider (was under Reminders)
              • Use-app-to-create-teams toggle (NEW — when off, the
                Game page surfaces a manual-only UI; when on, the
                balancer runs as before)
              • Send-teams-N-hrs-before-game (NEW — controls when
                the team-assignment email goes out, separate from
                when balancing happens)
              • Pair / no-pair player constraints (NEW — placeholder
                for the matrix UI that lets coaches lock players
                together or apart; saved as JSON in settings)
            Position: per spec, immediately after Payment Collection. */}
        {activeSection === 'balancing' && canEdit && (
          <div className="card">
            <h2 className="font-bold text-lg flex items-center gap-2 mb-4">
              ⚖️ Create Teams
            </h2>
            <p className="text-sm text-secondary mb-4">
              Controls how Game Day creates balanced teams from confirmed players. When the
              app does it for you, ratings + position + variety inform the split. Or you can
              skip it and assign teams yourself per game.
            </p>

            {/* Use-app toggle — high-level switch */}
            <ToggleSetting
              emoji="🤖"
              title="Use the app to create teams"
              shortDesc="Let the app help split players into Home / Away."
              longDesc="When on, you'll get one of two flows below: an AI balancer that uses ratings, positions, and variety; or a manual bulk-pick UI where you assign players to Home/Away yourself. When off, the entire 'Balance Teams' card on each game page is hidden — you handle teams entirely outside the app."
              tooltip="Coaches who run set lines or follow a roster from outside the app should turn this off. The Balance Teams card disappears from every game page when off."
              value={form.useAppForBalancing !== false}
              onChange={(next) => setForm(f => ({ ...f, useAppForBalancing: next }))}
            />

            {/* May 4 2026 (v2.5.69) — sub-options under Use App.
                Mutually exclusive: Auto-generate vs Manually Create.
                Coach feedback: "Change 'Use the app to create balanced
                teams' to 'Use the app to create teams', then have a sub
                option 'Auto-generate teams' and 'Manually Create
                Teams'. Both can't be on, if one is toggled on, the
                other is off."
                Implemented as radios since they're inherently 1-of-N.
                Hidden when the parent Use-app toggle is OFF (no point
                showing sub-options for a feature that's disabled).
                When 'manual' is selected, the auto-balance section
                + variety slider below are suppressed since they only
                apply to the auto path. The manual path's UI lives on
                each game page (bulk-pick popup). ── */}
            {form.useAppForBalancing !== false && (
              <div className="mt-3 ml-6 space-y-2" style={{
                borderLeft: '2px solid var(--border)',
                paddingLeft: 14,
              }}>
                <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-2)' }}>
                  How should the app create teams?
                </p>

                {/* Option A: Auto-generate */}
                <div
                  className="flex items-start gap-3 p-3 rounded-xl cursor-pointer"
                  style={{
                    background: form.teamCreationMode !== 'manual' ? 'rgba(0,212,255,0.06)' : 'var(--surface-2)',
                    border: `1.5px solid ${form.teamCreationMode !== 'manual' ? 'rgba(0,212,255,0.4)' : 'var(--border)'}`,
                  }}
                  onClick={() => setForm(f => ({ ...f, teamCreationMode: 'auto' }))}>
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                    background: form.teamCreationMode !== 'manual' ? 'var(--ice)' : 'var(--surface-3)',
                    border: `2px solid ${form.teamCreationMode !== 'manual' ? 'var(--ice)' : 'var(--border)'}`,
                    boxShadow: form.teamCreationMode !== 'manual' ? 'inset 0 0 0 3px var(--bg)' : 'none',
                  }} />
                  <div style={{ flex: 1 }}>
                    <p className="text-sm font-semibold flex items-center" style={{ gap: 6 }}>
                      ⚖️ Auto-generate teams
                      {/* v2.5.104 — Create Teams compact mode. When the
                          super-admin compact_toggle_descriptions flag
                          is on, hide the long description paragraph
                          and surface it via a ⓘ tooltip next to the
                          radio title. Outside compact mode, the
                          paragraph renders inline beneath the title
                          as it always has — no icon (would be
                          redundant with the visible paragraph). */}
                      <TogglePresentationContext.Consumer>
                        {(ctx) => ctx?.compact && (
                          <FieldHelp anchor>
                            The app's balancer uses ratings, positions, and variety bias to split
                            checked-in players into roughly even Home and Away rosters. You can
                            override individual swaps after, and the auto-balance cron + email
                            flow runs from these settings.
                          </FieldHelp>
                        )}
                      </TogglePresentationContext.Consumer>
                    </p>
                    <TogglePresentationContext.Consumer>
                      {(ctx) => !ctx?.compact && (
                        <p className="text-xs text-secondary mt-0.5">
                          The app's balancer uses ratings, positions, and variety bias to split
                          checked-in players into roughly even Home and Away rosters. You can
                          override individual swaps after, and the auto-balance cron + email
                          flow runs from these settings.
                        </p>
                      )}
                    </TogglePresentationContext.Consumer>

                    {/* v2.5.149 — sub-options that ONLY apply to the
                        auto-generate path now live INSIDE this card,
                        indented, so the hierarchy reads "Auto-generate
                        teams → (schedule it / include goalies)". Coach
                        feedback: these belonged under Auto-generate
                        rather than floating as siblings below the
                        radios. Rendered only when auto is the selected
                        mode. stopPropagation on the wrapper so toggling
                        a sub-option doesn't re-trigger the card's
                        onClick (which would re-select auto — harmless
                        but avoids a flash). */}
                    {form.teamCreationMode !== 'manual' && (
                      <div
                        className="mt-3 space-y-3"
                        style={{ borderTop: '1px dashed var(--border)', paddingTop: 12 }}
                        onClick={(e) => e.stopPropagation()}>
                        <ToggleSetting
                          emoji="⚖️"
                          title="Run the balancer automatically on a schedule"
                          shortDesc="Build balanced teams X hours before each game automatically."
                          longDesc="When ON, the system runs the team balancer on a recurring schedule — X hours before each game — so balanced rosters are ready without you opening the app. When OFF, you tap 'Balance' yourself on each game page."
                          tooltip="This decides WHEN teams get built (scheduled vs only when you tap Balance). Auto-generate above decides WHO builds them (the app vs you by hand)."
                          value={form.autoBalanceEnabled}
                          onChange={(next) => setForm(f => ({ ...f, autoBalanceEnabled: next }))}>
                          <label className="label">Hours before game</label>
                          <input type="number" min="1" max="72" className="input-field"
                            style={{ maxWidth: 120 }}
                            value={form.autoBalanceHoursBefore}
                            onChange={set('autoBalanceHoursBefore')} />
                          <FieldHelp>
                            1–72 hours. Typical: 24 (day-before), 6 (afternoon-of for evening games).
                          </FieldHelp>
                        </ToggleSetting>

                        <ToggleSetting
                          emoji="🥅"
                          title="Include goalies in team balancing"
                          shortDesc="Goalies count toward the balanced split, or stay manual?"
                          longDesc="When on, goalies are included in the balanced-teams algorithm just like skaters. When off, the balancer ignores goalies entirely — they stay unassigned and you slot them into Home/Away yourself on the Game page."
                          tooltip="Per-game override still works: the 'Goalies IN/OUT' button on the Balanced Teams card flips just for that one game, without changing this team-level default."
                          value={form.balanceIncludeGoalies !== false}
                          onChange={(next) => setForm(f => ({ ...f, balanceIncludeGoalies: next }))}
                        />

                        <ToggleSetting
                          emoji="🔁"
                          title="Goalies switch sides each mini-game"
                          shortDesc="Mini 1 as assigned, then both goalies swap ends every new mini."
                          longDesc="For nights with several mini-games and the same two goalies: the first mini they play the side they were assigned; every time a new mini starts, both goalies switch teams. Turn this on and the Goalies section on the Stats page will credit wins, losses, and goals for/against following that alternation. Skater stats are not affected."
                          tooltip="Only changes how goalie stats are attributed — it doesn't move anyone on the roster or scoreboard."
                          value={form.goalieSwapEachMini === true}
                          onChange={(next) => setForm(f => ({ ...f, goalieSwapEachMini: next }))}
                        />

                        {/* Variety slider — only when the scheduled
                            balancer is actually running. Moved inside
                            the auto card with the schedule toggle it
                            depends on. */}
                        {form.autoBalanceEnabled && (
                          <div className="pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                            <div className="flex items-center justify-between mb-1">
                              <label className="label flex items-center" style={{ marginBottom: 0, gap: 6 }}>
                                🎲 Mix it up — variety bias
                                <FieldHelp anchor>
                                  Looks at your last {form.balanceVarietyLookback || 4} balanced games and tries to
                                  avoid putting the same players together every week. Rating balance still comes first —
                                  this is just a tiebreaker. Set to Off if you have set lines or want consistent player
                                  groupings.
                                </FieldHelp>
                              </label>
                              <span className="text-sm font-bold" style={{ color: 'var(--ice)' }}>
                                {(() => {
                                  const v = Number(form.balanceVarietyWeight ?? 0.4);
                                  if (v === 0) return 'Off';
                                  if (v < 0.25) return 'Subtle';
                                  if (v < 0.6)  return 'Moderate';
                                  if (v < 0.85) return 'Strong';
                                  return 'Maximum';
                                })()}
                              </span>
                            </div>
                            <input
                              type="range"
                              min="0" max="1" step="0.1"
                              value={form.balanceVarietyWeight ?? 0.4}
                              onChange={e => setForm(f => ({ ...f, balanceVarietyWeight: parseFloat(e.target.value) }))}
                              className="w-full"
                              style={{ accentColor: 'var(--ice)' }}
                            />
                            <div className="flex justify-between text-xs text-secondary mt-1">
                              <span>Off (same teams OK)</span>
                              <span>Max (always fresh)</span>
                            </div>
                            {Number(form.balanceVarietyWeight ?? 0.4) > 0 && (
                              <div className="mt-3">
                                <label className="label">Look back this many games</label>
                                <select className="input-field" style={{ maxWidth: 140 }}
                                  value={form.balanceVarietyLookback || 4}
                                  onChange={e => setForm(f => ({ ...f, balanceVarietyLookback: parseInt(e.target.value) }))}>
                                  {[2, 3, 4, 5, 6, 8, 10].map(n => (
                                    <option key={n} value={n}>{n} games</option>
                                  ))}
                                </select>
                                <FieldHelp>
                                  Default: 4. Higher = more variety but slower to react when a player joins/leaves.
                                </FieldHelp>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Option B: Manually Create */}
                <div
                  className="flex items-start gap-3 p-3 rounded-xl cursor-pointer"
                  style={{
                    background: form.teamCreationMode === 'manual' ? 'rgba(167,139,250,0.10)' : 'var(--surface-2)',
                    border: `1.5px solid ${form.teamCreationMode === 'manual' ? 'rgba(167,139,250,0.4)' : 'var(--border)'}`,
                  }}
                  onClick={() => setForm(f => ({ ...f, teamCreationMode: 'manual' }))}>
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                    background: form.teamCreationMode === 'manual' ? '#a78bfa' : 'var(--surface-3)',
                    border: `2px solid ${form.teamCreationMode === 'manual' ? '#a78bfa' : 'var(--border)'}`,
                    boxShadow: form.teamCreationMode === 'manual' ? 'inset 0 0 0 3px var(--bg)' : 'none',
                  }} />
                  <div style={{ flex: 1 }}>
                    <p className="text-sm font-semibold flex items-center" style={{ gap: 6 }}>
                      ✋ Manually Create Teams
                      <TogglePresentationContext.Consumer>
                        {(ctx) => ctx?.compact && (
                          <FieldHelp anchor>
                            No automatic balancing. On each game page, a "Bulk Assign Players"
                            popup lets you multi-select checked-in players and drop them onto
                            Home or Away. Auto-balance scheduling + variety bias are not used.
                          </FieldHelp>
                        )}
                      </TogglePresentationContext.Consumer>
                    </p>
                    <TogglePresentationContext.Consumer>
                      {(ctx) => !ctx?.compact && (
                        <p className="text-xs text-secondary mt-0.5">
                          No automatic balancing. On each game page, a "Bulk Assign Players"
                          popup lets you multi-select checked-in players and drop them onto
                          Home or Away. Auto-balance scheduling + variety bias are not used.
                        </p>
                      )}
                    </TogglePresentationContext.Consumer>
                  </div>
                </div>
              </div>
            )}

            {/* May 2 2026 — Include goalies in balancing toggle.
                Sits directly under the Use-app toggle since it's the
                next most-fundamental "how does balancing work" choice.
                When OFF, goalies are dropped from the balancer input
                entirely — coach assigns them manually on the Game
                page. The Game page's per-game "Goalies IN/OUT" button
                still exists and overrides per call; this just changes
                the default. A clarifying note appears on the Game
                page Balanced Teams card when this is OFF so non-coach
                viewers (and the coach themselves) know the goalies
                won't be auto-slotted.
                v2.5.107 — gated on useAppForBalancing. When the coach
                isn't using the app to balance, this setting is
                meaningless (there's no balancer running to include
                goalies in). Hidden rather than disabled because the
                related controls (auto-balance + email) are also
                hidden in that state — keeps the section clean. The
                form key still saves so flipping useAppForBalancing
                back ON restores the previously-chosen value. */}
            {/* v2.5.149 — "Include goalies in team balancing" moved
                INTO the Auto-generate card above (it only applies to
                the balancer). Standalone block removed. */}


            {/* v2.5.105 — Auto-balance section restructured per coach
                feedback. Previously the variety slider was gated only
                on team-creation-mode (so it would show even when
                auto-balance was OFF — bug). Now also gated on
                autoBalanceEnabled so the slider hides when there's
                no auto-balance to apply variety to.
                Also: legacy "Auto-balance teams + email assignments"
                ToggleSetting renamed to "Automatically create a
                balance team" (creation only — email split out). The
                +email functionality moved into a sibling
                "Automatically email created teams" toggle below.
                Only the auto-balance creation step is gated on
                teamCreationMode !== 'manual'; the email toggle is
                visible whenever any team-creation path is active. ── */}
            {/* v2.5.149 — "Run the balancer automatically on a
                schedule" + the variety slider moved INTO the Auto-
                generate card above (they only apply to the auto path).
                Standalone block removed; the email toggle below stays
                here as it applies to BOTH creation paths. */}


            {/* v2.5.105 — Unified email toggle. Replaces the legacy
                "Send team assignments automatically (manual mode)"
                toggle which only worked in manual-creation mode.
                Now this single toggle controls emails for BOTH paths
                (auto AND manual). Coach feedback: "Have email as its
                own toggle, so that a coach can turn it on or off
                regardless if they are manually or automatically
                creating teams."
                Visibility: only shown when at least one creation
                method is active (Use-app ON with auto-balance ON, OR
                Use-app OFF with manual creation). When neither, the
                toggle is disabled with a note explaining why.
                Coach feedback: "If neither team creation options are
                toggled on, then prevent 'Automatically E-mail
                Created Teams' toggle to be selected and note they
                have to select Manual or Automatic team creation."
                ── */}
            {/* v2.5.107 — when useAppForBalancing is OFF, hide the
                email toggle entirely. Coach feedback: "if I have Use
                the app to create teams turn off, then also ensure
                that Include goalies in team balancing is also off
                and Automatically email created teams is turned off."
                A coach who turns off use-app isn't using Game Day
                for team creation — they presumably handle emails
                themselves. The form value is force-set to false in
                the save payload when use-app is OFF so the cron
                doesn't fire even if the value was previously ON. */}
            {form.useAppForBalancing !== false && (() => {
              // Inside this gate use-app is guaranteed true.
              const hasAutoCreation   = form.teamCreationMode !== 'manual'
                                     && form.autoBalanceEnabled;
              const hasManualCreation = form.teamCreationMode === 'manual';
              const eitherCreation    = hasAutoCreation || hasManualCreation;
              return (
                <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                  <ToggleSetting
                    emoji="📧"
                    title="Automatically email created teams"
                    shortDesc={hasAutoCreation
                      ? "Email players their assignment when teams are created."
                      : hasManualCreation
                        ? "Email manually-assigned teams on a schedule."
                        : "Disabled — turn on a team-creation method first."}
                    longDesc="Email each player their team assignment (Home/Away + jersey color) when teams are created. Works for both auto-balanced and manually-assigned teams. Without this, the roster is still saved and visible in-app, but no emails go out."
                    tooltip="Emails each player their Home/Away assignment and jersey color. Set how many hours before the game the email goes out below. Turn off to rely on in-app visibility only."
                    disabled={!eitherCreation}
                    value={eitherCreation && form.autoEmailTeamsEnabled !== false}
                    onChange={(next) => setForm(f => ({
                      ...f,
                      autoEmailTeamsEnabled: next,
                      // v2.5.105 — keep the legacy
                      // `send_teams_manual_enabled` flag in sync
                      // with the new unified toggle. The manual
                      // cron's candidate query (Case A in
                      // checkManualTeamAssignmentSends) still
                      // requires this flag to be true; without
                      // syncing it, flipping the new email toggle ON
                      // in pure manual mode would have no effect
                      // because the candidate query wouldn't match.
                      // Keeping the legacy key in lockstep with the
                      // new key means existing cron logic continues
                      // to work without a schema/cron migration.
                      sendTeamsManualEnabled: next,
                    }))}>
                    {/* v2.5.149 — send-time input now shows in BOTH
                        modes (coach asked for an explicit "N hours
                        before the game" control for the email itself,
                        not just inherited from the balance schedule).
                        • Manual mode → send_teams_hours_before (the
                          manual cron's window).
                        • Auto mode → the email piggybacks on the
                          auto-balance run, but we still surface the
                          balance hours-before here read-only-ish so
                          the coach sees WHEN it'll send, with a note
                          that changing it is done on the schedule
                          toggle above. */}
                    {hasManualCreation && !hasAutoCreation && (
                      <>
                        <label className="label">Hours before game</label>
                        <input type="number" min="1" max="72" className="input-field"
                          style={{ maxWidth: 140 }}
                          value={form.sendTeamsHoursBefore ?? 6}
                          onChange={e => setForm(f => ({ ...f, sendTeamsHoursBefore: parseInt(e.target.value) || 6 }))} />
                        <FieldHelp>
                          0–72 hours. Common picks: 24 (day-before), 6 (afternoon-of evening games),
                          2 (last-minute confirmation).
                        </FieldHelp>
                      </>
                    )}
                    {hasAutoCreation && (
                      <>
                        {/* v2.5.181 — coach asked to set this time directly
                            instead of being redirected to the balancer
                            toggle. In auto mode the assignment email is
                            sent in the same pass the balancer runs (you
                            can't email teams before they're created), so
                            this hours-before IS the balance-run schedule
                            (auto_balance_hours_before) — we bind to it
                            here so changing it in either place stays in
                            sync, and no longer silently default to 2. */}
                        <label className="label">Hours before game</label>
                        <input type="number" min="1" max="72" className="input-field"
                          style={{ maxWidth: 140 }}
                          value={form.autoBalanceHoursBefore ?? 24}
                          onChange={e => setForm(f => ({
                            ...f,
                            autoBalanceHoursBefore: Math.max(1, Math.min(72, parseInt(e.target.value, 10) || 24)),
                          }))} />
                        <FieldHelp>
                          Teams are balanced and emailed this many hours before each game.
                          This is the same schedule as “Run the balancer automatically” above —
                          changing it here updates both. Common picks: 24 (day-before),
                          6 (afternoon-of), 2 (last-minute).
                        </FieldHelp>
                      </>
                    )}
                  </ToggleSetting>
                  {!eitherCreation && (
                    <p className="text-xs mt-2 px-1" style={{ color: 'var(--puck)', fontStyle: 'italic' }}>
                      ⚠ Select a team-creation method above (Auto-generate teams + scheduled balancer ON, OR Manually Create Teams) before enabling email.
                    </p>
                  )}

                  {/* 2026-07-06 — coach request: auto re-balance when the
                      roster changes AFTER teams were created (drop-out or
                      late addition), with a 4-minute grace window so an
                      accidental "Out" tap that's corrected right away
                      causes no churn or duplicate email. AUTO mode only —
                      in manual mode a re-run would overwrite the coach's
                      hand-built teams with algorithm output. Locking teams
                      on a game always overrides this setting. Backend:
                      scheduler.js checkRebalanceOnChange. */}
                  <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                    <ToggleSetting
                      emoji="🔁"
                      title="Re-balance if the roster changes"
                      shortDesc={hasAutoCreation
                        ? "Someone drops out or joins after teams are made? Re-run the balancer automatically."
                        : "Requires Auto-generate teams with the scheduled balancer ON."}
                      longDesc="After teams have been created, a drop-out (In → Out) or a late addition (a new In, or a waitlist promotion) automatically re-runs the balancer so both sides stay fair — right up to game time. Changes get a 4-minute grace window first: someone who taps Out by mistake and rejoins causes no churn and no email. If 'Automatically email created teams' is ON, everyone receives their corrected assignment by email; if OFF, teams update in-app and the game's Teams card shows the 'send update' banner. Locking teams on a game always overrides this."
                      tooltip="Watches for roster changes after teams exist and re-balances about 4–6 minutes after the last change. Respects the email toggle above and per-game team locks."
                      disabled={!hasAutoCreation}
                      value={hasAutoCreation && form.rebalanceOnChangeEnabled === true}
                      onChange={(next) => setForm(f => ({ ...f, rebalanceOnChangeEnabled: next }))}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Apr 30 2026 (App_Change_List #30) — Jersey Colors block
                moved here from Team Info. Coach feedback: jerseys map
                onto home/away assignments produced by the balancer,
                so they belong inside this section. Same form-state
                keys (homeJerseyColor / awayJerseyColor) — save/load
                paths are unchanged. */}
            <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
              <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>
                🥅 Jersey Colors
              </h3>
              <p className="text-xs text-secondary mb-3">
                Shown to players on every game's Balanced Teams view so they know which sweater to bring.
              </p>

              <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                {[
                  { key: 'homeJerseyColor', label: 'Home Team Jersey', placeholder: 'black', accent: 'var(--ice)' },
                  { key: 'awayJerseyColor', label: 'Away Team Jersey', placeholder: 'white', accent: 'var(--goal)' },
                ].map(cfg => (
                  <div key={cfg.key}>
                    <label className="label" style={{ color: cfg.accent }}>{cfg.label}</label>
                    <input
                      type="text"
                      className="input-field"
                      value={form[cfg.key] || ''}
                      placeholder={cfg.placeholder}
                      maxLength={24}
                      onChange={set(cfg.key)}
                    />
                    {/* Quick-picks — tapping sets the field to that color. */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {['black','white','red','blue','green','yellow','orange','grey'].map(c => {
                        const isActive = (form[cfg.key] || '').trim().toLowerCase() === c;
                        return (
                          <button
                            key={c}
                            type="button"
                            onClick={() => set(cfg.key)({ target: { value: c } })}
                            style={{
                              padding: '4px 10px', borderRadius: 999,
                              fontSize: 11, fontWeight: 600,
                              background: isActive ? cfg.accent + '20' : 'var(--surface-2)',
                              border: `1px solid ${isActive ? cfg.accent + '55' : 'var(--border)'}`,
                              color: isActive ? cfg.accent : 'var(--text-2)',
                              cursor: 'pointer',
                              textTransform: 'capitalize',
                            }}>
                            {c}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* v2.5.181 — Pair / Keep-Apart Players. The balancer now
                honors these (services/balancing.js). Soft = nudge, Hard =
                must. Editing happens in a modal; the summary here shows
                what's active. Stored in balance_pairs_json (structured). */}
            <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
              <div className="flex items-center justify-between gap-3 mb-1">
                <h3 className="font-semibold text-sm">🔗 Pair / Keep-Apart Players</h3>
                <button type="button" onClick={openPairModal}
                  className="btn-ghost text-xs px-3 py-1.5"
                  style={{ whiteSpace: 'nowrap' }}>
                  {(pairConstraints.pairs.length + pairConstraints.keepApart.length) > 0 ? 'Manage' : 'Set up'}
                </button>
              </div>
              <p className="text-xs text-secondary mb-2">
                Keep specific players together, or apart, when the balancer builds teams.
                A <strong>hard</strong> rule must always hold; a <strong>soft</strong> rule
                is a strong preference the balancer can override for a fairer game.
              </p>
              {(pairConstraints.pairs.length + pairConstraints.keepApart.length) === 0 ? (
                <p className="text-xs" style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>
                  No rules set — the balancer treats everyone freely.
                </p>
              ) : (
                <div className="flex flex-col gap-1.5 mt-1">
                  {pairConstraints.pairs.map((c, i) => (
                    <div key={`p${i}`} className="text-xs flex items-center gap-2 flex-wrap">
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 6,
                        background: 'var(--ice-a10)', color: 'var(--ice)',
                      }}>PAIR</span>
                      <span style={{ color: 'var(--text)' }}>{nameForId(c.a)} + {nameForId(c.b)}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 6,
                        background: c.strength === 'hard' ? 'var(--penalty-a15)' : 'var(--puck-a8)',
                        color: c.strength === 'hard' ? 'var(--penalty)' : 'var(--puck)',
                      }}>{c.strength === 'hard' ? 'Hard' : 'Soft'}</span>
                    </div>
                  ))}
                  {pairConstraints.keepApart.map((c, i) => (
                    <div key={`a${i}`} className="text-xs flex items-center gap-2 flex-wrap">
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 6,
                        background: 'var(--penalty-a15)', color: 'var(--penalty)',
                      }}>APART</span>
                      <span style={{ color: 'var(--text)' }}>{nameForId(c.a)} ✕ {nameForId(c.b)}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 6,
                        background: c.strength === 'hard' ? 'var(--penalty-a15)' : 'var(--puck-a8)',
                        color: c.strength === 'hard' ? 'var(--penalty)' : 'var(--puck)',
                      }}>{c.strength === 'hard' ? 'Hard' : 'Soft'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Roster Contacts — quick-access copy & export for coaches ──
            Lives in Team Settings so it's reachable even without visiting the
            Players page. Same actions also exist on the Players page.
            Apr 2026 — folded into the 'team-info' sub-page since it's a
            team-admin utility (not part of any settings group). ── */}
        {activeSection === 'team-info' && canEdit && (
          <div className="card mb-5">
            <div className="flex items-center gap-2 mb-2">
              <Users size={16} style={{ color: 'var(--ice)' }} />
              <h3 className="font-semibold">Player Contacts</h3>
            </div>
            <p className="text-secondary text-xs mb-3">
              Quick access to everyone's contact info — for mass emails or record-keeping.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={copyAllEmailsFromSettings}
                disabled={exportingRoster}
                className="btn-ghost text-sm px-4 py-2 flex items-center gap-2"
                title="Copy everyone's email addresses to clipboard — paste into Outlook/Gmail">
                <Copy size={14} /> Copy All Emails
              </button>
              <button
                type="button"
                onClick={exportRosterCSV}
                disabled={exportingRoster}
                className="btn-ghost text-sm px-4 py-2 flex items-center gap-2"
                title="Download a CSV with names, emails, phones — opens in Excel">
                <FileSpreadsheet size={14} /> Export to Excel
              </button>
            </div>
            <p className="text-xs text-secondary mt-2" style={{ lineHeight: 1.5 }}>
              Export includes first name, last name, email, phone, role and invite status.
              Emails list is comma-separated for pasting into a To or BCC field.
            </p>
          </div>
        )}

        {/* ── Drinks Duty ──
            Apr 2026 (Tom_Notes F-order) — moved into the form flow
            from after the form so the visual section order matches
            the spec. The DrinksSettings component manages its own
            state and saves via separate API calls; placing it here
            doesn't change behaviour. */}
        {activeSection === 'drinks' && canEdit && (
          <DrinksSettings teamId={teamId} settings={team?.settings} />
        )}

        {/* ── Chat & Notifications ──
            Apr 2026 (Tom_Notes F-order) — relocated to live between
            Drinks and Privacy per the new section order. Internals
            unchanged. */}
        {activeSection === 'chat' && canEdit && (
          <div className="card mb-5">
            <div className="flex items-center gap-2 mb-2">
              <Bell size={16} style={{ color: 'var(--ice)' }} />
              <h3 className="font-semibold">Chat & Notifications</h3>
            </div>
            <p className="text-secondary text-xs mb-4">
              Control how team chat messages reach your players outside the app.
            </p>

            {/* Chat notification scope — tri-state picker.
                Apr 2026 — default flipped from 'off' to 'everyone'
                so regular team chat reaches the team by email out
                of the box. Players who don't want chat emails can
                turn off email notifications in their own profile.
                'off'      → only announcements email the team
                'coaches'  → every message emails coaches + managers only
                'everyone' → every message emails every team member (DEFAULT)
                Announcements always email the whole team regardless
                of this setting (forced 'everyone' scope server-side). ── */}
            <div>
              <p className="text-sm font-semibold mb-2">Email notifications for chat messages</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: 'off',      emoji: '🔕', label: 'Off',          desc: 'Only announcements email the team.' },
                  { key: 'coaches',  emoji: '🎯', label: 'Coaches Only', desc: 'Every message emails coaches + managers.' },
                  { key: 'everyone', emoji: '📣', label: 'Everyone',     desc: 'Every message emails the whole team.' },
                ].map(opt => {
                  const active = (form.chatNotifyMode || 'everyone') === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setForm(p => ({ ...p, chatNotifyMode: opt.key }))}
                      className="flex flex-col items-start gap-1 py-3 px-3 rounded-xl text-left transition-all"
                      style={{
                        background: active ? 'var(--ice-a8)' : 'var(--surface-2)',
                        border: `1.5px solid ${active ? 'var(--ice-a40)' : 'var(--border)'}`,
                        color: active ? 'var(--ice)' : 'var(--text-2)',
                        cursor: 'pointer',
                      }}>
                      <span className="text-sm font-bold">
                        {opt.emoji} {active ? '✓ ' : ''}{opt.label}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--text-3)', fontWeight: 500, lineHeight: 1.4 }}>
                        {opt.desc}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-secondary mt-2" style={{ lineHeight: 1.5 }}>
                Players who've disabled email notifications in their profile won't receive these either way.
                Announcements always email everyone regardless of this setting.
              </p>
            </div>

            {/* v2.5.181 — Chat retention + clear-all. Messages and their
                attachment files older than the window are auto-deleted
                by a daily job; the button wipes everything immediately. */}
            <div className="mt-5 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
              <h4 className="font-semibold text-sm mb-1">🗑️ Chat history &amp; cleanup</h4>
              <p className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
                Automatically delete chat messages and their attached files after a set
                number of days. This frees storage and limits how long history is kept.
              </p>

              <label className="label">Delete messages &amp; files older than</label>
              <div className="flex items-center gap-2 mb-1">
                <input
                  type="number" min="0" max="3650" className="input-field"
                  style={{ maxWidth: 120 }}
                  value={form.chatRetentionDays ?? 180}
                  onChange={e => setForm(p => ({
                    ...p,
                    chatRetentionDays: e.target.value === '' ? '' : Math.max(0, Math.min(3650, parseInt(e.target.value, 10) || 0)),
                  }))} />
                <span className="text-sm" style={{ color: 'var(--text-2)' }}>days</span>
              </div>
              <FieldHelp>
                Default is 180 days. Set to <strong>0</strong> to keep messages forever (no auto-delete).
                Deleted messages and their files are removed permanently and can't be recovered.
                Changes apply when you Save this page; the cleanup runs once daily.
              </FieldHelp>

              <div className="mt-4 p-3 rounded-xl" style={{ background: 'var(--penalty-a8, rgba(248,113,113,0.05))', border: '1px solid var(--penalty-a25, rgba(248,113,113,0.25))' }}>
                <p className="text-xs mb-2" style={{ color: 'var(--text-2)', lineHeight: 1.5 }}>
                  <strong style={{ color: 'var(--penalty)' }}>Clear everything now.</strong>{' '}
                  Immediately delete <strong>all</strong> chat messages and attached files for this team,
                  across every season. This can't be undone.
                </p>
                <button type="button" onClick={() => setShowClearChatModal(true)}
                  className="text-xs px-4 py-2 rounded-lg font-semibold"
                  style={{ background: 'var(--penalty-a15)', color: 'var(--penalty)', border: '1px solid var(--penalty-a25)' }}>
                  Clear all Chat messages &amp; Files Now
                </button>
              </div>
            </div>

            {/* May 4 2026 (v2.5.69) — Coach Alerts subsection. Houses
                the low-attendance alert (relocated from Waitlist
                Management) plus four new check-out notifications.
                Coach asked: "Add in new options for e-mail
                notifications to the coach if a full time player checks
                out or if a full time goalie checks out. They should
                get a push / e-mail notification. Also include other
                options, such as notify the coach if a spare checks
                out, or spare goalie checks out. The coach can select
                everything as off, or select different options or all
                options."
                Each toggle is independent — coach mixes and matches.
                All four checkout toggles default off so existing teams
                aren't suddenly spammed. ── */}
            <div className="pt-4 mt-4" style={{ borderTop: '1px solid var(--border)' }}>
              <p className="text-xs font-bold mb-3" style={{
                fontFamily: 'var(--font-display)', letterSpacing: '0.12em',
                textTransform: 'uppercase', color: 'var(--puck)',
              }}>
                🚨 Coach Alerts
              </p>

              <div className="space-y-3">
                {/* Low attendance alert — relocated from Waitlist
                    Management. Same form fields and backend keys; only
                    visual location changed. ── */}
                <ToggleSetting
                  emoji="⚠️"
                  title="Notify coaches when attendance is low"
                  shortDesc="In-app alert if check-in count is below threshold near game time."
                  longDesc="If checked-in count is below the threshold within the warning window, send all coaches/managers an in-app alert. Sent once per game per crossing (not every cron tick) so you won't get spammed."
                  tooltip="Useful for catching last-minute drop-outs in time to invite spares manually."
                  accentColor="var(--puck)"
                  value={form.lowAttendanceAlertEnabled}
                  onChange={(next) => setForm(p => ({ ...p, lowAttendanceAlertEnabled: next }))}>
                  <div className="space-y-2">
                    <div>
                      <label className="label">Alert when count is below</label>
                      <input type="number" min="1" max="50" className="input-field mt-1"
                        style={{ maxWidth: 140 }}
                        value={form.lowAttendanceAlertThreshold}
                        onChange={e => setForm(p => ({ ...p, lowAttendanceAlertThreshold: e.target.value }))} />
                    </div>
                    <div>
                      <label className="label">Within how many hours of game</label>
                      <select className="input-field mt-1"
                        style={{ maxWidth: 220 }}
                        value={form.lowAttendanceAlertHoursBefore}
                        onChange={e => setForm(p => ({ ...p, lowAttendanceAlertHoursBefore: e.target.value }))}>
                        {[2, 4, 6, 12, 24, 48, 72].map(h => (
                          <option key={h} value={h}>{h} hour{h !== 1 ? 's' : ''} before</option>
                        ))}
                      </select>
                    </div>
                    <p className="text-xs text-secondary mt-1">
                      Useful for catching last-minute drop-outs in time to invite spares manually.
                    </p>
                  </div>
                </ToggleSetting>

                {/* Check-out notifications — four independent toggles.
                    Each one fires push + email to every coach/manager
                    on the team when a player of that tier flips their
                    RSVP to 'out'. Backend hook lives in the availability
                    PATCH route (when status transitions to 'out',
                    look up the team's notify_coach_on_*_checkout flags
                    and dispatch). Idempotent per (game, user) so a
                    rapid in→out→in→out toggle doesn't blast 4 emails. ── */}
                <ToggleSetting
                  emoji="🚪"
                  title="Notify coaches when a Full-Time player checks out"
                  shortDesc="Push + email to all coaches when a FT player flips to 'Out'."
                  longDesc="Every coach and manager on the team receives an in-app push notification AND an email when a full-time player checks out of an upcoming game. Useful for high-stakes games where you want to act fast on a drop-out."
                  tooltip="Independent of the low-attendance alert above. Fires once per (game, player) check-out event — but only if the game is within the timing window set just below this toggle. If a player checks out earlier than that, the alert is held back; widen the window to be notified sooner."
                  accentColor="var(--penalty)"
                  value={form.notifyCoachOnFtPlayerCheckout}
                  onChange={(next) => setForm(p => ({ ...p, notifyCoachOnFtPlayerCheckout: next }))}
                />
                {form.notifyCoachOnFtPlayerCheckout && (
                  <div className="mb-3 ml-2 pl-3" style={{ borderLeft: '2px solid var(--penalty-a15, rgba(248,113,113,0.18))' }}>
                    <CheckoutWindowPicker
                      value={form.notifyCoachCheckoutWindowFtPlayer}
                      onChange={(h) => setForm(p => ({ ...p, notifyCoachCheckoutWindowFtPlayer: h }))}
                    />
                  </div>
                )}

                <ToggleSetting
                  emoji="🥅"
                  title="Notify coaches when a Full-Time goalie checks out"
                  shortDesc="Push + email when a FT goalie flips to 'Out'."
                  longDesc="Every coach and manager on the team receives an in-app push notification AND an email when a full-time goalie checks out. Goalies are scarce — this gets you on the spare-goalie blast immediately."
                  tooltip="Often the most actionable check-out signal. Goalies are usually 1-2 per team."
                  accentColor="var(--penalty)"
                  value={form.notifyCoachOnFtGoalieCheckout}
                  onChange={(next) => setForm(p => ({ ...p, notifyCoachOnFtGoalieCheckout: next }))}
                />
                {form.notifyCoachOnFtGoalieCheckout && (
                  <div className="mb-3 ml-2 pl-3" style={{ borderLeft: '2px solid var(--penalty-a15, rgba(248,113,113,0.18))' }}>
                    <CheckoutWindowPicker
                      value={form.notifyCoachCheckoutWindowFtGoalie}
                      onChange={(h) => setForm(p => ({ ...p, notifyCoachCheckoutWindowFtGoalie: h }))}
                    />
                  </div>
                )}

                <ToggleSetting
                  emoji="⏳"
                  title="Notify coaches when a spare player checks out"
                  shortDesc="Push + email when a spare skater flips to 'Out'."
                  longDesc="Notify coaches when an invited spare player checks out. Useful when running a tight roster — you can immediately invite the next spare in line. Spares who haven't been invited yet don't trigger this notification (you didn't ask them to come)."
                  tooltip="Only fires for spares who've been explicitly invited (via Auto-invite Spares or manual invite). Un-invited spares can't 'check out' since they were never in."
                  value={form.notifyCoachOnSparePlayerCheckout}
                  onChange={(next) => setForm(p => ({ ...p, notifyCoachOnSparePlayerCheckout: next }))}
                />
                {form.notifyCoachOnSparePlayerCheckout && (
                  <div className="mb-3 ml-2 pl-3" style={{ borderLeft: '2px solid var(--penalty-a15, rgba(248,113,113,0.18))' }}>
                    <CheckoutWindowPicker
                      value={form.notifyCoachCheckoutWindowSparePlayer}
                      onChange={(h) => setForm(p => ({ ...p, notifyCoachCheckoutWindowSparePlayer: h }))}
                    />
                  </div>
                )}

                <ToggleSetting
                  emoji="⏳🥅"
                  title="Notify coaches when a spare goalie checks out"
                  shortDesc="Push + email when an invited spare goalie flips to 'Out'."
                  longDesc="Notify coaches when an invited spare goalie checks out. Same as the spare-player toggle but specifically for goalies — separate so you can opt in to one without the other."
                  tooltip="Spare goalies who haven't been invited don't trigger this. Only fires for explicitly-invited spare goalies."
                  value={form.notifyCoachOnSpareGoalieCheckout}
                  onChange={(next) => setForm(p => ({ ...p, notifyCoachOnSpareGoalieCheckout: next }))}
                />
                {form.notifyCoachOnSpareGoalieCheckout && (
                  <div className="mb-3 ml-2 pl-3" style={{ borderLeft: '2px solid var(--penalty-a15, rgba(248,113,113,0.18))' }}>
                    <CheckoutWindowPicker
                      value={form.notifyCoachCheckoutWindowSpareGoalie}
                      onChange={(h) => setForm(p => ({ ...p, notifyCoachCheckoutWindowSpareGoalie: h }))}
                    />
                  </div>
                )}

                {/* v2.5.165 — the shared "Check-out alert timing" box was
                    replaced by a per-toggle window (the indented picker
                    under each of the four toggles above). Coaches asked to
                    customise the "within how many hours" timing separately
                    for each tier. The legacy shared key
                    (notify_coach_checkout_window_hours) is still written by
                    save() and used as the backend fallback for any tier
                    whose own key is unset, so existing teams' value carried
                    into each tier on load. ── */}
                {(form.notifyCoachOnFtPlayerCheckout || form.notifyCoachOnFtGoalieCheckout
                  || form.notifyCoachOnSparePlayerCheckout || form.notifyCoachOnSpareGoalieCheckout) && (
                  <p className="text-xs text-secondary mt-1" style={{ fontStyle: 'italic' }}>
                    ⏱️ Each check-out alert above has its own timing — set how many hours before
                    the game each one should still notify you, right under its toggle.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── SMS (v2.5.135 SMS-A) ── manager-only toggle for the
            team's SMS sending. Lives inside the 'chat' section so
            it's adjacent to email notification settings — same
            mental model ("which channels does this team use to
            reach players"). Backend route requires manager role;
            we render only when isManager so coaches don't see a
            toggle they can't use. */}
        {activeSection === 'chat' && isManager && (
          <TeamSmsToggleCard teamId={teamId} />
        )}
        {/* ── WhatsApp game updates (2026-10-01, Game Day Stats) — coach or
            manager; the backend route allows both. */}
        {activeSection === 'chat' && canEdit && (
          <TeamWhatsAppCard teamId={teamId} />
        )}

        {/* ── Privacy (Bug 9) ── */}
        {activeSection === 'privacy' && canEdit && (
          <div className="card mb-5">
            <div className="flex items-center gap-2 mb-2">
              <Settings size={16} style={{ color: 'var(--ice)' }} />
              <h3 className="font-semibold">Privacy</h3>
            </div>
            <p className="text-secondary text-xs mb-4">
              Control what non-coach team members can see. Coaches and managers always see everything.
            </p>

            {/* 2026-07-27 (per Gilles) — the "Show player names to
                non-coach members" toggle is REMOVED: with the player-
                visible team leaderboard, teammates seeing each other's
                names is the product's baseline (nobody realistically
                hides names from their own team). The save payload below
                force-writes show_player_names_to_players: true, which
                also self-heals any team a coach had previously flipped
                off — one settings save and names are back. The backend
                read-sites (games.js / teams.js showOtherNames) still
                honor the flag, per house rule (backend infrastructure
                stays when only the UI control is removed). */}

            {/* Show ratings toggle (default OFF — preserves coach-private rating system) */}
            <ToggleSetting
              emoji="⭐"
              title="Show player ratings to non-coach members"
              shortDesc="Players can see each other's skill ratings (1–10)."
              longDesc="When on, players can see each other's skill ratings (1–10). When off (default), only coaches and managers see ratings. Rating notes are always coach-private regardless."
              tooltip="Useful for transparent leagues that already share rosters with ratings. Off by default since most teams treat ratings as a coach-internal balancer input."
              accentColor="var(--puck)"
              value={form.showRatingsToPlayers}
              onChange={(next) => setForm(p => ({ ...p, showRatingsToPlayers: next }))}
            />
          </div>
        )}

        {/* ── Your Role ──
            Apr 2026 (Tom_Notes F-order) — moved into the form flow.
            YourRole self-manages its own role-change API call; this
            placement is purely structural for the new section
            ordering.
            Apr 2026 sub-pages — folded into the 'danger' sub-page
            since it's about your membership in this team (sits next
            to Leave Team). */}
        {activeSection === 'danger' && canEdit && team?.myMemberId && (
          <YourRole teamId={teamId} memberId={team.myMemberId} currentRole={team?.myRole} />
        )}

        {/* ── Chat & Notifications relocated above Privacy
            (Apr 2026, Tom_Notes F-order) ── */}

        {/* ── Miscellaneous (Apr 2026) ───────────────────────────
            Catch-all section for small UI/display toggles that don't
            belong in Automation, Payments, Spare Management, or the
            other themed cards. Added bottom-of-settings by design:
            coaches typically set up the big-ticket settings first and
            rarely revisit; the miscellaneous toggles are opt-in tweaks
            that don't need to be discoverable up top.

            First toggle: attendance-count badges on the Game page
            availability rows. Defaults OFF so existing teams are
            unchanged. ── */}
        {activeSection === 'privacy' && canEdit && (
          <div className="card">
            <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Settings size={16} style={{ color: 'var(--ice)' }} /> Miscellaneous
            </h2>
            <div className="space-y-3">
              <ToggleSetting
                emoji="📊"
                title="Show attendance history on game page"
                shortDesc="Display each player's all-time games-in / games-out next to their name."
                longDesc="Displays each player's all-time games-in / games-out count next to their name on the game page availability list (e.g. 5✅ 0❌). Useful for spotting reliable vs. flaky players at a glance. Off by default."
                tooltip="Counts are computed from game_availability rows across the team's full history. Tombstoned (deleted) accounts are excluded."
                value={!!form.showAttendanceCounts}
                onChange={(next) => setForm(f => ({ ...f, showAttendanceCounts: next }))}
              />
            </div>
          </div>
        )}

        {/* Apr 2026 — Save button hidden on sub-pages that don't bind
            to the shared form state.
            Drinks (v2.5.105) has its own internal state and renders
            its OWN "Save Settings" button at the bottom of the
            DrinksSettings panel — see that component for the
            in-card save UI.
            Payouts has the StripeConnectPanel which makes its own
            API calls.
            Danger Zone only contains action buttons (Leave Team /
            Dev Tools) that have their own confirmation flows.
            Showing the shared "Save Settings" button on those pages
            would be misleading. */}
        {canEdit && activeSection !== 'drinks' && activeSection !== 'danger' && activeSection !== 'payouts' && (
          <button
            id="team-settings-save-button"
            type="submit"
            disabled={saving}
            className="btn-ice w-full justify-center py-3">
            {saving ? <div className="spinner" style={{ width:16, height:16 }} /> : <><Save size={16} /> Save Settings</>}
          </button>
        )}
      </form>

      {/* ── Drinks Duty + Your Role moved (Apr 2026, Tom_Notes F-order)
          They now render inside the main form flow (just before Chat
          and after Privacy respectively) so the visual section order
          matches the spec. They still self-manage their own state via
          internal API calls — moving the JSX is purely structural. */}

      {/* ── Notify Team removed (Apr 2026, Tom_Notes I1) ──
          The "Send the current player list to all members" button
          duplicated the Email Players action that already exists on
          the Game page (Coach Actions). Coaches reported the two
          paths were confusing and rarely used the team-settings
          one. Removed here; notifyRoster() helper kept in case we
          want to surface it elsewhere later. */}

      {/* ── Dev Tools — coaches and admins ── */}
      {activeSection === 'danger' && (canEdit || isAdmin) && (
        <div className="card mt-6" style={{ borderColor: 'rgba(167,139,250,0.2)', border: '1px solid rgba(167,139,250,0.2)' }}>
          <h2 className="font-bold text-lg mb-1 flex items-center gap-2" style={{ color: 'var(--text)' }}>
            <Wand2 size={16} style={{ color: '#a78bfa' }} /> Dev Tools
          </h2>
          <p className="text-xs mb-4" style={{ color: 'var(--text-2)' }}>
            Generate fake players for testing. Creates 20 skaters + 2 goalies with random ratings.
          </p>
          <button onClick={seedRoster} disabled={seeding}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{
              background: seedConfirm ? 'rgba(167,139,250,0.25)' : 'rgba(167,139,250,0.1)',
              border: '1px solid rgba(167,139,250,0.35)', color: '#a78bfa',
            }}>
            {seeding
              ? <><div className="spinner" style={{ width:14, height:14 }} /> Generating...</>
              : seedConfirm
              ? <>⚠ Tap again to confirm</>
              : <><Wand2 size={14} /> Generate Fake Players</>}
          </button>
        </div>
      )}

      {/* Danger zone */}
      {activeSection === 'danger' && (
      <div className="card mt-6 border-penalty/20" style={{ borderColor: 'rgba(255,68,68,0.2)' }}>
        <h2 className="font-bold text-lg mb-3 flex items-center gap-2" style={{ color: 'var(--penalty)' }}>
          <AlertTriangle size={16} /> Danger Zone
        </h2>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium">Leave this team</p>
            <p className="text-xs text-secondary">You'll be removed from the player list. Coaches cannot leave if they're the only coach.</p>
          </div>
          <button onClick={() => setShowLeave(true)}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{ background:'rgba(255,68,68,0.08)', border:'1px solid rgba(255,68,68,0.2)', color:'var(--penalty)' }}>
            <LogOut size={14} /> Leave Team
          </button>
        </div>

        {/* Apr 2026 — Delete Team. Coach-only (isCoach check, not
            isManager). Permanently removes the team and ALL its data:
            members, games, seasons, stats, payments, chat history,
            drinks, balance history, etc. The cascade FK rules in the
            schema handle the cleanup; the modal below requires the
            coach to type the team name to confirm, and adds an extra
            "you're the sole coach" warning when applicable. Hidden
            for managers — managers can do most coach things but team
            destruction should require the head coach role. */}
        {isCoach && (
          <div className="flex items-center justify-between gap-4 mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,68,68,0.15)' }}>
            <div>
              <p className="text-sm font-medium">Delete this team</p>
              <p className="text-xs text-secondary">
                Permanently removes the team and <strong>all</strong> its data — games, players,
                stats, payments, chat history, everything. This cannot be undone.
              </p>
            </div>
            <button onClick={openDeleteModal}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
              style={{ background:'rgba(255,68,68,0.15)', border:'1px solid rgba(255,68,68,0.35)', color:'var(--penalty)' }}>
              <Trash2 size={14} /> Delete Team
            </button>
          </div>
        )}
      </div>
      )}

      {/* Apr 2026 — Delete Team confirmation modal (step 1 of 2 for sole
          coaches; step 1 of 1 if there are co-coaches).
          Requires typing the exact team name to enable the delete button.
          Backend re-validates the name match server-side as a defence-in-
          depth check against accidental clicks bypassing the frontend. */}
      {showDelete === 'confirm' && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ zIndex: 500 }}>
          <BodyScrollLock />
          <div className="glass w-full sm:max-w-md p-6 animate-slide-up modal-sheet">
            <div className="text-4xl text-center mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-center mb-2" style={{ color: 'var(--penalty)' }}>
              Delete {team?.name}?
            </h2>
            <p className="text-sm text-center mb-4" style={{ color: 'var(--text-2)' }}>
              This <strong>permanently removes</strong> the team and everything attached to it:
            </p>
            <ul className="text-xs space-y-1 mb-4 px-2" style={{ color: 'var(--text-2)' }}>
              <li>• All games and game history</li>
              <li>• All players, ratings, jersey numbers, and pending invites</li>
              <li>• All seasons and per-season stats</li>
              <li>• Chat messages and attachments</li>
              <li>• Drinks duty history, payments, balance history</li>
              <li>• Stripe Connect link (your Stripe account itself stays)</li>
            </ul>
            <p className="text-xs text-center mb-4" style={{ color: 'var(--penalty)' }}>
              <strong>This cannot be undone.</strong>
            </p>

            <div className="mb-4">
              <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-2)' }}>
                Type <span style={{ color: 'var(--penalty)', fontFamily: 'monospace' }}>{team?.name}</span> to confirm:
                {/* 2026-07-10 (coach request, backlog 3.6) — copy the exact
                    team name so it can be pasted into the confirm box
                    (long/typo-prone names made deletion needlessly hard). */}
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(team?.name || '');
                      toast.success('Team name copied — paste it below');
                    } catch { /* clipboard blocked — they can select the red text */ }
                  }}
                  className="btn-ghost"
                  style={{ marginLeft: 8, padding: '2px 8px', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Copy size={11} /> Copy name
                </button>
              </label>
              <input
                type="text"
                autoFocus
                className="input-field"
                value={deleteConfirmName}
                onChange={e => setDeleteConfirmName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Escape') closeDeleteModal();
                  if (e.key === 'Enter' && (deleteConfirmName || '').trim() === (team?.name || '').trim()) {
                    e.preventDefault();
                    proceedToFinalWarning();
                  }
                }}
                placeholder="Team name" />
            </div>

            <div className="flex gap-3">
              <button onClick={closeDeleteModal} disabled={deleting} className="btn-ghost flex-1 justify-center">
                Cancel
              </button>
              <button
                onClick={proceedToFinalWarning}
                disabled={deleting || (deleteConfirmName || '').trim() !== (team?.name || '').trim()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all"
                style={{
                  background: 'rgba(255,68,68,0.18)',
                  border: '1px solid rgba(255,68,68,0.4)',
                  color: 'var(--penalty)',
                  opacity: ((deleteConfirmName || '').trim() === (team?.name || '').trim()) ? 1 : 0.5,
                }}>
                {deleting
                  ? <div className="spinner" style={{ width: 14, height: 14 }} />
                  : <><Trash2 size={14} /> Continue</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apr 2026 — Delete Team final-warning modal (step 2, sole-coach
          only). Coaches asked for an extra confirmation when there's
          no other coach to take over — once the team's gone there's
          literally no recourse. Requires explicit checkbox tick to
          enable the destructive button. Pressing Cancel here returns
          to the first modal so the coach can back out without losing
          their typed team name. */}
      {showDelete === 'finalWarning' && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ zIndex: 500 }}>
          <BodyScrollLock />
          <div className="glass w-full sm:max-w-md p-6 animate-slide-up modal-sheet"
            style={{ border: '2px solid rgba(255,68,68,0.4)' }}>
            <div className="text-5xl text-center mb-3">🛑</div>
            <h2 className="text-xl font-bold text-center mb-3" style={{ color: 'var(--penalty)' }}>
              You're the only coach
            </h2>
            <p className="text-sm mb-4" style={{ color: 'var(--text)', lineHeight: 1.55 }}>
              No other coach can take over after you delete this team. Every player loses access
              instantly, all game history is gone, and there's no way to recover any of it.
            </p>
            <p className="text-sm mb-4" style={{ color: 'var(--text-2)', lineHeight: 1.55 }}>
              If you're stepping back from coaching but want to keep the team running,
              consider promoting another player to coach first (Players page → click their name).
            </p>

            <label
              className="flex items-start gap-3 p-3 rounded-xl cursor-pointer mb-4"
              style={{
                background: deleteAck ? 'rgba(255,68,68,0.08)' : 'var(--surface-2)',
                border: `1.5px solid ${deleteAck ? 'rgba(255,68,68,0.4)' : 'var(--border)'}`,
              }}
              onClick={() => setDeleteAck(p => !p)}>
              <div style={{
                width: 20, height: 20, borderRadius: 5, flexShrink: 0, marginTop: 2,
                background: deleteAck ? 'var(--penalty)' : 'var(--surface-3)',
                border: `1.5px solid ${deleteAck ? 'var(--penalty)' : 'var(--border)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 12, fontWeight: 800,
              }}>
                {deleteAck ? '✓' : ''}
              </div>
              <p className="text-sm" style={{ color: 'var(--text)' }}>
                I understand this will <strong>permanently delete {team?.name}</strong> and all its
                data, and that no one — including me — can recover it.
              </p>
            </label>

            <div className="flex gap-3">
              <button onClick={() => setShowDelete('confirm')} disabled={deleting} className="btn-ghost flex-1 justify-center">
                Back
              </button>
              <button
                onClick={executeDelete}
                disabled={deleting || !deleteAck}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all"
                style={{
                  background: deleteAck ? 'var(--penalty)' : 'rgba(255,68,68,0.2)',
                  border: '1px solid var(--penalty)',
                  color: deleteAck ? '#fff' : 'var(--penalty)',
                  opacity: deleteAck ? 1 : 0.6,
                }}>
                {deleting
                  ? <div className="spinner" style={{ width: 14, height: 14 }} />
                  : <><Trash2 size={14} /> Delete Forever</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leave confirm modal */}
      {showLeave && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ zIndex: 500 }}>
          <BodyScrollLock />
          <div className="glass w-full sm:max-w-sm p-6 animate-slide-up modal-sheet">
            <div className="text-4xl text-center mb-4">🚪</div>
            <h2 className="text-xl font-bold text-center mb-2">Leave {team?.name}?</h2>
            <p className="text-secondary text-sm text-center mb-6">
              You'll be removed from the team's player list. The coach will need to re-invite you if you change your mind.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowLeave(false)} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button onClick={leaveTeam} disabled={leaving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all"
                style={{ background:'rgba(255,68,68,0.15)', border:'1px solid rgba(255,68,68,0.3)', color:'var(--penalty)' }}>
                {leaving ? <div className="spinner" style={{width:14,height:14}}/> : <><LogOut size={14}/> Leave Team</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* v2.5.181 — Pair / Keep-Apart player-picker modal. Two add-forms
          (pair = together, keep-apart = separate), each with a soft/hard
          choice, plus a live list of current rules with per-row strength
          toggle + remove. Writes straight into form.balancePairsJson via
          the helpers; nothing saves until the coach hits the page's Save. */}
      {showPairModal && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ zIndex: 500 }}>
          <BodyScrollLock />
          <div className="glass w-full sm:max-w-lg p-6 animate-slide-up modal-sheet"
            // Mobile fix — 90vh doesn't account for mobile Safari's dynamic
            // toolbar / Capacitor status bar, which can clip this modal's
            // bottom (this is the one TeamPage modal with an unbounded-
            // growth content list — pair/keep-apart rules). --mobile-h is
            // set by useCapacitorPlugins.js to the real visible viewport;
            // 100dvh is the web fallback. Matches the pattern already used
            // in finance/SparesTab.jsx.
            style={{ maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)', overflowY: 'auto' }}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-bold">🔗 Pair / Keep-Apart Players</h2>
              <button onClick={() => setShowPairModal(false)} className="btn-ghost text-xs px-3 py-1.5">Done</button>
            </div>
            <p className="text-xs text-secondary mb-4">
              Choose players to keep <strong>together</strong> or <strong>apart</strong> when the balancer builds teams.
              <strong> Hard</strong> always holds; <strong>Soft</strong> is a strong preference the balancer can
              override to keep the game fair. Changes apply when you Save the page.
            </p>

            {pairRosterLoading ? (
              <div className="flex justify-center py-8"><div className="spinner" style={{ width: 24, height: 24 }} /></div>
            ) : pairRoster.length < 2 ? (
              <p className="text-sm text-center py-6" style={{ color: 'var(--text-2)' }}>
                You need at least two players on the roster to set pairings.
              </p>
            ) : (
              <>
                {/* ── Add a PAIR (keep together) ── */}
                {(() => {
                  const PlayerPicker = ({ value, onChange, exclude, placeholder }) => (
                    <select className="input-field" style={{ flex: 1, minWidth: 0 }}
                      value={value} onChange={e => onChange(e.target.value)}>
                      <option value="">{placeholder}</option>
                      {pairRoster.filter(p => p.userId !== exclude).map(p => (
                        <option key={p.userId} value={p.userId}>{p.name}</option>
                      ))}
                    </select>
                  );
                  const StrengthToggle = ({ value, onChange }) => (
                    <div className="flex gap-1" role="group" aria-label="Strength">
                      {['soft', 'hard'].map(s => (
                        <button key={s} type="button" onClick={() => onChange(s)}
                          className="text-xs px-3 py-2 rounded-lg font-semibold transition-all"
                          style={{
                            background: value === s
                              ? (s === 'hard' ? 'var(--penalty-a15)' : 'var(--puck-a8)')
                              : 'var(--surface-2, rgba(127,127,127,0.08))',
                            color: value === s
                              ? (s === 'hard' ? 'var(--penalty)' : 'var(--puck)')
                              : 'var(--text-3)',
                            border: `1px solid ${value === s
                              ? (s === 'hard' ? 'var(--penalty-a25)' : 'var(--puck-a25)')
                              : 'transparent'}`,
                            textTransform: 'capitalize',
                          }}>
                          {s}
                        </button>
                      ))}
                    </div>
                  );
                  return (
                    <>
                      <div className="mb-5">
                        <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--ice)' }}>Keep together (Pair)</h3>
                        <div className="flex gap-2 mb-2">
                          <PlayerPicker value={pairDraft.a} exclude={pairDraft.b} placeholder="Player…"
                            onChange={(v) => setPairDraft(d => ({ ...d, a: v }))} />
                          <PlayerPicker value={pairDraft.b} exclude={pairDraft.a} placeholder="Player…"
                            onChange={(v) => setPairDraft(d => ({ ...d, b: v }))} />
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <StrengthToggle value={pairDraft.strength} onChange={(s) => setPairDraft(d => ({ ...d, strength: s }))} />
                          <button type="button" onClick={() => addConstraint('pairs', pairDraft)}
                            className="btn-ice text-xs px-4 py-2" style={{ whiteSpace: 'nowrap' }}>
                            + Add pair
                          </button>
                        </div>
                      </div>

                      {/* ── Add a KEEP-APART (separate) ── */}
                      <div className="mb-5 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                        <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--penalty)' }}>Keep apart</h3>
                        <div className="flex gap-2 mb-2">
                          <PlayerPicker value={apartDraft.a} exclude={apartDraft.b} placeholder="Player…"
                            onChange={(v) => setApartDraft(d => ({ ...d, a: v }))} />
                          <PlayerPicker value={apartDraft.b} exclude={apartDraft.a} placeholder="Player…"
                            onChange={(v) => setApartDraft(d => ({ ...d, b: v }))} />
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <StrengthToggle value={apartDraft.strength} onChange={(s) => setApartDraft(d => ({ ...d, strength: s }))} />
                          <button type="button" onClick={() => addConstraint('keepApart', apartDraft)}
                            className="text-xs px-4 py-2 rounded-lg font-semibold"
                            style={{ background: 'var(--penalty-a15)', color: 'var(--penalty)', border: '1px solid var(--penalty-a25)', whiteSpace: 'nowrap' }}>
                            + Add keep-apart
                          </button>
                        </div>
                      </div>
                    </>
                  );
                })()}

                {/* ── Current rules ── */}
                <div className="pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                  <h3 className="text-sm font-semibold mb-2">Current rules</h3>
                  {(pairConstraints.pairs.length + pairConstraints.keepApart.length) === 0 ? (
                    <p className="text-xs" style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>None yet.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {pairConstraints.pairs.map((c, i) => (
                        <div key={`mp${i}`} className="flex items-center gap-2 flex-wrap text-xs p-2 rounded-lg"
                          style={{ background: 'var(--ice-a3, rgba(56,189,248,0.04))' }}>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 6, background: 'var(--ice-a10)', color: 'var(--ice)' }}>PAIR</span>
                          <span style={{ color: 'var(--text)', flex: 1, minWidth: 0 }}>{nameForId(c.a)} + {nameForId(c.b)}</span>
                          <button type="button" onClick={() => toggleConstraintStrength('pairs', i)}
                            title="Tap to switch soft/hard"
                            style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
                              background: c.strength === 'hard' ? 'var(--penalty-a15)' : 'var(--puck-a8)',
                              color: c.strength === 'hard' ? 'var(--penalty)' : 'var(--puck)',
                              border: 'none', textTransform: 'capitalize' }}>
                            {c.strength}
                          </button>
                          <button type="button" onClick={() => removeConstraint('pairs', i)}
                            className="btn-ghost" style={{ padding: '2px 8px', fontSize: 11 }}>Remove</button>
                        </div>
                      ))}
                      {pairConstraints.keepApart.map((c, i) => (
                        <div key={`ma${i}`} className="flex items-center gap-2 flex-wrap text-xs p-2 rounded-lg"
                          style={{ background: 'var(--penalty-a8, rgba(248,113,113,0.05))' }}>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 6, background: 'var(--penalty-a15)', color: 'var(--penalty)' }}>APART</span>
                          <span style={{ color: 'var(--text)', flex: 1, minWidth: 0 }}>{nameForId(c.a)} ✕ {nameForId(c.b)}</span>
                          <button type="button" onClick={() => toggleConstraintStrength('keepApart', i)}
                            title="Tap to switch soft/hard"
                            style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
                              background: c.strength === 'hard' ? 'var(--penalty-a15)' : 'var(--puck-a8)',
                              color: c.strength === 'hard' ? 'var(--penalty)' : 'var(--puck)',
                              border: 'none', textTransform: 'capitalize' }}>
                            {c.strength}
                          </button>
                          <button type="button" onClick={() => removeConstraint('keepApart', i)}
                            className="btn-ghost" style={{ padding: '2px 8px', fontSize: 11 }}>Remove</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs mt-3" style={{ color: 'var(--text-3)' }}>
                    Remember to <strong>Save</strong> the page to apply these.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* v2.5.181 — Clear-all-chat confirmation. Destructive, so a
          dedicated confirm step (not just the button) before we fire. */}
      {showClearChatModal && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ zIndex: 500 }}>
          <BodyScrollLock />
          <div className="glass w-full sm:max-w-sm p-6 animate-slide-up modal-sheet">
            <div className="text-4xl text-center mb-4">🗑️</div>
            <h2 className="text-xl font-bold text-center mb-2" style={{ color: 'var(--penalty)' }}>
              Clear all chat for {team?.name}?
            </h2>
            <p className="text-sm text-center mb-3" style={{ color: 'var(--text-2)' }}>
              This permanently deletes <strong>every chat message and attached file</strong> for
              this team, across all seasons. It cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowClearChatModal(false)} disabled={clearingChat}
                className="btn-ghost flex-1 justify-center">Cancel</button>
              <button onClick={clearAllChatNow} disabled={clearingChat}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all"
                style={{ background: 'rgba(255,68,68,0.18)', border: '1px solid rgba(255,68,68,0.4)', color: 'var(--penalty)' }}>
                {clearingChat ? <div className="spinner" style={{ width: 14, height: 14 }} /> : <><Trash2 size={14} /> Delete everything</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </TogglePresentationContext.Provider>
  );
}
